import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  timestamp,
  jsonb,
  integer,
  boolean,
  date,
  customType,
  numeric,
  uniqueIndex,
  index,
  primaryKey,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import type { KpiSettings } from "./kpi-settings";

// Custom type for pgvector VECTOR(1536) – stores as string, returned as string
const vector1536 = customType<{ data: string; driverData: string }>({
  dataType() {
    return "vector(1536)";
  },
  toDriver(value: string) {
    return value;
  },
  fromDriver(value: string) {
    return value;
  },
});

// Agencies table – groups users into agency organizations
export const agencies = pgTable("agencies", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  ownerId: varchar("owner_id").notNull(), // FK added after users table to avoid circular ref
  createdAt: timestamp("created_at").defaultNow(),
});

export const users = pgTable(
  "users",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    username: text("username"),
    firstName: text("first_name"),
    lastName: text("last_name"),
    email: text("email").notNull().unique(),
    password: text("password").notNull(),
    passwordHash: text("password_hash"),
    googleId: text("google_id"),
    // Role-based access control: 'client' | 'employee' | 'manager' | 'agency_owner' | 'super_admin'
    role: varchar("role", { length: 20 }).default("client").notNull(),
    // Agency membership – links user to an agency organization
    agencyId: varchar("agency_id"),
    // Agency RBAC v1 – preset plus optional custom override.
    agencyRolePreset: varchar("agency_role_preset", { length: 40 }),
    permissionMode: varchar("permission_mode", { length: 20 }).default("preset"),
    tokensRemaining: integer("tokens_remaining").default(0).notNull(),
    tokensUsedThisMonth: integer("tokens_used_this_month").default(0).notNull(),
    currentMonthYear: varchar("current_month_year")
      .default(sql`to_char(now(), 'YYYY-MM')`)
      .notNull(),
    // New tokenomics cycle fields (add-only; legacy monthly fields kept for backward compatibility)
    subscriptionPeriodStart: timestamp("subscription_period_start"), // when current subscription cycle started
    subscriptionNextRenewalAt: timestamp("subscription_next_renewal_at"), // expected renewal moment
    lastTokenResetAt: timestamp("last_token_reset_at"), // last time allocation was (re)applied
    monthlyTokenAllocation: integer("monthly_token_allocation").default(0), // base allocation per cycle (app tokens)
    hasSeenWelcomeForm: boolean("has_seen_welcome_form").default(false).notNull(),
    // Subscription fields
    subscriptionActive: boolean("subscription_active").default(false).notNull(),
    subscriptionId: text("subscription_id"),
    lastPaymentAmount: integer("last_payment_amount"),
    lastPaymentDate: timestamp("last_payment_date"),
    subscriptionCancelledAt: timestamp("subscription_cancelled_at"),
    // Stripe billing
    stripeCustomerId: text("stripe_customer_id"),
    subscriptionPlanKey: text("subscription_plan_key"), // e.g. 'lead_generation:pro:monthly'
    // Access control – which modules/views are visible to the user
    // null = full access (default), array = only listed modules/views visible
    // Special values: 'dashboard' for client data dashboard, plus module IDs
    allowedModules: jsonb("allowed_modules").$type<string[] | null>().default(null),
    // User preference – which modules to hide from panel (subset of allowedModules)
    // null = show all accessible modules (default), array = hide listed module IDs
    hiddenModules: jsonb("hidden_modules").$type<string[] | null>().default(null),
    // Agency permissions – explicit custom permission list or legacy preset add-ons.
    // New keys live in shared/permissions.ts; legacy agency_* keys are normalized
    // in the authorization layer for backwards compatibility.
    agencyPermissions: jsonb("agency_permissions").$type<string[] | null>().default(null),
    // Agency model: admin can assign an ad account from their own Meta integration
    assignedAdAccountId: varchar("assigned_ad_account_id"), // act_xxx from admin's Meta (legacy single)
    assignedAdAccountName: varchar("assigned_ad_account_name"),
    assignedFromUserId: varchar("assigned_from_user_id"), // which admin assigned it
    // Multiple ad accounts support (array of {id, name})
    assignedAdAccounts: jsonb("assigned_ad_accounts")
      .$type<{ id: string; name: string }[] | null>()
      .default(null),
    // Ad account add-on pack (extra accounts beyond plan base limit)
    adAccountPackSize: integer("ad_account_pack_size").default(0).notNull(),
    // Trial & onboarding profile
    trialEndsAt: timestamp("trial_ends_at"),
    trialPlus: boolean("trial_plus").default(false).notNull(),
    paymentFailedAt: timestamp("payment_failed_at"),
    onboardingCompletedAt: timestamp("onboarding_completed_at"),
    phone: varchar("phone", { length: 30 }),
    companyName: text("company_name"),
    companySize: varchar("company_size", { length: 30 }),
    howDidYouHear: varchar("how_did_you_hear", { length: 50 }),
    whatDoYouNeed: varchar("what_do_you_need", { length: 100 }),
    // TOTP 2FA — required for super_admin
    totpSecret: text("totp_secret"), // encrypted via AES-256-GCM (encryptToken)
    totpEnabled: boolean("totp_enabled").default(false).notNull(),
    totpBackupCodes: text("totp_backup_codes"), // encrypted JSON array of hashed codes
    // Currency display preference: null = use ad account's native currency, or explicit 'PLN'|'USD'|'EUR' override
    preferredCurrency: varchar("preferred_currency", { length: 10 }),
    // UI/output locale: 'pl' | 'en' | 'es' | 'pt'. null = inferred from request headers.
    locale: varchar("locale", { length: 5 }),
    // Demo booking prompt tracking (trial users)
    hasSeenDemoPromptEarly: boolean("has_seen_demo_prompt_early").default(false).notNull(),
    hasSeenDemoPromptMid: boolean("has_seen_demo_prompt_mid").default(false).notNull(),
    trialAiResponseCount: integer("trial_ai_response_count").default(0).notNull(),
    // Super-admin tooling: when true, generated media for this user is watermarked
    // even if subscription/trial would normally suppress it (preview mode).
    watermarkPreviewMode: boolean("watermark_preview_mode").default(false).notNull(),
    typeformResponses: jsonb("typeform_responses"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    createdAtIdx: index("users_created_at_idx").on(table.createdAt),
    emailLowerIdx: index("users_email_lower_idx").on(sql`LOWER(${table.email})`),
  })
);

/**
 * FX rates cache. Stores daily mid-rates per (base, quote, date).
 * Source priority: Frankfurter (ECB) primary, NBP fallback.
 * Used by FxService.toDisplay() for converting native ad-account currencies
 * (e.g. campaign spend in PLN) into the user's preferredCurrency or
 * locale-default currency for analytics/dashboard display.
 *
 * Historical rate (kurs z dnia transakcji) is preserved by storing the rate
 * for the date of the underlying event, not just "today".
 */
export const fxRates = pgTable(
  "fx_rates",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    baseCurrency: varchar("base_currency", { length: 3 }).notNull(),
    quoteCurrency: varchar("quote_currency", { length: 3 }).notNull(),
    rateDate: date("rate_date").notNull(),
    rate: numeric("rate", { precision: 18, scale: 8 }).notNull(),
    source: varchar("source", { length: 20 }).notNull(), // 'frankfurter' | 'nbp' | 'manual'
    fetchedAt: timestamp("fetched_at").defaultNow().notNull(),
  },
  (table) => ({
    uqRate: uniqueIndex("fx_rates_unique_idx").on(
      table.baseCurrency,
      table.quoteCurrency,
      table.rateDate
    ),
  })
);

// Token usage audit trail (new table)
export const tokenUsageEvents = pgTable("token_usage_events", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  moduleId: varchar("module_id"), // which module/assistant initiated the cost
  modelId: varchar("model_id"),
  appMultiplier: integer("app_multiplier").default(1).notNull(),
  claudeInputTokens: integer("claude_input_tokens").default(0).notNull(),
  claudeOutputTokens: integer("claude_output_tokens").default(0).notNull(),
  claudeTotalTokens: integer("claude_total_tokens").default(0).notNull(),
  appTokensCharged: integer("app_tokens_charged").default(0).notNull(), // tokens deducted from user (post-multiplier)
  preTokensRemaining: integer("pre_tokens_remaining").default(0).notNull(),
  postTokensRemaining: integer("post_tokens_remaining").default(0).notNull(),
  operation: varchar("operation"), // e.g., 'ad-creator.generate', 'offer-builder.message'
  // Real provider cost in USD (Anthropic/OpenAI/Replicate/ElevenLabs/etc.).
  // When present, admin analytics uses this directly instead of estimating from token counts or app tokens.
  actualCostUsd: numeric("actual_cost_usd"),
  // Provider-side generation/request id (OpenRouter `id`, fal.ai `request_id`, etc.) for retro cost lookup and audit.
  providerGenerationId: varchar("provider_generation_id", { length: 128 }),
  // Source of `actualCostUsd`:
  //   'provider_exact'           – returned by provider API (OpenRouter usage.cost) — 100% trust
  //   'estimated_pricing_table'  – computed from our pricing table (fal.ai, Whisper, ElevenLabs)
  //   'reconciled'               – adjusted by reconciliation cron against provider invoice
  //   'fallback'                 – legacy revenue÷5 estimation (pre-actualCostUsd era)
  costSource: varchar("cost_source", { length: 32 }).default("estimated_pricing_table"),
  createdAt: timestamp("created_at").defaultNow(),
  errorFlag: boolean("error_flag").default(false).notNull(), // true if LLM call failed or cost aborted
  aborted: boolean("aborted").default(false).notNull(), // true if >500 cost ceiling triggered
});

// Snapshot of provider pricing as of a given date — lookup table for historical reconstruction.
// Insert a new row whenever upstream pricing changes; events are valued using the snapshot active at their createdAt.
export const providerPricingSnapshots = pgTable("provider_pricing_snapshots", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  provider: varchar("provider", { length: 32 }).notNull(), // 'fal', 'openrouter', 'anthropic', 'whisper', 'elevenlabs'
  modelId: varchar("model_id", { length: 128 }).notNull(),
  unit: varchar("unit", { length: 32 }).notNull(), // 'per_second', 'per_image', 'per_1k_input_tokens', etc.
  usdPerUnit: numeric("usd_per_unit").notNull(),
  effectiveFrom: timestamp("effective_from").notNull(),
  source: varchar("source", { length: 64 }), // url or note where the price was fetched from
  createdAt: timestamp("created_at").defaultNow(),
});

// Monthly provider invoice totals — ground truth for reconciliation cron.
// Compare sum(actualCostUsd) per provider/period against `totalUsdInvoiced` and apply drift correction.
export const providerInvoicePeriods = pgTable("provider_invoice_periods", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  provider: varchar("provider", { length: 32 }).notNull(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  totalUsdInvoiced: numeric("total_usd_invoiced").notNull(),
  estimatedSumUsd: numeric("estimated_sum_usd"), // our internal sum at reconciliation time
  driftRatio: numeric("drift_ratio"), // invoice / estimated
  reconciledAt: timestamp("reconciled_at"),
  source: varchar("source", { length: 64 }), // 'manual', 'cron-fal', 'cron-openrouter'
  createdAt: timestamp("created_at").defaultNow(),
});

export const modules = pgTable("modules", {
  id: varchar("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  icon: text("icon").notNull(),
  systemPrompt: text("system_prompt").notNull(),
  color: text("color").notNull(),
  locked: boolean("locked").default(false),
  suggestions: text("suggestions").array(),
});

// New conversation structure for POPPY API single requests
export const conversations = pgTable(
  "conversations",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    moduleId: varchar("module_id").notNull(),
    projectId: varchar("project_id"),
    title: varchar("title"),
    isCustomTitle: boolean("is_custom_title").default(false), // True if user edited title manually
    // Store complete conversation history as text for additional_context
    conversationHistory: text("conversation_history").default(""),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    userIdIdx: index("conversations_user_id_idx").on(table.userId),
  })
);

// Messages now reference conversations instead of chats
export const messages = pgTable(
  "messages",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    conversationId: varchar("conversation_id")
      .notNull()
      .references(() => conversations.id, { onDelete: "cascade" }),
    chatId: varchar("chat_id"),
    userId: varchar("user_id"),
    role: text("role").notNull(), // 'user' | 'assistant'
    content: text("content").notNull(),
    imageUrl: text("image_url"), // URL of attached image
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    conversationIdIdx: index("messages_conversation_id_idx").on(table.conversationId),
    conversationCreatedIdx: index("messages_conversation_id_created_at_idx").on(
      table.conversationId,
      table.createdAt
    ),
  })
);

// Keep legacy chats table for backward compatibility
export const chats = pgTable("chats", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  moduleId: varchar("module_id").notNull(),
  title: text("title").notNull(),
  conversationId: text("conversation_id"), // POPPY AI conversation ID
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const worksheets = pgTable("worksheets", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  filename: text("filename").notNull(),
  originalName: text("original_name").notNull(),
  size: text("size").notNull(),
  uploadPath: text("upload_path"),
  mimeType: text("mime_type").notNull(),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Arkusz ofertowy - przechowuje dane o ofercie użytkownika
export const offerSheets = pgTable(
  "offer_sheets",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    title: text("title").notNull().default("Moja oferta"),
    content: text("content").notNull(), // Single text field for entire offer
    isActive: boolean("is_active").default(false).notNull(), // Only one can be active per user
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    userIdIdx: index("offer_sheets_user_id_idx").on(table.userId),
  })
);

// Historia arkuszy ofert - zabezpieczenie przeciw utracie danych
export const offerSheetHistory = pgTable("offer_sheet_history", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  offerSheetId: varchar("offer_sheet_id")
    .notNull()
    .references(() => offerSheets.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  title: text("title").notNull(),
  content: text("content").notNull(),
  version: integer("version").notNull().default(1),
  changeType: varchar("change_type").notNull(), // 'create' | 'update' | 'delete'
  changeReason: text("change_reason"), // Powód zmiany dla debugowania
  createdAt: timestamp("created_at").defaultNow(),
});

// Campaign drafts — strukturalny stan kampanii budowanej w Twórcy Kampanii (AI step-by-step).
// Wypełniany inkrementalnie przez bloki [STATE].patch w odpowiedziach AI; konsumowany przez
// /launch-readiness i /launch-from-draft (przekształca payload w params dla Meta API).
export const campaignDrafts = pgTable("campaign_drafts", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  conversationId: varchar("conversation_id"), // FK do conversations (campaign-creator lub goals-builder)
  clientId: varchar("client_id"), // FK do agency_clients (opcjonalny)
  offerSheetId: varchar("offer_sheet_id"),
  step: integer("step").notNull().default(1), // 1..6
  status: varchar("status").notNull().default("draft"), // 'draft' | 'ready' | 'launching' | 'launched' | 'failed'
  payload: jsonb("payload")
    .notNull()
    .default(sql`'{}'::jsonb`), // { offer, goals, structure, creatives, targeting, copy, leadForm, metaConfig }
  launchedCampaignId: varchar("launched_campaign_id"), // Meta campaign ID po udanym launchu
  launchedAt: timestamp("launched_at"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Session table for authentication
export const sessions = pgTable("sessions", {
  id: varchar("id").primaryKey(),
  userId: varchar("user_id").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Password reset tokens
export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  token: varchar("token").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  used: boolean("used").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Bank pomysłów - IG Handles konkurencji
export const competitorHandles = pgTable("competitor_handles", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  igHandle: varchar("ig_handle").notNull(), // @nazwa_konta (match actual table structure)
  createdAt: timestamp("created_at").defaultNow(),
});

// Bank pomysłów - Prostsze pomysły na content
export const contentIdeas = pgTable("content_ideas", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  content: text("content").notNull(),
  source: varchar("source"),
  tags: text("tags").array(),
  createdAt: timestamp("created_at").defaultNow(),
});

export type ApprovalStatus = "pending" | "approved" | "rejected";

export interface ContentPipelineTask {
  id: string;
  title: string;
  description?: string;
  labels?: string[];
  assignee?: string;
  imageUrl?: string;
  createdAt?: string;
  process?: string;
  editorUserId?: string;
  editorName?: string;
  contentTypes?: string[];
  scheduledAt?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  script?: string;
  /** Approval gate (Sprint 3, transcript 2026-05). Undefined = no gate / legacy task. */
  approvalStatus?: ApprovalStatus;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  /** Scheduler integration (VP-107): link to a real scheduled_posts row + publish state. */
  scheduledPostId?: string;
  scheduledPlatform?: string;
  publishedPostId?: string;
  publishedAt?: string;
}

export interface ContentPipelineColumn {
  id: string;
  title: string;
  tasks: ContentPipelineTask[];
}

export const contentPipelineSnapshots = pgTable("content_pipeline_snapshots", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" })
    .unique(),
  columns: jsonb("columns").$type<ContentPipelineColumn[]>().notNull().default([]),
  contentTypes: jsonb("content_types").$type<string[]>().notNull().default([]),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// User reels table (for storing user's own reels data - "Moje rolki")
export const userReels = pgTable("user_reels", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  thumbnail: text("thumbnail"), // URL to thumbnail image
  url: text("url").notNull(), // Instagram reel URL
  transcript: text("transcript"), // Reel transcript/captions
  hookAnalysis: text("hook_analysis"), // AI analysis of the hook
  caption: text("caption"), // Instagram caption
  views: integer("views"), // Number of views
  likes: integer("likes"), // Number of likes
  comments: integer("comments"), // Number of comments
  postDate: timestamp("post_date"), // When the reel was posted
  username: text("username"), // Instagram username
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Competitor analysis table (for storing detailed competitor post analysis - "Analiza konkurencji")
export const competitorAnalysis = pgTable("competitor_analysis", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  competitorHandle: text("competitor_handle").notNull(), // @username
  thumbnail: text("thumbnail"), // URL to thumbnail image
  url: text("url").notNull(), // Instagram post URL
  transcript: text("transcript"), // Post transcript/captions
  hookAnalysis: text("hook_analysis"), // AI analysis of the hook
  caption: text("caption"), // Instagram caption
  views: integer("views"), // Number of views
  likes: integer("likes"), // Number of likes
  comments: integer("comments"), // Number of comments
  postDate: timestamp("post_date"), // When the post was published
  username: text("username"), // Instagram username
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// User profiles table for Instagram handles and other profile data
export const userProfiles = pgTable("user_profiles", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" })
    .unique(),
  instagramHandle: text("instagram_handle"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Idea Bank Summaries - AI-generated summaries for Reel Creator context
export const ideaBankSummaries = pgTable("idea_bank_summaries", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  summary: text("summary").notNull(), // AI-generated summary content
  tags: text("tags").array(), // Tags for categorization
  lastUpdated: timestamp("last_updated").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Reel Transcripts - For "Sklonuj się" module (from n8n webhooks)
export const reelTranscripts = pgTable("reel_transcripts", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  url: text("url").notNull(), // Instagram/YouTube reel URL
  transcript: text("transcript").notNull(), // Transcript from n8n webhook
  platform: varchar("platform", { length: 20 }).notNull(), // 'instagram' or 'youtube'
  createdAt: timestamp("created_at").defaultNow(),
});

// Inspiration Groups - groups of video links for Bank pomysłów
export const inspirationGroups = pgTable("inspiration_groups", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Inspiration Links - individual video links (YT, IG, TikTok) with transcripts
export const inspirationLinks = pgTable("inspiration_links", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  groupId: varchar("group_id")
    .notNull()
    .references(() => inspirationGroups.id, { onDelete: "cascade" }),
  url: text("url").notNull(),
  platform: varchar("platform", { length: 20 }).notNull(), // 'youtube' | 'instagram' | 'tiktok'
  title: text("title"), // auto-detected or user-provided
  thumbnail: text("thumbnail"), // video thumbnail URL
  duration: integer("duration"), // video duration in seconds
  transcript: text("transcript"), // Whisper transcription
  status: varchar("status", { length: 20 }).notNull().default("pending"), // 'pending' | 'processing' | 'done' | 'error'
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
});

// ── Daily Viral / Inspiracje (Organic) ─────────────────────────────────────
// Globalny cache viralnych filmów — wspólny między userami tej samej niszy.
// Analiza (hook/transkrypt) liczona RAZ per film i serwowana wszystkim → płaski koszt.
export const viralInspirations = pgTable(
  "viral_inspirations",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    platform: varchar("platform", { length: 20 }).notNull(), // 'tiktok' | 'instagram' | 'youtube'
    sourceUrl: text("source_url").notNull(),
    externalId: varchar("external_id", { length: 128 }), // id posta z platformy (dedup)
    title: text("title"),
    thumbnailUrl: text("thumbnail_url"), // S3/URL — nigdy base64
    authorHandle: varchar("author_handle", { length: 128 }),
    authorFollowers: integer("author_followers"),
    viewCount: integer("view_count"),
    likeCount: integer("like_count"),
    // viralScore = views / mediana kanału (outlier). Skala x100: 1000 = 10.00x.
    viralScore: integer("viral_score"),
    niche: varchar("niche", { length: 40 }).notNull(), // klucz z NICHES
    market: varchar("market", { length: 8 }).notNull().default("US"), // ISO kraj rynku
    durationSec: integer("duration_sec"),
    hook: text("hook"), // pierwsze 3s / hook wyekstrahowany przez AI
    transcript: text("transcript"),
    formatSummary: text("format_summary"), // struktura filmu (AI)
    status: varchar("status", { length: 20 }).notNull().default("pending"), // pending|analyzing|ready|error
    publishedAt: timestamp("published_at"),
    analyzedAt: timestamp("analyzed_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    nicheMarketIdx: index("viral_inspirations_niche_market_idx").on(t.niche, t.market),
    externalIdx: uniqueIndex("viral_inspirations_platform_external_idx").on(
      t.platform,
      t.externalId
    ),
  })
);

// Profil contentowy usera — steruje filtrowaniem feedu i adaptacją pod markę.
// Jeden profil per (user, agencyClientId); upsert w serwisie.
export const userContentProfiles = pgTable(
  "user_content_profiles",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    agencyClientId: varchar("agency_client_id"), // opcjonalny scope per podklient (bez twardego FK — konwencja repo)
    sourceHandle: varchar("source_handle", { length: 128 }), // @handle z onboardingu
    sourcePlatform: varchar("source_platform", { length: 20 }), // 'instagram' | 'tiktok'
    niche: varchar("niche", { length: 40 }).notNull(), // główna nisza (klucz NICHES)
    nicheTags: jsonb("niche_tags")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    contentPillars: jsonb("content_pillars")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    tone: varchar("tone", { length: 40 }), // klucz TONES
    offer: text("offer"), // co user sprzedaje (do adaptacji)
    markets: jsonb("markets")
      .$type<string[]>()
      .notNull()
      .default(sql`'["US"]'::jsonb`),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    userIdx: index("user_content_profiles_user_idx").on(t.userId),
  })
);

export type ViralInspiration = typeof viralInspirations.$inferSelect;
export type UserContentProfile = typeof userContentProfiles.$inferSelect;

// User Clones - AI analyzed speaking style ("Mój klon")
export const userClones = pgTable("user_clones", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull().default("Mój klon"),
  styleAnalysis: text("style_analysis").notNull(), // AI analysis of speaking style
  isActive: boolean("is_active").default(false).notNull(), // Only one can be active per user
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertReelTranscriptSchema = createInsertSchema(reelTranscripts).omit({
  id: true,
  userId: true,
  createdAt: true,
});

export const insertUserCloneSchema = createInsertSchema(userClones).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({
  id: true,
  createdAt: true,
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
});

export const insertTokenUsageEventSchema = createInsertSchema(tokenUsageEvents).omit({
  id: true,
  createdAt: true,
});

export const loginSchema = z.object({
  email: z.string().email("Nieprawidłowy adres email"),
  password: z.string().min(6, "Hasło musi mieć co najmniej 6 znaków"),
});

export const registerSchema = z.object({
  username: z.string().min(2, "Nazwa użytkownika musi mieć co najmniej 2 znaki"),
  email: z.string().email("Nieprawidłowy adres email"),
  password: z.string().min(6, "Hasło musi mieć co najmniej 6 znaków"),
  trialType: z.enum(["standard", "extended"]).optional(),
  referralCode: z.string().optional(),
});

export const resetPasswordRequestSchema = z.object({
  email: z.string().email("Nieprawidłowy adres email"),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1, "Token jest wymagany"),
  password: z.string().min(6, "Hasło musi mieć co najmniej 6 znaków"),
});

export const insertOfferSheetSchema = createInsertSchema(offerSheets).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOfferSheetHistorySchema = createInsertSchema(offerSheetHistory).omit({
  id: true,
  createdAt: true,
});

export const insertCompetitorHandleSchema = createInsertSchema(competitorHandles).omit({
  id: true,
  userId: true,
  createdAt: true,
});

export const insertContentIdeaSchema = createInsertSchema(contentIdeas).omit({
  id: true,
  userId: true,
  createdAt: true,
});

export const insertContentPipelineSnapshotSchema = createInsertSchema(
  contentPipelineSnapshots
).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserReelSchema = createInsertSchema(userReels).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCompetitorAnalysisSchema = createInsertSchema(competitorAnalysis).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertIdeaBankSummarySchema = createInsertSchema(ideaBankSummaries).omit({
  id: true,
  userId: true,
  createdAt: true,
  lastUpdated: true,
});

export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInspirationGroupSchema = createInsertSchema(inspirationGroups).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInspirationLinkSchema = createInsertSchema(inspirationLinks).omit({
  id: true,
  userId: true,
  createdAt: true,
});

export const insertViralInspirationSchema = createInsertSchema(viralInspirations).omit({
  id: true,
  createdAt: true,
});

export const insertUserContentProfileSchema = createInsertSchema(userContentProfiles).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

// Meta (Facebook) ad account integrations – stores OAuth tokens per user
export const metaIntegrations = pgTable("meta_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  metaUserId: varchar("meta_user_id"), // Meta user ID from Graph API
  metaUserName: varchar("meta_user_name"), // Display name from Meta
  accessToken: text("access_token").notNull(), // Long-lived token (encrypted via AES-256-GCM)
  tokenExpiresAt: timestamp("token_expires_at"), // When the token expires (null = no expiry / long-lived)
  selectedAdAccountId: varchar("selected_ad_account_id"), // Currently selected ad account ID (act_xxx)
  selectedAdAccountName: varchar("selected_ad_account_name"), // Human-readable name
  adAccounts:
    jsonb("ad_accounts").$type<
      { id: string; name: string; accountStatus: number; currency: string }[]
    >(), // Cached list
  adAccountsRefreshedAt: timestamp("ad_accounts_refreshed_at"), // Last time the catalog was re-fetched from Meta (stale-while-revalidate auto-refresh)
  scopes: text("scopes"), // Comma-separated list of granted scopes
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertMetaIntegrationSchema = createInsertSchema(metaIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Meta Ad Cache ──────────────────────────────────────────────
// Lookup table populated on-demand from Meta Graph API to enrich
// tracking_conversions.last_utm_content (= Meta ad.id) with
// human-readable ad/campaign metadata in the leads UI. last_synced_at
// drives staleness — refresh after 24 h. PK is composite (user_id, ad_id)
// so different agencies/accounts don't collide if Meta reuses IDs.
export const metaAdCache = pgTable(
  "meta_ad_cache",
  {
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    adId: varchar("ad_id", { length: 64 }).notNull(),
    adName: text("ad_name"),
    accountId: varchar("account_id", { length: 64 }),
    adsetId: varchar("adset_id", { length: 64 }),
    adsetName: text("adset_name"),
    campaignId: varchar("campaign_id", { length: 64 }),
    campaignName: text("campaign_name"),
    thumbnailUrl: text("thumbnail_url"),
    lastSyncedAt: timestamp("last_synced_at").defaultNow().notNull(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.userId, t.adId] }),
  })
);

export type MetaAdCacheRow = typeof metaAdCache.$inferSelect;

// Slack workspace integrations — one workspace per user (MVP). Bot token stored AES-256-GCM encrypted.
export const slackIntegrations = pgTable(
  "slack_integrations",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    teamId: varchar("team_id").notNull(),
    teamName: varchar("team_name"),
    label: varchar("label"),
    botUserId: varchar("bot_user_id").notNull(),
    botAccessToken: text("bot_access_token").notNull(),
    authedUserId: varchar("authed_user_id"),
    scopes: text("scopes"),
    channels: jsonb("channels")
      .$type<{ id: string; name: string; isPrivate: boolean }[]>()
      .default([]),
    channelsRefreshedAt: timestamp("channels_refreshed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    userTeamUniq: uniqueIndex("slack_integrations_user_team_idx").on(t.userId, t.teamId),
  })
);

export const insertSlackIntegrationSchema = createInsertSchema(slackIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const SLACK_EVENT_TYPES = [
  "kpi_daily",
  "integration_failure",
  "campaign_milestone",
  "new_lead",
] as const;
export type SlackEventType = (typeof SLACK_EVENT_TYPES)[number];
export const SLACK_DESTINATIONS = ["channel", "dm"] as const;
export type SlackDestination = (typeof SLACK_DESTINATIONS)[number];

// Per-event delivery preferences for Slack notifications.
export const slackEventPreferences = pgTable(
  "slack_event_preferences",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    slackIntegrationId: varchar("slack_integration_id")
      .notNull()
      .references(() => slackIntegrations.id, { onDelete: "cascade" }),
    eventType: varchar("event_type").notNull(),
    enabled: boolean("enabled").default(true).notNull(),
    destination: varchar("destination").notNull(),
    channelId: varchar("channel_id"),
    channelName: varchar("channel_name"),
    /** Warsaw local hour (0..23) at which the KPI daily report is delivered. Only meaningful for eventType="kpi_daily". */
    sendHour: integer("send_hour").default(6).notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    integrationEventUniq: uniqueIndex("slack_event_prefs_integration_event_idx").on(
      t.slackIntegrationId,
      t.eventType
    ),
    byUser: index("slack_event_prefs_user_idx").on(t.userId),
    byIntegration: index("slack_event_prefs_integration_idx").on(t.slackIntegrationId),
  })
);

export const insertSlackEventPreferenceSchema = createInsertSchema(slackEventPreferences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Agency-level Slack integration — one workspace per agency. Lets agency owners
// receive a forwarded copy of all client events in a separate workspace/channel.
export const agencySlackIntegrations = pgTable(
  "agency_slack_integrations",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    agencyId: varchar("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    teamId: varchar("team_id").notNull(),
    teamName: varchar("team_name"),
    label: varchar("label"),
    botUserId: varchar("bot_user_id").notNull(),
    botAccessToken: text("bot_access_token").notNull(),
    authedUserId: varchar("authed_user_id"),
    scopes: text("scopes"),
    channels: jsonb("channels")
      .$type<{ id: string; name: string; isPrivate: boolean }[]>()
      .default([]),
    channelsRefreshedAt: timestamp("channels_refreshed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    agencyTeamUniq: uniqueIndex("agency_slack_integrations_agency_team_idx").on(
      t.agencyId,
      t.teamId
    ),
  })
);

export const insertAgencySlackIntegrationSchema = createInsertSchema(agencySlackIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Per-event delivery preferences for agency-level Slack notifications.
// `clientFilter` controls which client users' events get forwarded:
//   null / [] = all clients in the agency
//   ["uid1", "uid2"] = only those specific client users
export const agencySlackEventPreferences = pgTable(
  "agency_slack_event_preferences",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    agencyId: varchar("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    agencySlackIntegrationId: varchar("agency_slack_integration_id")
      .notNull()
      .references(() => agencySlackIntegrations.id, { onDelete: "cascade" }),
    eventType: varchar("event_type").notNull(),
    enabled: boolean("enabled").default(true).notNull(),
    destination: varchar("destination").notNull(),
    channelId: varchar("channel_id"),
    channelName: varchar("channel_name"),
    clientFilter: jsonb("client_filter").$type<string[] | null>(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    integrationEventUniq: uniqueIndex("agency_slack_event_prefs_integration_event_idx").on(
      t.agencySlackIntegrationId,
      t.eventType
    ),
    byAgency: index("agency_slack_event_prefs_agency_idx").on(t.agencyId),
    byIntegration: index("agency_slack_event_prefs_integration_idx").on(t.agencySlackIntegrationId),
  })
);

export const insertAgencySlackEventPreferenceSchema = createInsertSchema(
  agencySlackEventPreferences
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Cache of Meta Ads account-level daily insights, one row per (user, adAccount, date).
// Populated incrementally: first call backfills ~360 days, subsequent calls only
// refetch the last 2 days (attribution window) and append any new days since
// the latest cached date. Lets the Dashboard "Dane" tab render full user history
// without hammering the Graph API on every load.
export const metaDailyInsights = pgTable(
  "meta_daily_insights",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    adAccountId: varchar("ad_account_id").notNull(),
    date: date("date").notNull(),
    data: jsonb("data").notNull(), // full DailyInsight payload
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    // Required by the daily-cache upsert (onConflictDoUpdate on these 3 columns).
    userAccountDateUniq: uniqueIndex("meta_daily_insights_user_account_date_uniq").on(
      t.userId,
      t.adAccountId,
      t.date
    ),
  })
);

// Sync state for Meta Lead Gen Instant Forms — tracks last sync per (user, page, form).
// Used for incremental polling: we only fetch leads with created_time > lastLeadCreatedTime.
// One row is created on first sync; updated on every subsequent sync.
export const metaLeadgenSyncState = pgTable(
  "meta_leadgen_sync_state",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    // Soft reference to users.id — NO hard FK on purpose. Agency clients can be
    // assigned as ad-account-only (agencyClients.userId pointing at a row that may
    // not exist in users, same as trackingVisitors.ownerUserId). A hard FK here
    // 500s lead sync for those clients. Keep ownership soft, consistent with the
    // rest of the system.
    userId: varchar("user_id").notNull(),
    pageId: varchar("page_id").notNull(),
    formId: varchar("form_id").notNull(),
    formName: text("form_name"),
    lastSyncedAt: timestamp("last_synced_at"),
    lastLeadCreatedAt: timestamp("last_lead_created_at"), // created_time of the newest lead we've imported
    lastImportedCount: integer("last_imported_count").default(0),
    lastError: text("last_error"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    uniqueUserForm: uniqueIndex("meta_leadgen_sync_user_form_idx").on(t.userId, t.pageId, t.formId),
  })
);

// Async job tracking for /api/meta/leadgen/sync-all.
// Sync-all can take minutes (forms × pages × paginated leads), which exceeds
// CloudFront's 60s origin response timeout → 504. We run the sync in the background
// and let the UI poll this table for status.
export const metaLeadgenSyncJobs = pgTable(
  "meta_leadgen_sync_jobs",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    // Soft reference to users.id — NO hard FK (see metaLeadgenSyncState.userId).
    userId: varchar("user_id").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("running"), // running | completed | failed
    phase: varchar("phase", { length: 20 }), // scanning (pages→forms) | importing (forms→leads) | null
    totalImported: integer("total_imported").default(0),
    totalSkipped: integer("total_skipped").default(0),
    formsCount: integer("forms_count").default(0), // forms processed so far (or pagesScanned in scanning phase)
    formsTotal: integer("forms_total").default(0), // total forms (or pagesTotal in scanning phase)
    errorMessage: text("error_message"),
    summary: jsonb("summary"), // full SyncSummary on completion (forms[], errors[])
    startedAt: timestamp("started_at").defaultNow().notNull(),
    completedAt: timestamp("completed_at"),
  },
  (t) => ({
    byUserStartedAt: index("meta_leadgen_sync_jobs_user_started_idx").on(t.userId, t.startedAt),
  })
);

// ─── TikTok Ads (TikTok for Business / Marketing API) integrations ───
// SEPARATE from `tiktokIntegrations` (Content Creator API, open.tiktokapis.com,
// scope video.*) — that token does NOT work on business-api.tiktok.com.
// This table holds the TikTok for Business Marketing API OAuth tokens per user.
// Mirrors metaIntegrations 1:1. Tokens are AES-256-GCM encrypted via encryptToken.
export const tiktokAdsIntegrations = pgTable("tiktok_ads_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  tiktokBusinessId: varchar("tiktok_business_id"), // Business Center / core_user id from TikTok
  businessName: varchar("business_name"), // Display name
  accessToken: text("access_token").notNull(), // Marketing API token (encrypted via AES-256-GCM)
  refreshToken: text("refresh_token"), // Refresh token (encrypted via AES-256-GCM)
  tokenExpiresAt: timestamp("token_expires_at"), // When the access token expires
  selectedAdvertiserId: varchar("selected_advertiser_id"), // Currently active advertiser account
  selectedAdvertiserName: varchar("selected_advertiser_name"), // Human-readable name
  advertiserAccounts: jsonb("advertiser_accounts").$type<{ id: string; name: string }[]>(), // Cached list (mirror adAccounts[])
  scopes: text("scopes"), // Comma-separated list of granted scopes
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTiktokAdsIntegrationSchema = createInsertSchema(tiktokAdsIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Sync state for TikTok Lead Gen Instant Forms — tracks last sync per (user, advertiser, page, form).
// Mirror of metaLeadgenSyncState: incremental polling fetches leads with create_time > lastLeadCreatedAt.
export const tiktokLeadgenSyncState = pgTable(
  "tiktok_leadgen_sync_state",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    // Soft reference to users.id — NO hard FK (see metaLeadgenSyncState.userId).
    userId: varchar("user_id").notNull(),
    advertiserId: varchar("advertiser_id").notNull(),
    pageId: varchar("page_id").notNull(), // TikTok Instant Form / page id
    formId: varchar("form_id").notNull(),
    formName: text("form_name"),
    lastSyncedAt: timestamp("last_synced_at"),
    lastLeadCreatedAt: timestamp("last_lead_created_at"), // create_time of the newest lead we've imported
    lastImportedCount: integer("last_imported_count").default(0),
    lastError: text("last_error"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    uniqueUserForm: uniqueIndex("tiktok_leadgen_sync_user_form_idx").on(
      t.userId,
      t.advertiserId,
      t.pageId,
      t.formId
    ),
  })
);

// Social automation accounts synced from Meta Pages + connected Instagram Professional accounts.
export const socialAccounts = pgTable("social_accounts", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  platform: varchar("platform", { length: 20 }).notNull(), // 'facebook' | 'instagram'
  accountKey: varchar("account_key", { length: 120 }).notNull(), // stable per user/platform/page/ig
  pageId: varchar("page_id").notNull(),
  igUserId: varchar("ig_user_id"),
  name: text("name").notNull(),
  pageName: text("page_name"),
  pageAccessToken: text("page_access_token").notNull(), // encrypted Page Access Token
  subscribed: boolean("subscribed").default(false).notNull(),
  subscriptionError: text("subscription_error"),
  lastSubscribedAt: timestamp("last_subscribed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const socialAutomationRules = pgTable("social_automation_rules", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  accountId: varchar("account_id")
    .notNull()
    .references(() => socialAccounts.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  platform: varchar("platform", { length: 20 }).notNull(), // 'facebook' | 'instagram'
  type: varchar("type", { length: 30 }).notNull(), // 'comment_to_dm' | 'dm_reply'
  mediaScope: varchar("media_scope", { length: 20 }).notNull().default("all"), // 'all' | 'selected'
  mediaIds: jsonb("media_ids").$type<string[]>().default([]),
  triggerType: varchar("trigger_type", { length: 20 }).notNull().default("any"), // 'any' | 'keywords'
  keywords: jsonb("keywords").$type<string[]>().default([]),
  responseText: text("response_text").notNull(),
  responseLink: text("response_link"),
  publicReplyText: text("public_reply_text"), // optional public comment reply alongside private DM (comment_to_dm only)
  active: boolean("active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const socialAutomationEvents = pgTable("social_automation_events", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  accountId: varchar("account_id").references(() => socialAccounts.id, { onDelete: "set null" }),
  platform: varchar("platform", { length: 20 }).notNull(),
  eventType: varchar("event_type", { length: 20 }).notNull(), // 'comment' | 'dm'
  providerEventId: varchar("provider_event_id").notNull(),
  senderId: varchar("sender_id"),
  recipientId: varchar("recipient_id"),
  mediaId: varchar("media_id"),
  commentId: varchar("comment_id"),
  messageId: varchar("message_id"),
  text: text("text"),
  payload: jsonb("payload"),
  processedAt: timestamp("processed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const socialAutomationDeliveries = pgTable("social_automation_deliveries", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  ruleId: varchar("rule_id")
    .notNull()
    .references(() => socialAutomationRules.id, { onDelete: "cascade" }),
  eventId: varchar("event_id").references(() => socialAutomationEvents.id, {
    onDelete: "set null",
  }),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  accountId: varchar("account_id")
    .notNull()
    .references(() => socialAccounts.id, { onDelete: "cascade" }),
  recipientId: varchar("recipient_id"),
  commentId: varchar("comment_id"),
  messageId: varchar("message_id"),
  deliveryType: varchar("delivery_type", { length: 30 }).notNull(), // 'private_reply' | 'dm_reply'
  dedupeKey: varchar("dedupe_key", { length: 255 }).notNull().unique(),
  messageText: text("message_text").notNull(),
  status: varchar("status", { length: 20 }).notNull(), // 'sent' | 'failed' | 'skipped'
  metaMessageId: varchar("meta_message_id"),
  errorMessage: text("error_message"),
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSocialAccountSchema = createInsertSchema(socialAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSocialAutomationRuleSchema = createInsertSchema(socialAutomationRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// LeadConnector (GHL) CRM integrations – stores OAuth tokens per user
export const ghlIntegrations = pgTable("ghl_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  ghlUserId: varchar("ghl_user_id"),
  locationId: varchar("location_id").notNull(),
  locationName: varchar("location_name"),
  companyId: varchar("company_id"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  // VP-194: ślad nieudanego odświeżania tokenu OAuth (martwy refresh_token — apka odłączona
  // / re-instalowana po stronie GHL). authErrorAt = początek BIEŻĄCEJ serii porażek
  // (null = integracja zdrowa); pozwala policzyć „martwy > X dni" i zaalertować klienta o reconnect.
  authErrorAt: timestamp("auth_error_at"),
  authErrorMessage: text("auth_error_message"),
  authErrorCount: integer("auth_error_count").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGhlIntegrationSchema = createInsertSchema(ghlIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// HubSpot CRM integrations – stores OAuth tokens per user
export const hubspotIntegrations = pgTable("hubspot_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  hubspotPortalId: varchar("hubspot_portal_id"),
  hubspotPortalName: varchar("hubspot_portal_name"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertHubspotIntegrationSchema = createInsertSchema(hubspotIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// HubSpot pipeline stage → conversion type mappings
// User maps their deal stages to standardized funnel events
export const hubspotStageMappings = pgTable("hubspot_stage_mappings", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  pipelineId: varchar("pipeline_id").notNull(),
  pipelineLabel: varchar("pipeline_label").notNull(),
  stageId: varchar("stage_id").notNull(),
  stageLabel: varchar("stage_label").notNull(),
  // Standardized conversion type this stage maps to
  // null = ignore (don't create conversion for this stage)
  conversionType: varchar("conversion_type", { length: 50 }), // lead | qualified_lead | booked_call | no_show | unqualified_call | sale
  // Which deal property to use as revenue (null = no revenue)
  revenueField: varchar("revenue_field", { length: 100 }), // 'amount' or custom field name
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertHubspotStageMappingSchema = createInsertSchema(hubspotStageMappings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type HubspotStageMapping = typeof hubspotStageMappings.$inferSelect;
export type InsertHubspotStageMapping = z.infer<typeof insertHubspotStageMappingSchema>;

// Tracks which deals have already been synced (deduplication)
export const hubspotSyncedDeals = pgTable("hubspot_synced_deals", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  dealId: varchar("deal_id").notNull(), // HubSpot deal ID
  stageId: varchar("stage_id").notNull(), // stage at time of sync
  conversionId: varchar("conversion_id"), // FK to tracking_conversions.id
  syncedAt: timestamp("synced_at").defaultNow(),
});

// Google Ads integrations – stores OAuth tokens per user
export const googleAdsIntegrations = pgTable("google_ads_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  googleEmail: varchar("google_email"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  selectedCustomerId: varchar("selected_customer_id"), // Google Ads customer ID (xxx-xxx-xxxx)
  selectedCustomerName: varchar("selected_customer_name"),
  customerAccounts:
    jsonb("customer_accounts").$type<
      { id: string; descriptiveName: string; currencyCode: string; managerCustomerId?: string }[]
    >(),
  customerAccountsRefreshedAt: timestamp("customer_accounts_refreshed_at"), // Last catalog re-fetch from Google (stale-while-revalidate auto-refresh)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGoogleAdsIntegrationSchema = createInsertSchema(googleAdsIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// YouTube integrations – stores OAuth tokens + selected channel per user
export const youtubeIntegrations = pgTable("youtube_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  googleEmail: varchar("google_email"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  channelId: varchar("channel_id"),
  channelTitle: varchar("channel_title"),
  channelThumbnail: varchar("channel_thumbnail"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertYoutubeIntegrationSchema = createInsertSchema(youtubeIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// TikTok integrations – stores OAuth tokens + selected creator account per user
export const tiktokIntegrations = pgTable("tiktok_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  tiktokOpenId: varchar("tiktok_open_id"),
  tiktokUsername: varchar("tiktok_username"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  scopes: text("scopes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTiktokIntegrationSchema = createInsertSchema(tiktokIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── LinkedIn Integration ───
export const linkedinIntegrations = pgTable("linkedin_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  linkedinPersonId: varchar("linkedin_person_id"),
  linkedinName: varchar("linkedin_name"),
  linkedinEmail: varchar("linkedin_email"),
  linkedinOrganizationId: varchar("linkedin_organization_id"),
  linkedinOrganizationName: varchar("linkedin_organization_name"),
  linkedinPublishAs: varchar("linkedin_publish_as", { length: 20 })
    .notNull()
    .default("person")
    .$type<"person" | "organization">(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token"),
  tokenExpiresAt: timestamp("token_expires_at"),
  scopes: text("scopes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertLinkedinIntegrationSchema = createInsertSchema(linkedinIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Allegro Integration ───
export const allegroIntegrations = pgTable("allegro_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  allegroLogin: varchar("allegro_login"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  isSandbox: boolean("is_sandbox").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAllegroIntegrationSchema = createInsertSchema(allegroIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Google Merchant Center Integration ───
export const googleMerchantIntegrations = pgTable("google_merchant_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  googleEmail: varchar("google_email"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  merchantId: varchar("merchant_id"),
  merchantName: varchar("merchant_name"),
  merchantAccounts: jsonb("merchant_accounts").$type<{ id: string; name: string }[]>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGoogleMerchantIntegrationSchema = createInsertSchema(
  googleMerchantIntegrations
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── BaseLinker Integration ───
export const baseLinkerIntegrations = pgTable("baselinker_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  apiToken: text("api_token").notNull(),
  connectedAt: timestamp("connected_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertBaseLinkerIntegrationSchema = createInsertSchema(baseLinkerIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Shopify Integration ───
// Supports TWO install modes:
//   1. Custom App (legacy) — user pastes shopDomain + accessToken manually (isOAuth=false)
//   2. OAuth Public App    — user clicks "Connect Shopify", completes OAuth flow (isOAuth=true)
export const shopifyIntegrations = pgTable("shopify_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  shopDomain: varchar("shop_domain").notNull(), // e.g. "mystore.myshopify.com"
  accessToken: text("access_token").notNull(), // AES-256-GCM encrypted
  // Expiring offline tokens (VP-236): appki publiczne po 2026-04-01 dostają
  // access token z TTL ~1h + refresh token (~90 dni). NULL-e = legacy token
  // bezterminowy (ręczny /connect lub instalacja sprzed migracji).
  refreshToken: text("refresh_token"), // AES-256-GCM encrypted
  accessTokenExpiresAt: timestamp("access_token_expires_at"),
  refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
  shopName: varchar("shop_name"),
  isOAuth: boolean("is_oauth").default(false).notNull(),
  scopes: text("scopes"), // comma-separated granted scopes (OAuth only)
  installedAt: timestamp("installed_at"),
  uninstalledAt: timestamp("uninstalled_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertShopifyIntegrationSchema = createInsertSchema(shopifyIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

/**
 * Eventy z Shopify Custom Pixel (Customer Events sandbox) — jedyne miejsce,
 * z ktorego dostajemy fbp/fbc/tracking_id NA CHECKOUCIE i thank-you page
 * (ScriptTag tam nie dziala od Checkout Extensibility). Webhook orders/paid
 * stitchuje sie z tym po (shop_domain, order_id) lub (shop_domain,
 * checkout_token) → pelny identity bridge sciezka A, jak Hyros/wetracked.
 */
export const shopifyPixelEvents = pgTable(
  "shopify_pixel_events",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    shopDomain: varchar("shop_domain").notNull(),
    eventName: varchar("event_name").notNull(), // checkout_started | payment_info_submitted | checkout_completed
    checkoutToken: varchar("checkout_token"),
    orderId: varchar("order_id"), // dostepne dopiero na checkout_completed
    trackingId: varchar("tracking_id"), // _vp_tid cookie (nasz first-party id)
    fbp: text("fbp"),
    fbc: text("fbc"),
    email: text("email"),
    phone: text("phone"),
    pageUrl: text("page_url"),
    userAgent: text("user_agent"),
    ip: text("ip"),
    revenue: text("revenue"),
    currency: varchar("currency", { length: 3 }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    shopOrderIdx: index("shopify_pixel_events_shop_order_idx").on(table.shopDomain, table.orderId),
    shopCheckoutIdx: index("shopify_pixel_events_shop_checkout_idx").on(
      table.shopDomain,
      table.checkoutToken
    ),
    createdAtIdx: index("shopify_pixel_events_created_at_idx").on(table.createdAt),
  })
);

export const insertShopifyPixelEventSchema = createInsertSchema(shopifyPixelEvents).omit({
  id: true,
  createdAt: true,
});

/**
 * Stripe jako ŹRÓDŁO DANYCH klienta (NIE billing VP) — klient SaaS łączy swój
 * Stripe przez Connect OAuth (read_only), my czytamy subskrypcje i liczymy
 * MRR / aktywne / trial / churn / ARPU pod dashboard w trybie 'saas'.
 * Tokeny szyfrowane AES-256-GCM (encryptToken). Metryki cache'owane po syncu.
 */
export const stripeIntegrations = pgTable("stripe_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  stripeAccountId: varchar("stripe_account_id"), // acct_... (connected account z Connect OAuth)
  accountName: varchar("account_name"),
  accessToken: text("access_token"), // AES-256-GCM encrypted (Connect OAuth access token)
  refreshToken: text("refresh_token"), // AES-256-GCM encrypted (Connect OAuth refresh token)
  livemode: boolean("livemode").default(true).notNull(),
  scopes: text("scopes"), // np. "read_only"
  // metryki z ostatniego synca (MRR/ARPU znormalizowane do /msc w walucie konta)
  cachedMrr: numeric("cached_mrr", { precision: 14, scale: 2 }),
  cachedActiveSubs: integer("cached_active_subs"),
  cachedTrialingSubs: integer("cached_trialing_subs"),
  cachedChurned30d: integer("cached_churned_30d"),
  cachedArpu: numeric("cached_arpu", { precision: 14, scale: 2 }),
  currency: varchar("currency", { length: 10 }),
  lastSyncAt: timestamp("last_sync_at"),
  lastSyncError: text("last_sync_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertStripeIntegrationSchema = createInsertSchema(stripeIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type StripeIntegration = typeof stripeIntegrations.$inferSelect;
export type InsertStripeIntegration = z.infer<typeof insertStripeIntegrationSchema>;

/**
 * Pending Shopify installs — token zapisany ZANIM klient skojarzy go z kontem
 * w viral.partners. Tworzy się w shop-first flow: merchant instaluje aplikację
 * z Shopify, my dostajemy OAuth callback bez `user_id` w state, więc trzymamy
 * tu access_token aż do claim-u przez zalogowanego usera.
 *
 * Auto-cleanup: rekord usuwany po claim lub po wygaśnięciu (`expires_at`).
 * Cron czyści expired raz na dobę (do dodania osobno albo on-read).
 */
export const pendingShopifyInstalls = pgTable(
  "pending_shopify_installs",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    shopDomain: varchar("shop_domain").notNull(),
    accessToken: text("access_token").notNull(), // AES-256-GCM encrypted
    // Expiring offline tokens (VP-236) — parkowane razem z access tokenem;
    // access token zdąży wygasnąć przed claimem (TTL 1h vs 24h na claim),
    // ale refresh token żyje 90 dni, więc claim + pierwszy call go odświeżą.
    refreshToken: text("refresh_token"), // AES-256-GCM encrypted
    accessTokenExpiresAt: timestamp("access_token_expires_at"),
    refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
    shopName: varchar("shop_name"),
    scopes: text("scopes"),
    // Token jednorazowy do zclaim-owania integracji. Generowany przez backend,
    // wręczany klientowi przez URL `/integracje/shopify/claim?token=...`.
    claimToken: varchar("claim_token").notNull().unique(),
    expiresAt: timestamp("expires_at").notNull(),
    installedAt: timestamp("installed_at").defaultNow().notNull(),
  },
  (table) => ({
    claimTokenIdx: uniqueIndex("pending_shopify_installs_claim_token_idx").on(table.claimToken),
    shopDomainIdx: index("pending_shopify_installs_shop_domain_idx").on(table.shopDomain),
  })
);

export type PendingShopifyInstall = typeof pendingShopifyInstalls.$inferSelect;

/**
 * Shopify GDPR/compliance webhooks — audit trail (VP-207).
 * Shopify wymaga obsługi 3 topiców (customers/data_request, customers/redact,
 * shop/redact) i my musimy umieć UDOWODNIĆ, że żądanie przyszło i zostało
 * wykonane (App Store review + RODO art. 5(2) rozliczalność). Payload żądania
 * przechowujemy w całości — dla data_request to jedyne źródło "czego dotyczy
 * żądanie" przy ręcznej realizacji (merchant ma 30 dni); podstawa retencji:
 * wykazanie zgodności z obowiązkiem prawnym.
 */
export const shopifyGdprRequests = pgTable(
  "shopify_gdpr_requests",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    topic: varchar("topic", { length: 50 }).notNull(), // customers/data_request | customers/redact | shop/redact
    shopDomain: varchar("shop_domain").notNull(),
    payload: jsonb("payload").$type<Record<string, unknown>>().notNull(),
    // received → completed | failed; data_request zostaje 'received' aż człowiek
    // zrealizuje eksport (skrzynka TODO), redakty przechodzą automatycznie.
    status: varchar("status", { length: 20 }).notNull().default("received"),
    details: jsonb("details").$type<Record<string, unknown>>(), // liczniki zredagowanych wierszy / błąd
    receivedAt: timestamp("received_at").defaultNow().notNull(),
    processedAt: timestamp("processed_at"),
  },
  (table) => ({
    shopReceivedIdx: index("shopify_gdpr_requests_shop_received_idx").on(
      table.shopDomain,
      table.receivedAt
    ),
    statusIdx: index("shopify_gdpr_requests_status_idx").on(table.status),
  })
);

export type ShopifyGdprRequest = typeof shopifyGdprRequests.$inferSelect;

// ─── E-commerce Custom Costs ───
// Per-user catalog of cost components used to compute true profit per campaign:
//   - cogs_pct        → percentage of revenue (e.g. 0.30 = 30% COGS)
//   - cogs_fixed      → fixed amount per unit / per order
//   - shipping_pct    → percentage of revenue
//   - shipping_fixed  → fixed amount per order
//   - platform_fee_pct→ Shopify/PayPal/Stripe processing fees as %
//   - monthly_fixed   → recurring monthly overhead (rent, software, salaries)
//   - one_time        → one-off cost in date window
// Scope: optional `productId` (Shopify product GID) and/or `campaignId` (Meta/Google
// campaign id) — null means "applies to everything" for that user.
export const ecommerceCosts = pgTable("ecommerce_costs", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 200 }).notNull(),
  costType: varchar("cost_type", { length: 30 }).notNull(),
  value: numeric("value", { precision: 14, scale: 4 }).notNull(),
  currency: varchar("currency", { length: 3 }).default("PLN"),
  productId: varchar("product_id"),
  campaignId: varchar("campaign_id"),
  startDate: timestamp("start_date"),
  endDate: timestamp("end_date"),
  active: boolean("active").default(true).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertEcommerceCostSchema = createInsertSchema(ecommerceCosts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Calendly Integration ───
export const calendlyIntegrations = pgTable("calendly_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  calendlyUserUri: varchar("calendly_user_uri"),
  calendlyUserName: varchar("calendly_user_name"),
  calendlyUserEmail: varchar("calendly_user_email"),
  organizationUri: varchar("organization_uri"),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at"),
  selectedEventType: varchar("selected_event_type"),
  eventTypes:
    jsonb("event_types").$type<{ uri: string; name: string; slug: string; active: boolean }[]>(),
  webhookSubscriptionUri: varchar("webhook_subscription_uri"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCalendlyIntegrationSchema = createInsertSchema(calendlyIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Ringostat Integration ───
export const ringostatIntegrations = pgTable("ringostat_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  projectId: varchar("project_id").notNull(),
  apiKey: text("api_key"), // deprecated – webhook-based, no API key needed
  projectName: varchar("project_name"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertRingostatIntegrationSchema = createInsertSchema(ringostatIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Ringostat Calls (from webhooks) ───
export const ringostatCalls = pgTable(
  "ringostat_calls",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    projectId: varchar("project_id").notNull(),
    callId: varchar("call_id").notNull(), // Ringostat unique call ID
    callDate: timestamp("call_date").notNull(), // when the call happened
    caller: varchar("caller"), // caller number/SIP
    callee: varchar("callee"), // destination number
    callType: varchar("call_type").notNull(), // "out" or "in"
    status: varchar("status").notNull(), // ANSWERED, NO ANSWER, BUSY, etc.
    callDuration: integer("call_duration"), // total duration in seconds
    waitingTime: integer("waiting_time"), // waiting time in seconds
    dialogTime: integer("dialog_time"), // actual conversation time in seconds
    recordLink: text("record_link"), // URL to recording
    manager: varchar("manager"), // employee/manager name
    source: varchar("source").notNull().default("ringostat"), // 'ringostat' | 'ghl' — which telephony source
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    // Required for the webhook upsert ON CONFLICT (user_id, call_id) to work —
    // without this, every Ringostat webhook insert throws and no calls are stored.
    uniqueUserCall: uniqueIndex("ringostat_calls_user_call_uniq").on(t.userId, t.callId),
    // Speeds up the setter-stats range scan by (user, date).
    byUserDate: index("ringostat_calls_user_date_idx").on(t.userId, t.callDate),
  })
);

// ─── Ringostat Call AI Analysis (transcript + coaching) ───
export const ringostatCallAnalyses = pgTable(
  "ringostat_call_analyses",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    callId: varchar("call_id").notNull(), // matches ringostat_calls.callId
    callDate: timestamp("call_date"), // denormalized for time-bucketed score charts
    setterKey: varchar("setter_key"), // who handled the call (manager/agent)
    source: varchar("source").notNull().default("ringostat"), // 'ringostat' | 'ghl' — telephony source of the call
    status: varchar("status").notNull().default("pending"), // pending|transcribing|analyzing|done|failed
    transcript: text("transcript"),
    analysisHtml: text("analysis_html"), // full coaching feedback (Apple-style HTML)
    score: integer("score"), // 1-10 overall quality
    summary: text("summary"), // one-line headline ("największa dźwignia")
    tokensCharged: integer("tokens_charged"),
    error: text("error"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    uniqueUserCall: uniqueIndex("ringostat_call_analyses_user_call_uniq").on(t.userId, t.callId),
    byUserDate: index("ringostat_call_analyses_user_date_idx").on(t.userId, t.callDate),
  })
);

// Per-account config shape (KPI targets + conversion event mapping)
export interface AccountConfig {
  kpiCostPerLead?: string | null;
  kpiCostPerCall?: string | null;
  kpiCostPerClient?: string | null;
  kpiTargetRoas?: string | null;
  kpiCostPerTrial?: string | null; // SaaS: target cost per trial
  kpiCostPerSubscription?: string | null; // SaaS: target cost per subscription
  conversionLead?: string | null; // Meta action_type treated as "lead"
  conversionCall?: string | null; // Meta action_type treated as "scheduled call"
  conversionSale?: string | null; // Meta action_type treated as "sale"
  conversionTrial?: string | null; // SaaS: Meta action_type treated as "trial signup"
  conversionSubscription?: string | null; // SaaS: Meta action_type treated as "paid subscription"
}

// Analytics settings – per-user KPI targets and data source preferences
export const analyticsSettings = pgTable("analytics_settings", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" })
    .unique(),
  // Which ad accounts to include in reports (array of act_xxx IDs)
  selectedAdAccountIds: jsonb("selected_ad_account_ids").$type<string[]>().default([]),
  // Which agency clients to include in Slack reports (array of agency_client IDs)
  selectedClientIds: jsonb("selected_client_ids").$type<string[]>().default([]),
  // Data sources to include
  dataSources: jsonb("data_sources")
    .$type<{ meta: boolean; ghl: boolean; googleSheets: boolean }>()
    .default({ meta: true, ghl: false, googleSheets: false }),
  // Google Sheets URL for lead quality data
  googleSheetUrl: text("google_sheet_url"),
  // Column mapping: { "0": "source", "1": "name", ... } — user-defined in settings
  googleSheetColumnMapping: jsonb("google_sheet_column_mapping")
    .$type<Record<string, string>>()
    .default({}),
  // Global KPI targets (fallback when no per-account config)
  kpiCostPerLead: varchar("kpi_cost_per_lead"),
  kpiCostPerCall: varchar("kpi_cost_per_call"),
  kpiCostPerClient: varchar("kpi_cost_per_client"),
  kpiTargetRoas: varchar("kpi_target_roas"),
  // Per-account overrides: { [accountId]: AccountConfig }
  accountConfigs: jsonb("account_configs").$type<Record<string, AccountConfig>>().default({}),
  // Slack integration
  slackWebhookUrl: text("slack_webhook_url"),
  slackEnabled: boolean("slack_enabled").default(false),
  lastSlackSentAt: timestamp("last_slack_sent_at"),
  // Dashboard mode: ecommerce | lead_gen_basic | lead_gen_advanced
  dashboardMode: varchar("dashboard_mode").default("lead_gen_basic"),
  // GHL calendar IDs used for setter appointment tracking (advanced lead gen)
  setterCalendarIds: jsonb("setter_calendar_ids").$type<string[]>().default([]),
  // VP-168: GHL calendar IDs counted into the "Rozmowy" stats (dashboard, campaign table,
  // Sales Data). null = all calendars (legacy behaviour), [] = none → 0 conversations.
  // Independent from setterCalendarIds, where [] means "setter tab disabled".
  conversationCalendarIds: jsonb("conversation_calendar_ids").$type<string[]>(),
  // Setter call data source: 'ghl' | 'ringostat' (default ghl for backward compat)
  setterCallSource: varchar("setter_call_source").default("ghl"),
  // AI call analysis (Setter): when on, qualifying calls are auto-transcribed + coached.
  aiCallAnalysisEnabled: boolean("ai_call_analysis_enabled").default(false),
  // Minimum call duration (seconds) to bother analyzing — skips ultra-short calls.
  aiCallAnalysisMinSeconds: integer("ai_call_analysis_min_seconds").default(10),
  // User-configurable coaching prompt template (their own methodology/knowledge base).
  // Placeholders: {{transcript}}, {{company}}, {{offer}}, {{script}}, {{knowledge_base}}.
  aiCallAnalysisPrompt: text("ai_call_analysis_prompt"),
  // OpenRouter model id for the coaching analysis. null = default (Sonnet 4). Opus 4.8 is
  // a pricier, more thorough option (cost multiplier applied when charging tokens).
  aiCallAnalysisModel: varchar("ai_call_analysis_model"),
  // Ecommerce data source: 'meta' | 'allegro' (which platform powers the ecommerce dashboard)
  ecommerceSource: varchar("ecommerce_source").default("meta"),
  // Lead quality AI analysis
  leadQualityAiEnabled: boolean("lead_quality_ai_enabled").default(false),
  // Allegro catalog monitor — detect competitors selling the same EAN
  allegroMonitorEnabled: boolean("allegro_monitor_enabled").default(false),
  lastAllegroMonitorAt: timestamp("last_allegro_monitor_at"),
  // KPI Monitoring — periodic check of metrics vs targets (no AI, pure data comparison)
  monitoringEnabled: boolean("monitoring_enabled").default(false),
  lastMonitoringCheckAt: timestamp("last_monitoring_check_at"),
  // Quarterly sales KPI targets (all dashboard modes)
  quarterlyTargets: jsonb("quarterly_targets").$type<QuarterlyTargets>(),
  // Monthly sales KPI targets — same structure, keys like "2026-03"
  monthlyTargets: jsonb("monthly_targets").$type<MonthlyTargets>(),
  // KPI thresholds for dashboard KPI panel (user-configurable min/max per metric)
  kpiThresholds: jsonb("kpi_thresholds").$type<KpiThresholds>().default({}),
  // User-defined custom KPIs (metrics not covered by built-in definitions)
  customKpis: jsonb("custom_kpis").$type<CustomKpis>().default([]),
  // GHL attribution source: 'ghl' (default) | 'tracking_events' (use EverWebinar/tracking UTM data matched by email)
  ghlAttributionSource: varchar("ghl_attribution_source").default("ghl"),
  // Custom dashboard column configuration
  dashboardColumns: jsonb("dashboard_columns").$type<DashboardColumnConfig>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Period KPI targets — used for both quarterly (key: "2026-Q1") and monthly (key: "2026-03")
// Fields are optional to support all business types in one shared type
export interface QuarterlyTarget {
  // Lead Gen / VSL Funnel
  targetLeads?: number | null;
  targetCalls?: number | null; // umówione rozmowy
  targetSales?: number | null; // sprzedaże
  targetCpl?: number | null; // koszt na lead (zł) — target average for period
  targetCpbc?: number | null; // koszt na umówioną rozmowę (zł)
  // Ecommerce
  targetPurchaseValue?: number | null; // wartość sprzedaży (zł)
  targetPurchases?: number | null; // ilość sprzedaży
  targetCostPerPurchase?: number | null; // koszt za zakup (zł)
  targetRoas?: number | null; // ROAS (shared — lead gen & ecom)
  // SaaS
  targetTrials?: number | null;
  targetPaid?: number | null; // płatne subskrypcje
  targetMrr?: number | null; // MRR (zł)
  targetCpa?: number | null; // koszt na rejestrację (zł)
}

export interface QuarterlyTargets {
  [quarterKey: string]: QuarterlyTarget; // e.g. "2026-Q1", "2026-Q2"
}

export interface MonthlyTargets {
  [monthKey: string]: QuarterlyTarget; // e.g. "2026-03", "2026-04"
}

// Per-KPI threshold (min=higher is better, max=lower is better, range=both defined)
export interface KpiThreshold {
  min?: number | null; // value >= min is considered good
  max?: number | null; // value <= max is considered good
  // Manual value entered by user (used when the KPI cannot be calculated automatically).
  // When set, the dashboard displays this instead of the computed value.
  manualValue?: number | null;
}

// User-defined KPI not covered by the built-in definitions.
export interface CustomKpi {
  id: string; // uuid
  label: string;
  description?: string;
  format: "percent" | "currency" | "number";
  comparator: "gte" | "lte" | "range";
  min?: number | null;
  max?: number | null;
  value?: number | null; // current manual value
}

// Map of KPI key → threshold config. Keys defined in client/src/lib/kpi-definitions.ts
export interface KpiThresholds {
  [kpiKey: string]: KpiThreshold;
}

// List of user-defined custom KPIs (separate from KpiThresholds map).
export type CustomKpis = CustomKpi[];

export const insertAnalyticsSettingsSchema = createInsertSchema(analyticsSettings).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

// ─── Allegro Catalog Alerts ───
// Stores detected competing offers (same EAN) for user's Allegro listings
export const allegroCatalogAlerts = pgTable("allegro_catalog_alerts", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  offerId: varchar("offer_id").notNull(), // user's Allegro offer ID
  offerName: varchar("offer_name"), // user's offer title
  ean: varchar("ean"), // the EAN code
  competitorCount: integer("competitor_count").notNull().default(0), // how many OTHER sellers found
  competitorOffers: jsonb("competitor_offers")
    .$type<{ offerId: string; sellerLogin: string; price: string; currency: string }[]>()
    .default([]),
  userPrice: varchar("user_price"), // user's current price
  lowestPrice: varchar("lowest_price"), // cheapest competitor price
  notifiedAt: timestamp("notified_at"), // when Slack notification was sent
  resolvedAt: timestamp("resolved_at"), // when it was resolved (competitors gone)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Lead Quality Scores – AI-generated quality assessment for Google Sheets leads
export const leadQualityScores = pgTable("lead_quality_scores", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  // Lead identifier (hash of name+email+phone for deduplication)
  leadHash: varchar("lead_hash").notNull(),
  leadName: varchar("lead_name"),
  // AI quality score 1-10
  qualityScore: integer("quality_score").notNull(),
  // Short AI reasoning for the score
  reasoning: text("reasoning"),
  // Meta attribution IDs from Google Sheets
  campaignId: varchar("campaign_id"),
  adsetId: varchar("adset_id"),
  adId: varchar("ad_id"),
  // Raw lead data snapshot
  leadData: jsonb("lead_data").$type<Record<string, string>>(),
  isFake: boolean("is_fake"),
  analyzedAt: timestamp("analyzed_at").defaultNow(),
});

// Projects table for custom AI agents with user-defined context
export const projects = pgTable("projects", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: varchar("name").notNull(),
  description: varchar("description"),
  instructions: text("instructions"), // Custom instructions for the AI
  moduleId: varchar("module_id")
    .notNull()
    .references(() => modules.id),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertProjectSchema = createInsertSchema(projects).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export type Project = typeof projects.$inferSelect;
export type InsertProject = z.infer<typeof insertProjectSchema>;

// ─── Knowledge Chunks (RAG for marketing-analyst and other modules) ───
export const knowledgeChunks = pgTable("knowledge_chunks", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  moduleId: text("module_id").notNull(),
  variant: text("variant"),
  category: text("category").notNull(),
  title: text("title").notNull(),
  content: text("content").notNull(),
  embedding: vector1536("embedding"),
  sourceType: text("source_type").default("manual"),
  sourceRef: text("source_ref"),
  metadata: jsonb("metadata").default({}),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertKnowledgeChunkSchema = createInsertSchema(knowledgeChunks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type KnowledgeChunk = typeof knowledgeChunks.$inferSelect;
export type InsertKnowledgeChunk = z.infer<typeof insertKnowledgeChunkSchema>;

// ─── Analysis History (memory loop – what was recommended before?) ───
export const analysisHistory = pgTable("analysis_history", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  moduleId: text("module_id").notNull().default("marketing-analyst"),
  variant: text("variant"),
  analysisDate: date("analysis_date").defaultNow(),
  rawMetrics: jsonb("raw_metrics"),
  recommendations: text("recommendations").notNull(),
  actionsTaken: text("actions_taken"),
  outcome: text("outcome"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAnalysisHistorySchema = createInsertSchema(analysisHistory).omit({
  id: true,
  createdAt: true,
});

export type AnalysisHistory = typeof analysisHistory.$inferSelect;
export type InsertAnalysisHistory = z.infer<typeof insertAnalysisHistorySchema>;

// ─── Recommendation Feedback (learning loop) ───
export const recommendationFeedback = pgTable("recommendation_feedback", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  analysisId: varchar("analysis_id"),
  moduleId: text("module_id").notNull().default("marketing-analyst"),
  variant: text("variant"),
  rating: integer("rating").notNull(), // 1 = bad, 5 = good (or 0/1 for thumbs)
  recommendationSnippet: text("recommendation_snippet").notNull(), // which part was rated
  userCorrection: text("user_correction"), // what should have been said instead
  campaignContext: text("campaign_context"), // snapshot of data at time of feedback
  // Granular context fields:
  campaignId: text("campaign_id"),
  campaignName: text("campaign_name"),
  adsetId: text("adset_id"),
  adsetName: text("adset_name"),
  adId: text("ad_id"),
  adName: text("ad_name"),
  appliedToKnowledge: boolean("applied_to_knowledge").default(false), // was this turned into a knowledge chunk?
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertRecommendationFeedbackSchema = createInsertSchema(recommendationFeedback).omit({
  id: true,
  createdAt: true,
});

export type RecommendationFeedback = typeof recommendationFeedback.$inferSelect;
export type InsertRecommendationFeedback = z.infer<typeof insertRecommendationFeedbackSchema>;

export const insertModuleSchema = createInsertSchema(modules);

// Schema for new conversation structure
export const insertConversationSchema = createInsertSchema(conversations).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertChatSchema = createInsertSchema(chats).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertMessageSchema = createInsertSchema(messages)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    useIdeaBank: z.boolean().optional(),
    reelWithCTA: z.boolean().optional(),
    generateDescription: z.boolean().optional(),
    ctaAction: z.string().optional(),
    ctaReason: z.string().optional(),
    businessType: z.string().optional(),
  });

export const insertWorksheetSchema = createInsertSchema(worksheets).omit({
  id: true,
  uploadedAt: true,
});

// All type exports
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type LoginData = z.infer<typeof loginSchema>;
export type RegisterData = z.infer<typeof registerSchema>;
export type Module = typeof modules.$inferSelect;
export type InsertModule = z.infer<typeof insertModuleSchema>;
export type Conversation = typeof conversations.$inferSelect;
export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type Chat = typeof chats.$inferSelect;
export type InsertChat = z.infer<typeof insertChatSchema>;
export type Message = typeof messages.$inferSelect;
export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type Worksheet = typeof worksheets.$inferSelect;
export type InsertWorksheet = z.infer<typeof insertWorksheetSchema>;
export type Session = typeof sessions.$inferSelect;
export type OfferSheet = typeof offerSheets.$inferSelect;
export type InsertOfferSheet = z.infer<typeof insertOfferSheetSchema>;
export type OfferSheetHistory = typeof offerSheetHistory.$inferSelect;
export type InsertOfferSheetHistory = z.infer<typeof insertOfferSheetHistorySchema>;
export type CampaignDraft = typeof campaignDrafts.$inferSelect;
export type InsertCampaignDraft = typeof campaignDrafts.$inferInsert;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;
export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;
export type ResetPassword = z.infer<typeof resetPasswordSchema>;
export type CompetitorHandle = typeof competitorHandles.$inferSelect;
export type InsertCompetitorHandle = z.infer<typeof insertCompetitorHandleSchema>;
export type ContentIdea = typeof contentIdeas.$inferSelect;
export type InsertContentIdea = z.infer<typeof insertContentIdeaSchema>;
export type ContentPipelineSnapshot = typeof contentPipelineSnapshots.$inferSelect;
export type InsertContentPipelineSnapshot = z.infer<typeof insertContentPipelineSnapshotSchema>;
export type UserReel = typeof userReels.$inferSelect;
export type InsertUserReel = z.infer<typeof insertUserReelSchema>;
export type CompetitorAnalysis = typeof competitorAnalysis.$inferSelect;
export type InsertCompetitorAnalysis = z.infer<typeof insertCompetitorAnalysisSchema>;
export type UserProfile = typeof userProfiles.$inferSelect;
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type IdeaBankSummary = typeof ideaBankSummaries.$inferSelect;
export type ReelTranscript = typeof reelTranscripts.$inferSelect;
export type InsertReelTranscript = z.infer<typeof insertReelTranscriptSchema>;
export type UserClone = typeof userClones.$inferSelect;
export type InsertUserClone = z.infer<typeof insertUserCloneSchema>;
export type InspirationGroup = typeof inspirationGroups.$inferSelect;
export type InsertInspirationGroup = z.infer<typeof insertInspirationGroupSchema>;
export type InspirationLink = typeof inspirationLinks.$inferSelect;
export type InsertInspirationLink = z.infer<typeof insertInspirationLinkSchema>;
export type TokenUsageEvent = typeof tokenUsageEvents.$inferSelect;
export type InsertTokenUsageEvent = z.infer<typeof insertTokenUsageEventSchema>;
export type MetaIntegration = typeof metaIntegrations.$inferSelect;
export type InsertMetaIntegration = z.infer<typeof insertMetaIntegrationSchema>;
export type SlackIntegration = typeof slackIntegrations.$inferSelect;
export type InsertSlackIntegration = z.infer<typeof insertSlackIntegrationSchema>;
export type SlackEventPreference = typeof slackEventPreferences.$inferSelect;
export type InsertSlackEventPreference = z.infer<typeof insertSlackEventPreferenceSchema>;
export type AgencySlackIntegration = typeof agencySlackIntegrations.$inferSelect;
export type InsertAgencySlackIntegration = z.infer<typeof insertAgencySlackIntegrationSchema>;
export type AgencySlackEventPreference = typeof agencySlackEventPreferences.$inferSelect;
export type InsertAgencySlackEventPreference = z.infer<
  typeof insertAgencySlackEventPreferenceSchema
>;
export type SocialAccount = typeof socialAccounts.$inferSelect;
export type InsertSocialAccount = z.infer<typeof insertSocialAccountSchema>;
export type SocialAutomationRule = typeof socialAutomationRules.$inferSelect;
export type InsertSocialAutomationRule = z.infer<typeof insertSocialAutomationRuleSchema>;
export type SocialAutomationEvent = typeof socialAutomationEvents.$inferSelect;
export type SocialAutomationDelivery = typeof socialAutomationDeliveries.$inferSelect;
export type GhlIntegration = typeof ghlIntegrations.$inferSelect;
export type InsertGhlIntegration = z.infer<typeof insertGhlIntegrationSchema>;
export type GoogleAdsIntegration = typeof googleAdsIntegrations.$inferSelect;
export type InsertGoogleAdsIntegration = z.infer<typeof insertGoogleAdsIntegrationSchema>;
export type YoutubeIntegration = typeof youtubeIntegrations.$inferSelect;
export type InsertYoutubeIntegration = z.infer<typeof insertYoutubeIntegrationSchema>;
export type TiktokIntegration = typeof tiktokIntegrations.$inferSelect;
export type InsertTiktokIntegration = z.infer<typeof insertTiktokIntegrationSchema>;
export type LinkedinIntegration = typeof linkedinIntegrations.$inferSelect;
export type InsertLinkedinIntegration = z.infer<typeof insertLinkedinIntegrationSchema>;
export type AllegroIntegration = typeof allegroIntegrations.$inferSelect;
export type InsertAllegroIntegration = z.infer<typeof insertAllegroIntegrationSchema>;
export type GoogleMerchantIntegration = typeof googleMerchantIntegrations.$inferSelect;
export type InsertGoogleMerchantIntegration = z.infer<typeof insertGoogleMerchantIntegrationSchema>;
export type BaseLinkerIntegration = typeof baseLinkerIntegrations.$inferSelect;
export type InsertBaseLinkerIntegration = z.infer<typeof insertBaseLinkerIntegrationSchema>;
export type ShopifyIntegration = typeof shopifyIntegrations.$inferSelect;
export type InsertShopifyIntegration = z.infer<typeof insertShopifyIntegrationSchema>;
export type EcommerceCost = typeof ecommerceCosts.$inferSelect;
export type InsertEcommerceCost = z.infer<typeof insertEcommerceCostSchema>;
export type EcommerceCostType =
  | "cogs_pct"
  | "cogs_fixed"
  | "shipping_pct"
  | "shipping_fixed"
  | "platform_fee_pct"
  | "monthly_fixed"
  | "one_time";
export type AnalyticsSettings = typeof analyticsSettings.$inferSelect;
export type InsertAnalyticsSettings = z.infer<typeof insertAnalyticsSettingsSchema>;
export type CalendlyIntegration = typeof calendlyIntegrations.$inferSelect;
export type InsertCalendlyIntegration = z.infer<typeof insertCalendlyIntegrationSchema>;
export type Agency = typeof agencies.$inferSelect;
export type UserRole = "client" | "employee" | "manager" | "agency_owner" | "super_admin";
export type AgencyPermission =
  | "agency_panel"
  | "manage_clients"
  | "manage_members"
  | "assign_ad_accounts"
  | "add_tokens"
  | "impersonate";
export const AGENCY_PERMISSIONS: { value: AgencyPermission; label: string }[] = [
  { value: "agency_panel", label: "Panel agencji" },
  { value: "manage_clients", label: "Zarządzanie klientami" },
  { value: "manage_members", label: "Zarządzanie członkami" },
  { value: "assign_ad_accounts", label: "Przypisywanie kont reklamowych" },
  { value: "add_tokens", label: "Dodawanie tokenów" },
  { value: "impersonate", label: "Logowanie jako użytkownik" },
];

// Per-ad-account entry with optional Meta asset overrides
export interface AdAccountEntry {
  id: string;
  name: string;
  platform: "meta" | "google" | "tiktok";
  facebookPageId?: string; // primary page (legacy + used for CAPI/posting)
  facebookPageName?: string;
  // Additional Facebook Pages on the SAME ad account whose Instant Forms should
  // ALSO be synced (e.g. company page + a personal page). Lead sync pulls from
  // facebookPageId + all of these.
  facebookPages?: { id: string; name: string }[];
  instagramActorId?: string;
  instagramActorName?: string;
  pixelId?: string;
  pixelName?: string;
}

// Agency clients – represents a client company/brand managed by the agency
export const agencyClients = pgTable("agency_clients", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  agencyId: varchar("agency_id").notNull(),
  name: text("name").notNull(),
  // User account linked to this client (for impersonation / login)
  userId: varchar("user_id"),
  // Business model determines which KPIs are shown
  businessType: varchar("business_type", { length: 30 }).notNull().default("lead_gen"), // lead_gen | vsl_funnel | ecommerce | saas | autowebinar_book_a_call | autowebinar_mid_ticket
  // Ad accounts assigned to this client [{id, name, platform, facebookPageId?, ...}]
  adAccounts: jsonb("ad_accounts").$type<AdAccountEntry[]>().default([]),
  // Default Facebook Page for this client's campaigns (legacy — per-account overrides preferred)
  facebookPageId: varchar("facebook_page_id"),
  facebookPageName: text("facebook_page_name"),
  // Default Instagram actor for this client's campaigns (legacy — per-account overrides preferred)
  instagramActorId: varchar("instagram_actor_id"),
  instagramActorName: text("instagram_actor_name"),
  // Default Meta Pixel for this client's campaigns (legacy — per-account overrides preferred)
  pixelId: varchar("pixel_id"),
  pixelName: text("pixel_name"),
  // Which employee/manager is responsible for this client
  assignedUserId: varchar("assigned_user_id"),
  // Linked offer sheet for this client (used in campaign creation & AI form generation)
  offerSheetId: varchar("offer_sheet_id"),
  notes: text("notes"),
  // KPI targets – differ based on businessType. Shape + Zod validation live in
  // packages/shared/kpi-settings.ts (single source of truth shared with the
  // Slack digest builder + PATCH endpoint + frontend panel).
  kpiSettings: jsonb("kpi_settings").$type<KpiSettings | null>().default(null),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Many-to-many employee/client assignments. `clientId = '*'` is reserved for
// future "all clients" assignments; v1 UI writes concrete client ids.
export const agencyClientMembers = pgTable(
  "agency_client_members",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    agencyId: varchar("agency_id").notNull(),
    clientId: varchar("client_id").notNull(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    createdByUserId: varchar("created_by_user_id"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    uniqueAgencyClientUser: uniqueIndex("agency_client_members_unique_idx").on(
      t.agencyId,
      t.clientId,
      t.userId
    ),
  })
);

export const agencyInvitations = pgTable("agency_invitations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  agencyId: varchar("agency_id").notNull(),
  email: text("email").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  token: varchar("token", { length: 128 }).notNull().unique(),
  rolePreset: varchar("role_preset", { length: 40 }).notNull().default("assistant"),
  permissionMode: varchar("permission_mode", { length: 20 }).notNull().default("preset"),
  permissions: jsonb("permissions").$type<string[] | null>().default(null),
  clientIds: jsonb("client_ids").$type<string[]>().notNull().default([]),
  invitedByUserId: varchar("invited_by_user_id").notNull(),
  acceptedByUserId: varchar("accepted_by_user_id"),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  expiresAt: timestamp("expires_at").notNull(),
  acceptedAt: timestamp("accepted_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const permissionAuditEvents = pgTable("permission_audit_events", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  agencyId: varchar("agency_id").notNull(),
  actorUserId: varchar("actor_user_id").notNull(),
  targetUserId: varchar("target_user_id"),
  action: varchar("action", { length: 80 }).notNull(),
  before: jsonb("before").$type<Record<string, unknown> | null>().default(null),
  after: jsonb("after").$type<Record<string, unknown> | null>().default(null),
  createdAt: timestamp("created_at").defaultNow(),
});

export type AgencyClientMember = typeof agencyClientMembers.$inferSelect;
export type InsertAgencyClientMember = typeof agencyClientMembers.$inferInsert;
export type AgencyInvitation = typeof agencyInvitations.$inferSelect;
export type InsertAgencyInvitation = typeof agencyInvitations.$inferInsert;
export type PermissionAuditEvent = typeof permissionAuditEvents.$inferSelect;
export type InsertPermissionAuditEvent = typeof permissionAuditEvents.$inferInsert;

export const insertAgencyClientSchema = createInsertSchema(agencyClients).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type AgencyClient = typeof agencyClients.$inferSelect;
export type InsertAgencyClient = z.infer<typeof insertAgencyClientSchema>;

// ─── Applied Action Log (tracks budget changes, status changes applied via recommendations) ───
export const metaApplyActionLog = pgTable("meta_apply_action_log", {
  id: varchar("id")
    .primaryKey()
    .$defaultFn(() => crypto.randomUUID()),
  userId: varchar("user_id").notNull(),
  action: varchar("action", { length: 50 }).notNull(), // 'update_budget' | 'update_status'
  entityType: varchar("entity_type", { length: 20 }).notNull(), // 'campaign' | 'adset'
  entityId: varchar("entity_id", { length: 100 }).notNull(),
  entityName: text("entity_name"),
  previousValue: text("previous_value"), // e.g. "100" (budget) or "ACTIVE" (status)
  newValue: text("new_value"), // e.g. "120" (budget) or "PAUSED" (status)
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
});

// ──────────────────────────────────────────────────────────────────────────────
// ─── HYROS-style Tracking System ─────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────

// Visitors table — one row per unique first-party visitor
export const trackingVisitors = pgTable(
  "tracking_visitors",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    trackingId: varchar("tracking_id").notNull().unique(), // persistent first-party ID (cookie/localStorage)
    ownerUserId: varchar("owner_user_id"), // FK to users.id — which GawronifyOS user owns this visitor's data
    // Identity fields (filled via identity stitching)
    email: text("email"),
    phone: text("phone"),
    name: text("name"),
    // First touch attribution (immutable once set)
    firstUtmSource: text("first_utm_source"),
    firstUtmMedium: text("first_utm_medium"),
    firstUtmCampaign: text("first_utm_campaign"),
    firstUtmContent: text("first_utm_content"),
    firstUtmTerm: text("first_utm_term"),
    firstFbclid: text("first_fbclid"),
    firstGclid: text("first_gclid"),
    firstTtclid: text("first_ttclid"),
    firstMsclkid: text("first_msclkid"),
    firstGbraid: text("first_gbraid"),
    firstWbraid: text("first_wbraid"),
    firstReferrer: text("first_referrer"),
    firstLandingPage: text("first_landing_page"),
    firstSeenAt: timestamp("first_seen_at").defaultNow(),
    // Last touch (updated on every new session)
    lastUtmSource: text("last_utm_source"),
    lastUtmMedium: text("last_utm_medium"),
    lastUtmCampaign: text("last_utm_campaign"),
    lastUtmContent: text("last_utm_content"),
    lastUtmTerm: text("last_utm_term"),
    lastFbclid: text("last_fbclid"),
    lastGclid: text("last_gclid"),
    lastReferrer: text("last_referrer"),
    lastLandingPage: text("last_landing_page"),
    lastSeenAt: timestamp("last_seen_at").defaultNow(),
    // Linked GHL contact ID (for CRM mapping)
    ghlContactId: text("ghl_contact_id"),
    // Consent
    consentGiven: boolean("consent_given").default(false),
    consentTimestamp: timestamp("consent_timestamp"),
    // Meta
    userAgent: text("user_agent"),
    ip: text("ip"),
    // Meta CAPI match-quality signals — captured from browser-pixel cookies on
    // every page view. Sending these alongside hashed PII raises Event Match
    // Quality dramatically; without them Meta falls back to IP+UA matching only.
    // `fbp` is the _fbp first-party cookie (browser fingerprint), `fbc` is the
    // _fbc cookie (last click identifier) — server should prefer cookie-fbc over
    // a synthesized one when both are present.
    fbp: text("fbp"),
    fbc: text("fbc"),
    // Optional geographic signals — Meta hashes these for additional matching
    // when present. Filled from CRM/checkout payloads (Shopify/GHL) when known.
    city: text("city"),
    region: text("region"),
    zip: text("zip"),
    country: varchar("country", { length: 2 }),
    mergedIntoVisitorId: varchar("merged_into_visitor_id"), // if merged with another visitor
    // Hyros-style parent lead linking — a child lead (e.g. user signed up with second email)
    // points to the canonical "parent" visitor whose history is the source of truth.
    // Also used for cross-device/session stitching.
    parentVisitorId: varchar("parent_visitor_id"),
    parentLinkedAt: timestamp("parent_linked_at"),
    parentLinkedByUserId: varchar("parent_linked_by_user_id"),
    parentLinkReason: text("parent_link_reason"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    ownerEmailIdx: index("tracking_visitors_owner_email_idx").on(t.ownerUserId, t.email),
    ownerPhoneIdx: index("tracking_visitors_owner_phone_idx").on(t.ownerUserId, t.phone),
    ownerLastSeenIdx: index("tracking_visitors_owner_last_seen_idx").on(
      t.ownerUserId,
      t.lastSeenAt
    ),
    ownerParentIdx: index("tracking_visitors_owner_parent_idx").on(
      t.ownerUserId,
      t.parentVisitorId
    ),
    ghlContactIdx: index("tracking_visitors_ghl_contact_idx").on(t.ghlContactId),
    // VP-180: indeks częściowy pod hourly visitor-merge. selectDistinct(owner) oraz
    // per-owner select filtrują `merged_into_visitor_id IS NULL AND (email OR phone)`,
    // czego żaden indeks nie pokrywał → pełny seq scan tabeli gości co 60 min.
    mergeCandidatesIdx: index("tracking_visitors_merge_candidates_idx")
      .on(t.ownerUserId)
      .where(sql`merged_into_visitor_id IS NULL AND (email IS NOT NULL OR phone IS NOT NULL)`),
  })
);

// Sessions table — one row per visit session
export const trackingSessions = pgTable(
  "tracking_sessions",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull().unique(), // client-generated session ID
    visitorId: varchar("visitor_id").notNull(), // FK to tracking_visitors.id
    trackingId: varchar("tracking_id").notNull(), // denormalized for fast lookups
    // Attribution data for THIS session
    utmSource: text("utm_source"),
    utmMedium: text("utm_medium"),
    utmCampaign: text("utm_campaign"),
    utmContent: text("utm_content"),
    utmTerm: text("utm_term"),
    fbclid: text("fbclid"),
    gclid: text("gclid"),
    ttclid: text("ttclid"),
    msclkid: text("msclkid"),
    gbraid: text("gbraid"),
    wbraid: text("wbraid"),
    referrer: text("referrer"),
    landingPage: text("landing_page"),
    userAgent: text("user_agent"),
    ip: text("ip"),
    startedAt: timestamp("started_at").defaultNow(),
    lastActivityAt: timestamp("last_activity_at").defaultNow(),
  },
  (t) => ({
    visitorStartedIdx: index("tracking_sessions_visitor_started_idx").on(t.visitorId, t.startedAt),
    trackingStartedIdx: index("tracking_sessions_tracking_started_idx").on(
      t.trackingId,
      t.startedAt
    ),
  })
);

// Events table — raw funnel events
// Inbound GHL webhook log — records EVERY hit (success AND failure) so the
// setup panel can show "what actually arrived from GHL" with status + reason.
// Separate from tracking_events on purpose: failed/invalid hits must NOT create
// visitors/conversions (would pollute data), but the user still needs to SEE them
// to debug their GHL workflow ("wyslalem test z GHL -> czy doszedl, czy 400?").
export const ghlWebhookEvents = pgTable(
  "ghl_webhook_events",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    ownerUserId: varchar("owner_user_id").notNull(),
    receivedAt: timestamp("received_at").defaultNow(),
    status: integer("status").notNull(), // HTTP status we returned (200 / 400 / 401)
    ok: boolean("ok").notNull(), // true = imported into tracking, false = rejected
    ghlEvent: varchar("ghl_event", { length: 80 }), // raw "event" field from the body
    eventName: varchar("event_name", { length: 50 }), // mapped internal event (when ok)
    email: varchar("email"),
    phone: varchar("phone"),
    revenue: varchar("revenue"),
    error: text("error"), // rejection reason when !ok
    isTest: boolean("is_test").default(false),
  },
  (table) => ({
    ownerReceivedIdx: index("ghl_webhook_events_owner_received_idx").on(
      table.ownerUserId,
      table.receivedAt
    ),
  })
);

export const trackingEvents = pgTable(
  "tracking_events",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    eventId: varchar("event_id").notNull().unique(), // client-generated dedup key
    eventName: varchar("event_name", { length: 50 }).notNull(), // page_view, lead, booked_call, etc.
    trackingId: varchar("tracking_id").notNull(),
    sessionId: varchar("session_id"), // null for server-side webhook events
    visitorId: varchar("visitor_id"), // filled on ingest
    // Page context
    pageUrl: text("page_url"),
    referrer: text("referrer"),
    // Attribution snapshot at event time
    utmSource: text("utm_source"),
    utmMedium: text("utm_medium"),
    utmCampaign: text("utm_campaign"),
    utmContent: text("utm_content"),
    utmTerm: text("utm_term"),
    fbclid: text("fbclid"),
    gclid: text("gclid"),
    ttclid: text("ttclid"),
    msclkid: text("msclkid"),
    gbraid: text("gbraid"),
    wbraid: text("wbraid"),
    // Identity (may be unknown at event time)
    email: text("email"),
    phone: text("phone"),
    // Transaction data
    orderId: text("order_id"),
    revenue: numeric("revenue"), // stored as string for precision
    currency: varchar("currency", { length: 10 }),
    // Extra
    metadata: jsonb("metadata").$type<Record<string, any>>(),
    ip: text("ip"),
    userAgent: text("user_agent"),
    // Timestamps
    clientTimestamp: timestamp("client_timestamp"), // from frontend
    serverTimestamp: timestamp("server_timestamp").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    visitorTimeIdx: index("tracking_events_visitor_time_idx").on(t.visitorId, t.serverTimestamp),
    trackingTimeIdx: index("tracking_events_tracking_time_idx").on(t.trackingId, t.serverTimestamp),
    nameTimeIdx: index("tracking_events_name_time_idx").on(t.eventName, t.serverTimestamp),
    sessionIdx: index("tracking_events_session_idx").on(t.sessionId),
    orderIdx: index("tracking_events_order_idx").on(t.orderId),
    visitorNameIdx: index("tracking_events_visitor_name_idx").on(t.visitorId, t.eventName),
  })
);

// Identities table — links tracking_id to known identifiers
export const trackingIdentities = pgTable(
  "tracking_identities",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    visitorId: varchar("visitor_id").notNull(), // FK to tracking_visitors.id
    trackingId: varchar("tracking_id").notNull(),
    identityType: varchar("identity_type", { length: 30 }).notNull(), // email, phone, order_id, fbclid, gclid, ghl_contact_id
    identityValue: text("identity_value").notNull(),
    firstSeenAt: timestamp("first_seen_at").defaultNow(),
    lastSeenAt: timestamp("last_seen_at").defaultNow(),
  },
  (t) => ({
    typeValueIdx: index("tracking_identities_type_value_idx").on(t.identityType, t.identityValue),
    visitorIdx: index("tracking_identities_visitor_idx").on(t.visitorId),
  })
);

// Conversions table — key funnel moments with full attribution
export const trackingConversions = pgTable(
  "tracking_conversions",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    visitorId: varchar("visitor_id").notNull(),
    trackingId: varchar("tracking_id").notNull(),
    eventId: varchar("event_id").notNull(), // FK to tracking_events.event_id
    conversionType: varchar("conversion_type", { length: 50 }).notNull(), // lead, booked_call, qualified_call, purchase, upsell, refund
    // Identity at conversion time
    email: text("email"),
    phone: text("phone"),
    name: text("name"),
    // Transaction
    orderId: text("order_id"),
    revenue: numeric("revenue"),
    currency: varchar("currency", { length: 10 }),
    // First touch attribution
    firstUtmSource: text("first_utm_source"),
    firstUtmMedium: text("first_utm_medium"),
    firstUtmCampaign: text("first_utm_campaign"),
    firstUtmContent: text("first_utm_content"),
    firstUtmTerm: text("first_utm_term"),
    firstClickId: text("first_click_id"), // fbclid/gclid/etc from first touch
    firstClickIdType: varchar("first_click_id_type", { length: 20 }), // 'fbclid' | 'gclid' | etc
    firstReferrer: text("first_referrer"),
    firstLandingPage: text("first_landing_page"),
    firstSeenAt: timestamp("first_seen_at"),
    // Last touch attribution (before this conversion)
    lastUtmSource: text("last_utm_source"),
    lastUtmMedium: text("last_utm_medium"),
    lastUtmCampaign: text("last_utm_campaign"),
    lastUtmContent: text("last_utm_content"),
    lastUtmTerm: text("last_utm_term"),
    lastClickId: text("last_click_id"),
    lastClickIdType: varchar("last_click_id_type", { length: 20 }),
    lastReferrer: text("last_referrer"),
    lastLandingPage: text("last_landing_page"),
    // Last non-direct attribution
    lastNonDirectUtmSource: text("last_non_direct_utm_source"),
    lastNonDirectUtmMedium: text("last_non_direct_utm_medium"),
    lastNonDirectUtmCampaign: text("last_non_direct_utm_campaign"),
    // Metadata
    ghlContactId: text("ghl_contact_id"),
    metadata: jsonb("metadata").$type<Record<string, any>>(),
    // Source-side status (e.g. GHL appointment status: confirmed/cancelled/showed/noshow/invalid).
    // Null for non-appointment conversions (leads/purchases). Mapped to qualified/unqualified in UI.
    status: varchar("status", { length: 32 }),
    // When the record was created at the SOURCE (e.g. GHL appointment dateAdded = when the
    // call was booked) — distinct from convertedAt (= meeting startTime). Null when unknown.
    sourceCreatedAt: timestamp("source_created_at"),
    convertedAt: timestamp("converted_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    visitorTypeIdx: index("tracking_conversions_visitor_type_idx").on(
      t.visitorId,
      t.conversionType
    ),
    trackingTypeIdx: index("tracking_conversions_tracking_type_idx").on(
      t.trackingId,
      t.conversionType
    ),
    orderIdx: index("tracking_conversions_order_idx").on(t.orderId),
    ghlContactIdx: index("tracking_conversions_ghl_contact_idx").on(t.ghlContactId),
    convertedAtIdx: index("tracking_conversions_converted_at_idx").on(t.convertedAt),
    // VP-138: UNIQUE — idempotencja ingestu (jeden event = max jedna konwersja). Dedup po TOŻSAMOŚCI
    // kontaktu (cross-source) robi warstwa agregacji (conversion-dedup), patrz doc §7.3 opcja A.
    // ⚠️ Apply wymaga wcześniejszej deduplikacji ewentualnych duplikatów: scripts/migrate-data-dedup-conversion-events.ts
    eventIdx: uniqueIndex("tracking_conversions_event_idx").on(t.eventId),
    statusIdx: index("tracking_conversions_status_idx").on(t.status),
    sourceCreatedAtIdx: index("tracking_conversions_source_created_at_idx").on(t.sourceCreatedAt),
    // VP-140: idempotencja materializacji rozmów GHL — jeden appointment = max jedna konwersja
    // booked_call dla danego visitora. Bez tego puller dokładał duplikat na każdym ticku crona
    // (ten sam ghl_appointment_id setki razy → over-count rozmów w Sales Data). Klucz po visitorze,
    // nie globalnie, na wypadek tej samej lokalizacji GHL podpiętej u dwóch właścicieli.
    // ⚠️ Apply wymaga wcześniejszej deduplikacji: scripts/migrate-data-dedup-booked-calls.ts
    ghlApptIdx: uniqueIndex("tracking_conversions_ghl_appt_idx")
      .on(t.visitorId, sql`(${t.metadata} ->> 'ghl_appointment_id')`)
      .where(
        sql`${t.conversionType} = 'booked_call' AND ${t.metadata} ->> 'ghl_appointment_id' IS NOT NULL`
      ),
    // NOTE: a functional index on COALESCE(NULLIF(metadata->>'call_date','')::timestamp, converted_at)
    // was intended here for Sales Data meeting-date sort/filter, but BOTH text->timestamp and
    // text->timestamptz casts are STABLE (depend on DateStyle/TimeZone), so Postgres rejects them in
    // an index expression (42P17). Deferred — needs an IMMUTABLE wrapper function or a denormalized
    // timestamp column. meetingDateExpr in routes/tracking.ts still works, just unindexed for now.
  })
);

// UTM → Meta ID overrides. When a non-numeric utm_campaign (e.g. "community-ads") needs
// to be mapped to a real Meta campaign / adset / ad, the user creates a row here.
// Applied during normalizeUtmToMetaIds in ghl-service.ts.
export const utmAttributionOverrides = pgTable(
  "utm_attribution_overrides",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    ownerUserId: varchar("owner_user_id").notNull(), // FK users.id (or agency client owner)
    // Match key: which UTM dimension + value triggers the override (case-insensitive match)
    matchField: text("match_field").notNull(), // 'utm_campaign' | 'utm_source' | 'utm_medium' | 'utm_content' | 'utm_term'
    matchValue: text("match_value").notNull(), // e.g. "community-ads", "opt-inn"
    // Target Meta IDs (at least one must be set)
    metaCampaignId: text("meta_campaign_id"),
    metaAdSetId: text("meta_adset_id"),
    metaAdId: text("meta_ad_id"),
    // Audit
    notes: text("notes"),
    createdByUserId: varchar("created_by_user_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    ownerLookupIdx: index("utm_overrides_owner_lookup_idx").on(
      t.ownerUserId,
      t.matchField,
      t.matchValue
    ),
  })
);
export type UtmAttributionOverride = typeof utmAttributionOverrides.$inferSelect;
export type InsertUtmAttributionOverride = typeof utmAttributionOverrides.$inferInsert;

// Attribution snapshots — historical record of attribution state per session
export const trackingAttributionSnapshots = pgTable("tracking_attribution_snapshots", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  visitorId: varchar("visitor_id").notNull(),
  sessionId: varchar("session_id").notNull(),
  // Channel info
  channel: varchar("channel", { length: 50 }), // paid_social, paid_search, organic, direct, referral, email
  source: text("source"),
  medium: text("medium"),
  campaign: text("campaign"),
  clickId: text("click_id"),
  clickIdType: varchar("click_id_type", { length: 20 }),
  referrer: text("referrer"),
  landingPage: text("landing_page"),
  isDirect: boolean("is_direct").default(false),
  capturedAt: timestamp("captured_at").defaultNow(),
});

// Tracking configuration per user (consent, retention settings)
export const trackingConfig = pgTable("tracking_config", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(), // FK to users.id — one config per user
  // Consent settings
  requireConsent: boolean("require_consent").default(false), // if true, only track after consent
  consentMode: varchar("consent_mode", { length: 20 }).default("all"), // all | essential_only | none
  // Retention
  eventRetentionDays: integer("event_retention_days").default(365),
  visitorRetentionDays: integer("visitor_retention_days").default(730),
  // Cookie settings
  cookieDomain: text("cookie_domain"), // e.g. '.example.com'
  cookieMaxAgeDays: integer("cookie_max_age_days").default(365),
  // Custom tracking domain (subdomain-based serving for ad blocker resistance)
  trackingDomain: text("tracking_domain"), // e.g. 't.clientdomain.com' — CNAME to t.viral.partners
  useCustomDomain: boolean("use_custom_domain").default(false), // false = use default t.gawronify.com
  // Tracking script config
  trackPageViews: boolean("track_page_views").default(true),
  trackScrollDepth: boolean("track_scroll_depth").default(false),
  // Active
  enabled: boolean("enabled").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Tag rules — HYROS-style URL/event rules that auto-fire tags
// e.g. "if URL contains /thank-you → fire !lead", "if URL contains /checkout-complete → fire !sale"
// User-defined conversion tags. Independent from `tracking_tag_rules` (which
// fire ad-hoc tracking events based on URL patterns) — these are labels the
// user attaches to conversions for filtering/segmentation in CAPI, reports,
// dashboards, etc. Examples: "qualified", "junk", "hot", "cold".
export const conversionTags = pgTable(
  "conversion_tags",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    name: varchar("name", { length: 50 }).notNull(),
    // Slug is the lowercase canonical form used for matching in filters
    // (`metadata.tags` array entries). Stable even when the user renames the tag.
    slug: varchar("slug", { length: 50 }).notNull(),
    // Kind drives default UI affordances (colors, default behavior) without
    // dictating filter semantics — the user wires that explicitly via the CAPI
    // requiredTags / excludedTags choice.
    kind: varchar("kind", { length: 20 }).notNull().default("neutral"), // 'qualified' | 'disqualified' | 'neutral'
    color: varchar("color", { length: 20 }).default("#6b7280"), // hex; UI badge color
    description: text("description"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    // One slug per user — prevents two tags resolving to the same filter token.
    userSlugIdx: uniqueIndex("conversion_tags_user_slug_idx").on(t.userId, t.slug),
  })
);

// Mapping rule: when a conversion arrives from an integration carrying a
// known signal (GHL pipeline stage, GHL tag, Shopify tag, …), automatically
// attach the user's conversion tag. The webhook handlers consult these
// mappings before persisting the conversion, so by the time CAPI filters
// run the tag is already in `metadata.tags`.
export const conversionTagMappings = pgTable(
  "conversion_tag_mappings",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    tagId: varchar("tag_id").notNull(), // FK to conversion_tags.id
    source: varchar("source", { length: 20 }).notNull(), // 'ghl' | 'shopify' | 'calendly' | 'everwebinar' | 'custom'
    // Which field on the inbound payload to match against. For GHL we mostly
    // use 'pipeline_stage' (Customer / Show / NoShow) and 'tag' (admin-set
    // tags in the CRM). Extending to other sources reuses the same table.
    matchType: varchar("match_type", { length: 30 }).notNull(), // 'pipeline_stage' | 'tag' | 'event' | 'custom_field'
    // The literal value we expect to see (case-insensitive match performed in
    // application code). For matchType='custom_field', `customField` carries
    // the field name and matchValue carries the expected value.
    matchValue: text("match_value").notNull(),
    customField: varchar("custom_field", { length: 100 }),
    enabled: boolean("enabled").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    userSourceIdx: index("conversion_tag_mappings_user_source_idx").on(
      t.userId,
      t.source,
      t.enabled
    ),
  })
);

export const insertConversionTagSchema = createInsertSchema(conversionTags).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertConversionTagMappingSchema = createInsertSchema(conversionTagMappings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type ConversionTag = typeof conversionTags.$inferSelect;
export type InsertConversionTag = z.infer<typeof insertConversionTagSchema>;
export type ConversionTagMapping = typeof conversionTagMappings.$inferSelect;
export type InsertConversionTagMapping = z.infer<typeof insertConversionTagMappingSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Mini-CRM (Sales Data) — warstwa CRM na OSOBIE (trackingVisitors), nie na konwersji.
// Etapy pipeline'u w shared/crm-stage.ts (7 stalych: new/contacted/booked/showed/
// proposal/won/lost). Scoped per ownerUserId jak reszta trackingu.
// ─────────────────────────────────────────────────────────────────────────────

// Stan CRM per lead-osoba. Jeden rekord na (ownerUserId, visitorId).
export const crmLeads = pgTable(
  "crm_leads",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    ownerUserId: varchar("owner_user_id").notNull(), // FK users.id (lub agency client owner)
    visitorId: varchar("visitor_id").notNull(), // FK tracking_visitors.id — osoba/lead
    trackingId: varchar("tracking_id").notNull(), // denorm first-party id
    // Etap pipeline'u (CrmStage z shared/crm-stage.ts). Domyslnie 'new'.
    stage: varchar("stage", { length: 20 }).notNull().default("new"),
    // true gdy etap ustawiono recznie — wtedy auto-seed z konwersji juz nie nadpisuje.
    stageManual: boolean("stage_manual").notNull().default(false),
    // Reczna kwalifikacja leada przez handlowca: 'qualified' | 'unqualified' | null (nieustawione).
    // Niezalezne od auto-statusu GHL (qualificationOf w lead-status.ts).
    qualification: varchar("qualification", { length: 20 }),
    // Wartosc sprzedazy — wypelniana dopiero przy etapie 'won' (faza 2). Null w MVP.
    pipelineValue: numeric("pipeline_value"),
    lastActivityAt: timestamp("last_activity_at"),
    nextFollowUpAt: timestamp("next_follow_up_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    // Jeden rekord CRM na osobe u danego wlasciciela (idempotencja sync/seed).
    ownerVisitorIdx: uniqueIndex("crm_leads_owner_visitor_idx").on(t.ownerUserId, t.visitorId),
    // Query pod kanban: leady danego wlasciciela pogrupowane po etapie.
    ownerStageIdx: index("crm_leads_owner_stage_idx").on(t.ownerUserId, t.stage),
  })
);

// Log aktywnosci / notatek per lead. W MVP zapisujemy tylko stage_change (audit);
// notatki/call/email z UI to faza 2.
export const crmActivities = pgTable(
  "crm_activities",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    ownerUserId: varchar("owner_user_id").notNull(),
    leadId: varchar("lead_id").notNull(), // FK crm_leads.id
    type: varchar("type", { length: 20 }).notNull(), // note | call | email | stage_change | follow_up
    content: text("content"),
    metadata: jsonb("metadata").$type<Record<string, any>>(),
    actorUserId: varchar("actor_user_id"), // kto wykonal (null = system/auto)
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    leadCreatedIdx: index("crm_activities_lead_created_idx").on(t.leadId, t.createdAt),
  })
);

// Ustawienia CRM per wlasciciel — wybor przy pierwszym uruchomieniu funkcji.
// Brak rekordu = CRM jeszcze nie zainicjowany (UI pokazuje ekran wyboru).
export const crmSettings = pgTable("crm_settings", {
  ownerUserId: varchar("owner_user_id").primaryKey(), // jeden wiersz na uzytkownika
  // Tryb seedu istniejacych leadow:
  //   all_new  — wszystkie obecne leady na etap defaultStage (domyslnie 'new')
  //   all_auto — wszystkie obecne leady na etap auto-mapowany z konwersji
  //   only_new — nie seeduj istniejacych; do CRM wpadaja tylko leady utworzone po initializedAt
  seedMode: varchar("seed_mode", { length: 20 }).notNull(),
  defaultStage: varchar("default_stage", { length: 20 }).notNull().default("new"),
  // Cutoff dla trybu only_new — leady z created_at >= initializedAt sa pobierane.
  initializedAt: timestamp("initialized_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCrmLeadSchema = createInsertSchema(crmLeads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCrmActivitySchema = createInsertSchema(crmActivities).omit({
  id: true,
  createdAt: true,
});
export type CrmLead = typeof crmLeads.$inferSelect;
export type InsertCrmLead = z.infer<typeof insertCrmLeadSchema>;
export type CrmActivity = typeof crmActivities.$inferSelect;
export type InsertCrmActivity = z.infer<typeof insertCrmActivitySchema>;
export type CrmSettings = typeof crmSettings.$inferSelect;
export type CrmSeedMode = "all_new" | "all_auto" | "only_new";

export const trackingTagRules = pgTable("tracking_tag_rules", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // FK to users.id — rules per user
  tag: varchar("tag", { length: 50 }).notNull(), // e.g. "!clicked", "!lead", "!call", "!sale"
  triggerType: varchar("trigger_type", { length: 30 }).notNull(), // "url_contains", "url_exact", "url_regex", "form_submit", "integration"
  triggerValue: text("trigger_value"), // URL pattern, regex, integration name — null for "all pages" rules
  eventName: varchar("event_name", { length: 50 }).notNull(), // tracking event to fire: page_view, lead, booked_call, purchase, etc.
  autoIdentify: boolean("auto_identify").default(true), // auto-extract email/phone from forms
  revenue: numeric("revenue"), // fixed revenue for !sale rules
  currency: varchar("currency", { length: 10 }).default("PLN"),
  enabled: boolean("enabled").default(true),
  priority: integer("priority").default(0), // higher = matched first
  description: text("description"), // human-readable note
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Zod insert schemas
export const insertTrackingVisitorSchema = createInsertSchema(trackingVisitors).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertTrackingSessionSchema = createInsertSchema(trackingSessions).omit({ id: true });
export const insertTrackingEventSchema = createInsertSchema(trackingEvents).omit({
  id: true,
  createdAt: true,
});
export const insertTrackingIdentitySchema = createInsertSchema(trackingIdentities).omit({
  id: true,
});
export const insertTrackingConversionSchema = createInsertSchema(trackingConversions).omit({
  id: true,
  createdAt: true,
});
export const insertTrackingAttributionSnapshotSchema = createInsertSchema(
  trackingAttributionSnapshots
).omit({ id: true });
export const insertTrackingConfigSchema = createInsertSchema(trackingConfig).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertTrackingTagRuleSchema = createInsertSchema(trackingTagRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type TrackingVisitor = typeof trackingVisitors.$inferSelect;
export type InsertTrackingVisitor = z.infer<typeof insertTrackingVisitorSchema>;
export type TrackingSession = typeof trackingSessions.$inferSelect;
export type InsertTrackingSession = z.infer<typeof insertTrackingSessionSchema>;
export type TrackingEvent = typeof trackingEvents.$inferSelect;
export type InsertTrackingEvent = z.infer<typeof insertTrackingEventSchema>;
export type TrackingIdentity = typeof trackingIdentities.$inferSelect;
export type InsertTrackingIdentity = z.infer<typeof insertTrackingIdentitySchema>;
export type TrackingConversion = typeof trackingConversions.$inferSelect;
export type InsertTrackingConversion = z.infer<typeof insertTrackingConversionSchema>;
export type TrackingAttributionSnapshot = typeof trackingAttributionSnapshots.$inferSelect;
export type TrackingConfig = typeof trackingConfig.$inferSelect;
export type TrackingTagRule = typeof trackingTagRules.$inferSelect;
export type InsertTrackingTagRule = z.infer<typeof insertTrackingTagRuleSchema>;

// ─── Tracking Event Validation Schema (for API input) ───
export const trackingEventPayloadSchema = z.preprocess(
  // tracking.js sends explicit `null` for optional fields it did not collect
  // (e.g. phone:null on a lead form without a phone input). Zod .optional()
  // accepts undefined but rejects null — strip null-valued keys before validation.
  (val) =>
    val && typeof val === "object" && !Array.isArray(val)
      ? Object.fromEntries(
          Object.entries(val as Record<string, unknown>).filter(([, v]) => v !== null)
        )
      : val,
  z.object({
    event_name: z.enum([
      "page_view",
      "view_content",
      "lead",
      "booked_call",
      "qualified_call",
      "checkout_started",
      "purchase",
      "upsell",
      "refund",
    ]),
    event_id: z.string().min(1),
    tracking_id: z.string().min(1),
    user_id: z.string().optional(), // GawronifyOS user who owns the tracking snippet
    session_id: z.string().optional(), // optional for server-side webhook events
    timestamp: z.string().optional(),
    page_url: z.string().optional(),
    referrer: z.string().optional(),
    utm_source: z.string().optional(),
    utm_medium: z.string().optional(),
    utm_campaign: z.string().optional(),
    utm_content: z.string().optional(),
    utm_term: z.string().optional(),
    fbclid: z.string().optional(),
    gclid: z.string().optional(),
    ttclid: z.string().optional(),
    msclkid: z.string().optional(),
    gbraid: z.string().optional(),
    wbraid: z.string().optional(),
    email: z.string().email().optional().or(z.literal("")),
    phone: z.string().optional(),
    order_id: z.string().optional(),
    revenue: z.union([z.string(), z.number()]).optional(),
    currency: z.string().optional(),
    status: z.string().max(32).optional(),
    metadata: z.record(z.any()).optional(),
    user_agent: z.string().optional(),
    // Meta CAPI match-quality cookies forwarded from the browser pixel. The
    // backend persists these on the visitor so dispatch can include them in the
    // server event without resorting to IP+UA fallback matching.
    fbp: z.string().nullable().optional(),
    fbc: z.string().nullable().optional(),
  })
);
export type TrackingEventPayload = z.infer<typeof trackingEventPayloadSchema>;

// ─── CAPI (Conversions API) Server-Side Tracking ───

// Platforms supported for server-side conversion forwarding
export const capiPlatformEnum = z.enum(["meta", "google_ads", "tiktok"]);
export type CapiPlatform = z.infer<typeof capiPlatformEnum>;

// Event mappings — which internal conversion events get sent to which platforms as which event
export const capiEventMappings = pgTable("capi_event_mappings", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // FK to users.id
  platform: varchar("platform", { length: 30 }).notNull(), // 'meta' | 'google_ads' | 'tiktok'
  internalEvent: varchar("internal_event", { length: 50 }).notNull(), // e.g. 'lead', 'qualified_call', 'purchase'
  platformEvent: varchar("platform_event", { length: 100 }).notNull(), // e.g. 'Lead', 'Purchase', 'CompleteRegistration' (Meta), 'SUBMIT_LEAD_FORM' (Google)
  enabled: boolean("enabled").default(true),
  // Platform-specific config
  pixelId: varchar("pixel_id", { length: 100 }), // Meta: pixel/dataset ID; TikTok: pixel ID
  conversionActionId: varchar("conversion_action_id", { length: 100 }), // Google Ads: conversion action resource name
  customEventName: varchar("custom_event_name", { length: 100 }), // Override event name if different from platformEvent
  sendRevenue: boolean("send_revenue").default(true), // Whether to forward revenue data
  sendUserData: boolean("send_user_data").default(true), // Whether to send hashed PII (email, phone)
  deduplicationKey: varchar("deduplication_key", { length: 30 }).default("event_id"), // Which field to use for dedup: 'event_id' | 'order_id'
  // ─── Filters (Hyros-style Custom Conversion Rules) ───
  ignoreOrganic: boolean("ignore_organic").default(true), // Skip conversions from organic/direct sources
  onlyFirstConversion: boolean("only_first_conversion").default(false), // Only send first conversion per visitor (skip existing leads)
  requiredTags: jsonb("required_tags").$type<string[]>(), // Only send if conversion metadata has one of these tags
  excludedTags: jsonb("excluded_tags").$type<string[]>(), // Skip if conversion metadata has any of these tags
  minRevenue: numeric("min_revenue"), // Only send if revenue >= this value
  attributionSource: varchar("attribution_source", { length: 30 }).default("neuroflow"), // 'first_click' | 'last_click' | 'neuroflow'
  // NeuroFlow cutoff window — within this many days from first touch, credit
  // first click; beyond it, credit last click. Only consulted when
  // attributionSource = 'neuroflow'. NULL = use service default (30 days).
  neuroflowWindowDays: integer("neuroflow_window_days"),
  // ─── Hyros-parity filters ───
  // Only fire when the attributed click-id type matches the destination platform
  // (Meta ← fbclid, Google Ads ← gclid/gbraid/wbraid, TikTok ← ttclid). Prevents
  // sending a Google-attributed lead into Meta CAPI and vice-versa.
  matchPlatformAttribution: boolean("match_platform_attribution").default(false),
  // VP-240: filtry `ignoreOrganic` / `matchPlatformAttribution` pytały wyłącznie
  // touch wskazany przez model atrybucji. Gdy tam nie było click-id, konwersja
  // leciała do kosza mimo poprawnego fbclid na DRUGIM touchu — cicha utrata
  // konwersji przy każdym ruchu mieszanym (wejście organiczne → klik reklamy →
  // zakup, i odwrotnie). Z tą flagą filtry sprawdzają OBA touche i przepuszczają,
  // gdy którykolwiek niesie click-id pasujący do platformy. Model atrybucji dalej
  // decyduje, CZYJE dane wysyłamy i komu przypisujemy zasługę — zmienia się tylko
  // decyzja "czy w ogóle wysłać".
  anyTouchClickId: boolean("any_touch_click_id").default(true),
  // Drop the conversion if it happened more than N days after the visitor's
  // first seen timestamp. Mirrors Meta's 7-day click attribution window.
  attributionWindowDays: integer("attribution_window_days"),
  // Drop conversions whose email matches a test/junk pattern (built-in defaults
  // + per-mapping overrides). Useful for blocking QA bots and fake leads.
  excludeTestEmails: boolean("exclude_test_emails").default(false),
  excludeTestEmailPatterns: jsonb("exclude_test_email_patterns").$type<string[]>(),
  // Drop conversions from these IPs (visitor.ip exact-match).
  excludeIps: jsonb("exclude_ips").$type<string[]>(),
  // Output currency for revenue (e.g. 'USD'). When set, revenue is FX-converted
  // from the conversion's source currency before being sent. NULL = pass-through.
  targetCurrency: varchar("target_currency", { length: 3 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Send log — records every CAPI send for audit & debugging
export const capiSendLog = pgTable("capi_send_log", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  mappingId: varchar("mapping_id").notNull(), // FK to capi_event_mappings.id
  conversionId: varchar("conversion_id"), // FK to tracking_conversions.id
  platform: varchar("platform", { length: 30 }).notNull(),
  platformEvent: varchar("platform_event", { length: 100 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"), // 'pending' | 'sent' | 'failed' | 'skipped'
  httpStatus: integer("http_status"), // response status code
  responseBody: text("response_body"), // response body (for debugging)
  errorMessage: text("error_message"), // error details if failed
  payloadSent: jsonb("payload_sent"), // sanitized copy of what was sent (no raw PII)
  sentAt: timestamp("sent_at").defaultNow(),
});

// Domain → ownerUserId override map. Admin-managed safety net for misconfigured
// tracking snippets (where the embedded ?id=<userId> points at the wrong owner).
// `hostname` is normalized to lowercase, www-stripped at write time.
export const trackingDomainOverrides = pgTable("tracking_domain_overrides", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  hostname: varchar("hostname", { length: 253 }).notNull().unique(),
  ownerUserId: varchar("owner_user_id").notNull(), // FK to users.id (target owner)
  reason: text("reason"), // why the override exists (incident reference, etc.)
  createdByUserId: varchar("created_by_user_id"), // admin who added it
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTrackingDomainOverrideSchema = createInsertSchema(trackingDomainOverrides).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type TrackingDomainOverride = typeof trackingDomainOverrides.$inferSelect;
export type InsertTrackingDomainOverride = z.infer<typeof insertTrackingDomainOverrideSchema>;

// CAPI platform credentials/config per user
export const capiPlatformConfig = pgTable("capi_platform_config", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  platform: varchar("platform", { length: 30 }).notNull(), // 'meta' | 'google_ads' | 'tiktok'
  // Meta CAPI
  metaPixelId: varchar("meta_pixel_id", { length: 100 }), // Default pixel/dataset ID
  metaAccessToken: text("meta_access_token"), // System user token for CAPI (different from OAuth token)
  metaTestEventCode: varchar("meta_test_event_code", { length: 50 }), // For test mode
  // Google Ads Offline Conversions
  googleCustomerId: varchar("google_customer_id", { length: 50 }), // xxx-xxx-xxxx
  googleConversionActionId: varchar("google_conversion_action_id", { length: 200 }),
  // login-customer-id header value when uploading via an MCC parent (xxx-xxx-xxxx).
  googleLoginCustomerId: varchar("google_login_customer_id", { length: 50 }),
  // TikTok Events API
  tiktokPixelId: varchar("tiktok_pixel_id", { length: 100 }),
  tiktokAccessToken: text("tiktok_access_token"),
  tiktokTestEventCode: varchar("tiktok_test_event_code", { length: 50 }),
  // General
  enabled: boolean("enabled").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Zod schemas for CAPI tables
export const insertCapiEventMappingSchema = createInsertSchema(capiEventMappings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCapiSendLogSchema = createInsertSchema(capiSendLog).omit({ id: true });
export const insertCapiPlatformConfigSchema = createInsertSchema(capiPlatformConfig).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types for CAPI
export type CapiEventMapping = typeof capiEventMappings.$inferSelect;
export type InsertCapiEventMapping = z.infer<typeof insertCapiEventMappingSchema>;
export type CapiSendLog = typeof capiSendLog.$inferSelect;
export type CapiPlatformConfig = typeof capiPlatformConfig.$inferSelect;
export type InsertCapiPlatformConfig = z.infer<typeof insertCapiPlatformConfigSchema>;

// ─── External API Keys ───────────────────────────────────────────────────────
// API keys for external systems (CRMs, HubSpot, etc.) to push data into GawronifyOS
export const apiKeys = pgTable("api_keys", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  agencyId: varchar("agency_id").notNull(), // scoped to agency
  clientId: varchar("client_id"), // scoped to specific client (user) — null = agency-level key
  createdByUserId: varchar("created_by_user_id").notNull(), // who generated the key
  name: text("name").notNull(), // human-readable label, e.g. "HubSpot sync"
  keyPrefix: varchar("key_prefix", { length: 8 }).notNull(), // first 8 chars of key for identification
  keyHash: text("key_hash").notNull(), // SHA-256 hash of the full key
  scopes: jsonb("scopes")
    .$type<string[]>()
    .default(sql`'["contacts:write","contacts:read"]'`), // allowed scopes
  lastUsedAt: timestamp("last_used_at"),
  expiresAt: timestamp("expires_at"), // null = never expires
  revokedAt: timestamp("revoked_at"), // null = active, set = revoked
  createdAt: timestamp("created_at").defaultNow(),
});

// Contacts ingested via external API (users with UTMs, trial/conversion data)
export const apiIngestedContacts = pgTable("api_ingested_contacts", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  agencyId: varchar("agency_id").notNull(), // scoped to agency
  clientId: varchar("client_id"), // scoped to specific client (user)
  externalId: varchar("external_id"), // ID in the external system (e.g. HubSpot contact ID)
  email: text("email"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  phone: varchar("phone", { length: 30 }),
  // UTM tracking fields
  utmSource: varchar("utm_source", { length: 255 }),
  utmMedium: varchar("utm_medium", { length: 255 }),
  utmCampaign: varchar("utm_campaign", { length: 500 }),
  utmContent: varchar("utm_content", { length: 500 }),
  utmTerm: varchar("utm_term", { length: 500 }),
  // Lifecycle fields
  status: varchar("status", { length: 30 }).default("lead"), // lead | trial | converted | churned
  trialStartedAt: timestamp("trial_started_at"),
  convertedAt: timestamp("converted_at"),
  churnedAt: timestamp("churned_at"),
  // Revenue & value
  lifetimeValue: numeric("lifetime_value"), // total revenue from this contact
  currency: varchar("currency", { length: 10 }).default("PLN"),
  // Flexible extra data
  customData: jsonb("custom_data").$type<Record<string, any>>(),
  // Metadata
  apiKeyId: varchar("api_key_id"), // which key was used to create/update
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Conversion events pushed via external API (SaaS funnel events per contact)
export const apiIngestedEvents = pgTable("api_ingested_events", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  agencyId: varchar("agency_id").notNull(),
  clientId: varchar("client_id"), // scoped to specific client (user)
  contactId: varchar("contact_id"), // FK to api_ingested_contacts (resolved by email/external_id)
  // Event identification
  eventName: varchar("event_name", { length: 100 }).notNull(), // e.g. registerStart, activateSuccess
  // Attribution context at time of event
  utmSource: varchar("utm_source", { length: 255 }),
  utmMedium: varchar("utm_medium", { length: 255 }),
  utmCampaign: varchar("utm_campaign", { length: 500 }),
  utmContent: varchar("utm_content", { length: 500 }),
  utmTerm: varchar("utm_term", { length: 500 }),
  // Event-specific data
  revenue: numeric("revenue"), // for purchase/activation events
  currency: varchar("currency", { length: 10 }).default("PLN"),
  properties: jsonb("properties").$type<Record<string, any>>(), // flexible event metadata
  // Contact identification (for resolution)
  email: text("email"),
  externalId: varchar("external_id"),
  // Metadata
  apiKeyId: varchar("api_key_id"),
  eventTimestamp: timestamp("event_timestamp").defaultNow(), // when the event actually occurred
  createdAt: timestamp("created_at").defaultNow(), // when we received it
});

// ─── KPI Monitoring Alerts ───
// Stores alerts generated by periodic KPI checks (no AI, pure data comparison)
export const ALERT_TYPES = {
  CPL_EXCEEDED: "cpl_exceeded",
  CPC_EXCEEDED: "cpc_exceeded",
  ROAS_BELOW: "roas_below",
  NO_LEADS: "no_leads",
  SPEND_ANOMALY: "spend_anomaly",
  PLACEMENT_WASTE: "placement_waste",
  ZERO_CONVERSIONS: "zero_conversions",
  BUDGET_INCREASE: "budget_increase",
  HIGH_CPM: "high_cpm",
  MONTHLY_KPI_BEHIND: "monthly_kpi_behind",
  QUARTERLY_KPI_BEHIND: "quarterly_kpi_behind",
  EARLY_WINNER_SIGNAL: "early_winner_signal",
  EARLY_LOSER_SIGNAL: "early_loser_signal",
  // Integration alerts
  WEBINAR_LOW_SHOW_RATE: "webinar_low_show_rate",
  WEBINAR_LOW_OFFER_RATE: "webinar_low_offer_rate",
  SHOPIFY_LOW_STOCK: "shopify_low_stock",
  SHOPIFY_OUT_OF_STOCK: "shopify_out_of_stock",
  SHOPIFY_REVENUE_DROP: "shopify_revenue_drop",
  CRM_LOW_CLOSE_RATE: "crm_low_close_rate",
  CRM_LOW_SHOW_RATE: "crm_low_show_rate",
  CRM_NO_NEW_LEADS: "crm_no_new_leads",
  GHL_TOKEN_INVALID: "ghl_token_invalid", // VP-194: refresh tokenu GHL trwale pada (reconnect)
  SHOPIFY_GDPR_REQUEST_PENDING: "shopify_gdpr_request_pending", // VP-217: customers/data_request zalega (odpowiedz klientowi w 30 dni)
  SHOPIFY_GDPR_REDACT_FAILED: "shopify_gdpr_redact_failed", // VP-217: redakcja GDPR padla (reczna interwencja admina)
  BASELINKER_LOW_STOCK: "baselinker_low_stock",
  BASELINKER_REVENUE_DROP: "baselinker_revenue_drop",
  RINGOSTAT_LOW_ANSWER_RATE: "ringostat_low_answer_rate",
  RINGOSTAT_LOW_CALL_VOLUME: "ringostat_low_call_volume",
  ALLEGRO_SALES_DROP: "allegro_sales_drop",
  ALLEGRO_LOW_ROAS: "allegro_low_roas",
  HUBSPOT_LOW_CLOSE_RATE: "hubspot_low_close_rate",
  HUBSPOT_PIPELINE_STALL: "hubspot_pipeline_stall",
  CAPI_SENDS_FAILING: "capi_sends_failing", // VP-253: Meta odrzuca wysyłki CAPI (konwersje nie docierają do optymalizacji)
  TRACKING_OWNER_UNRESOLVED: "tracking_owner_unresolved", // VP-254: domena zbiera ruch, ktorego nie da sie przypisac do konta
} as const;

export type AlertType = (typeof ALERT_TYPES)[keyof typeof ALERT_TYPES];

export type AlertSeverity = "critical" | "warning" | "info";

export const monitoringAlerts = pgTable("monitoring_alerts", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  alertType: varchar("alert_type", { length: 50 }).notNull(),
  severity: varchar("severity", { length: 20 }).notNull(), // critical | warning | info
  accountId: varchar("account_id"), // ad account ID (act_xxx)
  accountName: varchar("account_name"),
  metricName: varchar("metric_name"), // e.g. 'cost_per_lead', 'roas'
  actualValue: numeric("actual_value"), // what was measured
  targetValue: numeric("target_value"), // what the KPI target is
  message: text("message").notNull(), // legacy human-readable PL message (fallback)
  recommendation: text("recommendation"), // legacy actionable recommendation (fallback)
  messageCode: varchar("message_code", { length: 100 }),
  messageParams: jsonb("message_params")
    .$type<Record<string, string | number> | null>()
    .default(null),
  recommendationCode: varchar("recommendation_code", { length: 100 }),
  recommendationParams: jsonb("recommendation_params")
    .$type<Record<string, string | number> | null>()
    .default(null),
  isRead: boolean("is_read").default(false),
  dismissedAt: timestamp("dismissed_at"),
  dedupeKey: varchar("dedupe_key"), // prevents duplicate alerts within 24h
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMonitoringAlertSchema = createInsertSchema(monitoringAlerts).omit({
  id: true,
  createdAt: true,
});
export type MonitoringAlert = typeof monitoringAlerts.$inferSelect;
export type InsertMonitoringAlert = z.infer<typeof insertMonitoringAlertSchema>;

// ─── Monitoring Alert Feedback (learning loop) ───
export const monitoringAlertFeedback = pgTable("monitoring_alert_feedback", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  alertId: varchar("alert_id")
    .notNull()
    .references(() => monitoringAlerts.id, { onDelete: "cascade" }),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull(), // 1 = thumbs up, 0 = thumbs down
  comment: text("comment"), // optional explanation
  alertType: varchar("alert_type", { length: 50 }).notNull(), // denormalized for fast queries
  alertMessage: text("alert_message").notNull(), // snapshot of the alert message at feedback time
  isGlobal: boolean("is_global").default(false), // true = super_admin feedback, applies to all users
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMonitoringAlertFeedbackSchema = createInsertSchema(monitoringAlertFeedback).omit(
  { id: true, createdAt: true }
);
export type MonitoringAlertFeedback = typeof monitoringAlertFeedback.$inferSelect;
export type InsertMonitoringAlertFeedback = z.infer<typeof insertMonitoringAlertFeedbackSchema>;

// ─── User Guidelines (custom alert rules / filters) ───
export const userGuidelines = pgTable("user_guidelines", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("active"), // 'active' | 'rejected'
  guidelineScope: varchar("guideline_scope", { length: 30 }).notNull().default("self"), // 'self' | 'single_client' | 'all_assigned_clients'
  targetAgencyClientId: varchar("target_agency_client_id").references(() => agencyClients.id, {
    onDelete: "set null",
  }),
  parsedRule: jsonb("parsed_rule"), // structured rule parsed from content at save time
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserGuidelineSchema = createInsertSchema(userGuidelines).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type UserGuideline = typeof userGuidelines.$inferSelect;
export type InsertUserGuideline = z.infer<typeof insertUserGuidelineSchema>;

// Zod schemas for External API
export const insertApiKeySchema = createInsertSchema(apiKeys).omit({
  id: true,
  createdAt: true,
  lastUsedAt: true,
  revokedAt: true,
});
export const insertApiIngestedContactSchema = createInsertSchema(apiIngestedContacts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertApiIngestedEventSchema = createInsertSchema(apiIngestedEvents).omit({
  id: true,
  createdAt: true,
});

// Types for External API
export type ApiKey = typeof apiKeys.$inferSelect;
export type InsertApiKey = z.infer<typeof insertApiKeySchema>;
export type ApiIngestedContact = typeof apiIngestedContacts.$inferSelect;
export type InsertApiIngestedContact = z.infer<typeof insertApiIngestedContactSchema>;
export type ApiIngestedEvent = typeof apiIngestedEvents.$inferSelect;
export type InsertApiIngestedEvent = z.infer<typeof insertApiIngestedEventSchema>;

// ── Changelog ──
export const CHANGELOG_STATUSES = ["gotowe", "wkrotce", "pomysly", "ogloszenia"] as const;
export const CHANGELOG_CATEGORIES = ["nowa-funkcja", "ulepszenie", "poprawka"] as const;

export const changelogEntries = pgTable("changelog_entries", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  description: text("description").notNull(),
  date: date("date").notNull(),
  status: varchar("status", { length: 20 }).notNull(), // gotowe | wkrotce | pomysly | ogloszenia
  category: varchar("category", { length: 20 }).notNull(), // nowa-funkcja | ulepszenie | poprawka
  imageUrl: text("image_url"),
  upvotes: integer("upvotes").default(0),
  downvotes: integer("downvotes").default(0),
  authorName: varchar("author_name", { length: 100 }),
  authorEmail: varchar("author_email", { length: 200 }),
  importance: integer("importance").default(3),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertChangelogEntrySchema = createInsertSchema(changelogEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type ChangelogEntry = typeof changelogEntries.$inferSelect;
export type InsertChangelogEntry = z.infer<typeof insertChangelogEntrySchema>;

export const changelogComments = pgTable("changelog_comments", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  entryId: varchar("entry_id")
    .notNull()
    .references(() => changelogEntries.id, { onDelete: "cascade" }),
  authorName: varchar("author_name", { length: 100 }).notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// ─── EverWebinar Integration ───
export const everwebinarIntegrations = pgTable("everwebinar_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  apiKey: text("api_key").notNull(), // encrypted via encryptToken()
  webinarId: integer("webinar_id").notNull(), // numeric ID from EverWebinar
  webinarName: varchar("webinar_name"), // human-readable name (fetched on connect)
  // Offer detection: attendees who watched at least this many seconds are treated as "stayed to offer"
  offerStartSeconds: integer("offer_start_seconds").notNull().default(2700), // 45 min default
  // Sync state
  lastSyncedAt: timestamp("last_synced_at"),
  lastSyncedDate: date("last_synced_date"), // last date we synced (yyyy-mm-dd)
  enabled: boolean("enabled").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertEverwebinarIntegrationSchema = createInsertSchema(everwebinarIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type EverwebinarIntegration = typeof everwebinarIntegrations.$inferSelect;
export type InsertEverwebinarIntegration = z.infer<typeof insertEverwebinarIntegrationSchema>;

// ── Brand Assets — DROPPED w 2026-05 (zunifikowane z firm_creative_assets).
// Migracja danych przez scripts/migrate-brand-assets-to-firm.ts
// Endpointy /api/ad-creator/brand-assets oraz /api/carousel-creator/brand-assets piszą teraz
// do firm_creative_assets (z mapperem w server/modules/_shared/brand-asset-mapper.ts).

// ── Avatars (named personas: kontekst + voice refs + zdjęcie) ──
// Wprowadzone w 2026-05 zgodnie z transcript feedback: jeden Avatar = persona
// (Mateusz, Pamela, Brand VP) podpinana jednym klikiem do generatora.
export const avatars = pgTable("avatars", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  clientId: varchar("client_id"),
  name: varchar("name", { length: 80 }).notNull(),
  contextText: text("context_text").notNull().default(""),
  voiceRefs: jsonb("voice_refs")
    .notNull()
    .default(sql`'[]'::jsonb`), // string[] (np. linki YouTube)
  photoUrl: text("photo_url"),
  photoStoragePath: text("photo_storage_path"),
  usePhotoByDefault: boolean("use_photo_by_default").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAvatarSchema = createInsertSchema(avatars).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type Avatar = typeof avatars.$inferSelect;
export type InsertAvatar = z.infer<typeof insertAvatarSchema>;

// ── Graphic Feedback (ad-creator) ──
export const graphicFeedback = pgTable("graphic_feedback", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  graphicId: varchar("graphic_id").notNull(),
  rating: varchar("rating", { length: 10 }).notNull(), // 'up' | 'down'
  prompt: text("prompt"),
  imageModel: varchar("image_model", { length: 50 }),
  format: varchar("format", { length: 30 }),
  funnelType: varchar("funnel_type", { length: 30 }),
  reason: text("reason"), // user's explanation of why it was good/bad
  isGlobal: boolean("is_global").default(false), // true for super_admin feedback
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGraphicFeedbackSchema = createInsertSchema(graphicFeedback).omit({
  id: true,
  createdAt: true,
});
export type GraphicFeedback = typeof graphicFeedback.$inferSelect;
export type InsertGraphicFeedback = z.infer<typeof insertGraphicFeedbackSchema>;

// ── User Graphics Library ──
export const userGraphics = pgTable("user_graphics", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  sessionId: varchar("session_id"), // groups graphics from one generation run
  r2Key: text("r2_key").notNull(), // Cloudflare R2 object key
  filename: text("filename").notNull(),
  format: varchar("format", { length: 20 }).notNull(), // 'feed' | 'story' | 'square' | 'landscape' | 'banner'
  variantIndex: integer("variant_index").notNull(),
  width: integer("width").notNull(),
  height: integer("height").notNull(),
  fileSizeBytes: integer("file_size_bytes").notNull(),
  prompt: text("prompt"),
  imageModel: varchar("image_model", { length: 50 }),
  funnelType: varchar("funnel_type", { length: 50 }),
  tags: jsonb("tags").$type<string[]>().default([]),
  notes: text("notes"),
  rating: varchar("rating", { length: 10 }), // 'up' | 'down' | null
  ratingReason: text("rating_reason"),
  mediaType: varchar("media_type", { length: 10 }).default("image"), // 'image' | 'video'
  isUserUpload: boolean("is_user_upload").default(false),
  campaignIds: jsonb("campaign_ids").$type<string[]>().default([]), // Meta campaign IDs linked
  // Viral Gallery (Higgsfield-style explore feed)
  isFeatured: boolean("is_featured").default(false), // curated by admin for Explore
  isPublic: boolean("is_public").default(false), // user opt-in: visible in Explore
  parentGraphicId: varchar("parent_graphic_id"), // set for upscale / img2video / recreate derivatives
  // Curated gallery — superadmin pins items and assigns category
  isInGallery: boolean("is_in_gallery").default(false),
  galleryCategory: varchar("gallery_category", { length: 50 }),
  // Recreate provenance — which creator made this and with what settings
  creatorType: varchar("creator_type", { length: 30 }), // 'ugc-studio' | 'ad-graphics' | 'reel-creator'
  creatorSettings: jsonb("creator_settings").$type<Record<string, unknown>>(),
  // When this graphic is one frame of a Stories sequence, links to story_sequences.id
  sequenceId: varchar("sequence_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUserGraphicSchema = createInsertSchema(userGraphics).omit({
  id: true,
  createdAt: true,
});
export type UserGraphic = typeof userGraphics.$inferSelect;
export type InsertUserGraphic = z.infer<typeof insertUserGraphicSchema>;

// ── Stories sequences (multi-frame Instagram Stories grouped as one library item) ──
export const storySequences = pgTable("story_sequences", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  conversationId: varchar("conversation_id"), // source chat conversation
  messageId: varchar("message_id"), // source assistant message (script source)
  title: text("title"), // derived from first frame's headline
  frameCount: integer("frame_count").notNull(),
  // Full script JSON (frames + sharedBackgroundPrompt) — restorable, re-renderable
  scriptJson: jsonb("script_json").$type<Record<string, unknown>>().notNull(),
  // Generation metadata: model, promptVersion, offerSheetId, backgroundMode, providerId
  generationMeta: jsonb("generation_meta").$type<Record<string, unknown>>(),
  // Thumbnail = first frame's r2Key for grid display
  thumbnailR2Key: text("thumbnail_r2_key"),
  notes: text("notes"),
  isPublic: boolean("is_public").default(false),
  isInGallery: boolean("is_in_gallery").default(false),
  galleryCategory: varchar("gallery_category", { length: 50 }),
  // VP-101: owner picks which of their sequences appear in the Stories-creator
  // header showcase. Independent of the (super_admin) viral-gallery pin above —
  // a simple per-user toggle, no category.
  showInShowcase: boolean("show_in_showcase").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertStorySequenceSchema = createInsertSchema(storySequences).omit({
  id: true,
  createdAt: true,
});
export type StorySequence = typeof storySequences.$inferSelect;
export type InsertStorySequence = z.infer<typeof insertStorySequenceSchema>;

// ── VP-75: Stories async render jobs ─────────────────────────────────────────
// Persisted so any ECS container can serve poll requests regardless of which
// container started the render (in-memory Map is per-process). Separate from
// story_sequences (library/content) — jobs are operational state with a TTL.
export const storyRenderJobs = pgTable(
  "story_render_jobs",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    jobId: varchar("job_id").notNull().unique(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    status: varchar("status", { length: 20 }).notNull().default("queued"), // queued | running | done | failed
    progress: integer("progress").notNull().default(0),
    error: text("error"),
    // Set once StoriesRenderer creates the sequence (allows frame lookup via userGraphics).
    sequenceId: varchar("sequence_id").references(() => storySequences.id, {
      onDelete: "set null",
    }),
    // Cached render metadata returned in the poll response (backgroundMode, provider, tokens).
    renderMeta: jsonb("render_meta").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    // Fast lookup for hasActiveDbJob (called on every POST /render).
    userStatusIdx: index("story_render_jobs_user_status_idx").on(table.userId, table.status),
    // Partial unique index enforces at most one active (queued/running) job per user.
    // Enables atomic INSERT … ON CONFLICT DO NOTHING instead of check-then-insert (TOCTOU).
    oneActivePerUser: uniqueIndex("story_render_jobs_one_active_per_user_idx")
      .on(table.userId)
      .where(sql`status IN ('queued', 'running')`),
  })
);

export type StoryRenderJob = typeof storyRenderJobs.$inferSelect;

// VP-173: appointmenty GHL, których kontakt jest TRWALE nierozwiązywalny (GHL zwraca 404 — kontakt
// skasowany/nieistniejący). Bez tego puller re-fetchował ich kontakty na każdym ticku crona (~15 min),
// trafiając w GHL rate-limit (429) na ślepo (te same id w kółko). Zapisujemy TYLKO twarde 404 — 429/5xx/
// błąd sieci są PRZEJŚCIOWE i muszą być retry'owane, więc ich tu NIE markujemy. Forward-fill: tabela
// startuje pusta i zapełnia się z sygnału 404; `lastSeenAt` daje retry-window (kontakt może wrócić w GHL,
// więc po oknie ponawiamy próbę raz). Patrz GhlAppointmentPullerService + fetchContactsByIds.
export const ghlUnresolvedAppointments = pgTable(
  "ghl_unresolved_appointments",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    ownerUserId: varchar("owner_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    ghlAppointmentId: varchar("ghl_appointment_id").notNull(),
    ghlContactId: varchar("ghl_contact_id"),
    reason: varchar("reason", { length: 32 }).notNull().default("contact_missing"), // contact_missing (GHL 404)
    attempts: integer("attempts").notNull().default(1),
    firstSeenAt: timestamp("first_seen_at").defaultNow().notNull(),
    lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
  },
  (table) => ({
    // Idempotent upsert + per-user exclusion lookup: INSERT ... ON CONFLICT (owner, appt) DO UPDATE.
    ownerApptIdx: uniqueIndex("ghl_unresolved_appointments_owner_appt_idx").on(
      table.ownerUserId,
      table.ghlAppointmentId
    ),
    // Retry-window scan: load a user's still-active markers (lastSeenAt within window).
    ownerSeenIdx: index("ghl_unresolved_appointments_owner_seen_idx").on(
      table.ownerUserId,
      table.lastSeenAt
    ),
  })
);

export type GhlUnresolvedAppointment = typeof ghlUnresolvedAppointments.$inferSelect;

// ── HeyGen custom avatars (per-user trained / instant) ──
export const userHeygenAvatars = pgTable("user_heygen_avatars", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  // 'photo' = HeyGen Photo Avatar (trained from portrait); 'instant' = HeyGen Instant Avatar (from short video)
  type: varchar("type", { length: 10 }).notNull(),
  // 'uploading' | 'training' | 'ready' | 'failed'
  status: varchar("status", { length: 20 }).notNull().default("uploading"),
  heygenGroupId: text("heygen_group_id"),
  heygenAvatarId: text("heygen_avatar_id"),
  heygenTrainJobId: text("heygen_train_job_id"),
  previewImageUrl: text("preview_image_url"),
  previewVideoUrl: text("preview_video_url"),
  sourceR2Key: text("source_r2_key"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserHeygenAvatarSchema = createInsertSchema(userHeygenAvatars).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type UserHeygenAvatar = typeof userHeygenAvatars.$inferSelect;
export type InsertUserHeygenAvatar = z.infer<typeof insertUserHeygenAvatarSchema>;

export const userMediaAssets = pgTable("user_media_assets", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  filename: text("filename").notNull(),
  mimeType: text("mime_type").notNull(),
  mediaType: varchar("media_type", { length: 10 }).notNull(),
  fileSizeBytes: integer("file_size_bytes").notNull(),
  storageProvider: varchar("storage_provider", { length: 20 }).notNull().default("r2"),
  r2Key: text("r2_key"),
  localPath: text("local_path"),
  bucketFolder: varchar("bucket_folder", { length: 100 }),
  sourceType: varchar("source_type", { length: 50 }).notNull(),
  linkedEntityType: varchar("linked_entity_type", { length: 50 }),
  linkedEntityId: varchar("linked_entity_id", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUserMediaAssetSchema = createInsertSchema(userMediaAssets).omit({
  id: true,
  createdAt: true,
});
export type UserMediaAsset = typeof userMediaAssets.$inferSelect;
export type InsertUserMediaAsset = z.infer<typeof insertUserMediaAssetSchema>;

// ── UGC Studio: Avatars ─────────────────────────────────────
export const userAvatars = pgTable("user_avatars", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  gender: varchar("gender", { length: 10 }), // 'male' | 'female' | 'neutral' | null
  sourceType: varchar("source_type", { length: 20 }).notNull(), // 'generated' | 'uploaded'
  r2Key: text("r2_key").notNull(),
  prompt: text("prompt"),
  modelUsed: varchar("model_used", { length: 50 }),
  isPinned: boolean("is_pinned").default(false).notNull(),
  isPublic: boolean("is_public").default(false).notNull(),
  tags: jsonb("tags").$type<string[]>().default([]),
  appearanceMeta: jsonb("appearance_meta").$type<{
    ethnicity?: string;
    ageRange?: string;
    hair?: string;
    build?: string;
    skinTone?: string;
    spokenLanguage?: string;
    nationality?: string;
    clothingDefault?: string;
    setting?: string;
    vibe?: string;
  }>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserAvatarSchema = createInsertSchema(userAvatars).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type UserAvatar = typeof userAvatars.$inferSelect;
export type InsertUserAvatar = z.infer<typeof insertUserAvatarSchema>;

// ── UGC Studio: Products / Apps ─────────────────────────────
export const userProducts = pgTable("user_products", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  type: varchar("type", { length: 10 }).notNull(), // 'product' | 'app'
  name: text("name").notNull(),
  description: text("description"),
  sourceUrl: text("source_url"),
  platform: varchar("platform", { length: 10 }), // 'desktop' | 'mobile' | null
  imageR2Keys: jsonb("image_r2_keys").$type<string[]>().default([]).notNull(),
  scrapedMeta: jsonb("scraped_meta").$type<Record<string, unknown>>().default({}),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserProductSchema = createInsertSchema(userProducts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type UserProduct = typeof userProducts.$inferSelect;
export type InsertUserProduct = z.infer<typeof insertUserProductSchema>;

// ── UGC Studio: Jobs ────────────────────────────────────────
export const ugcStudioJobs = pgTable("ugc_studio_jobs", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  status: varchar("status", { length: 20 }).notNull().default("queued"), // 'queued' | 'generating' | 'completed' | 'failed'
  prompt: text("prompt").notNull(),
  format: varchar("format", { length: 30 }).notNull(),
  aspectRatio: varchar("aspect_ratio", { length: 10 }).notNull(),
  resolution: varchar("resolution", { length: 10 }),
  durationSeconds: integer("duration_seconds").notNull(),
  videoModel: varchar("video_model", { length: 40 }).notNull(),
  avatarId: varchar("avatar_id"),
  productId: varchar("product_id"),
  referenceImageR2Key: text("reference_image_r2_key"),
  finalPrompt: text("final_prompt"),
  outputR2Key: text("output_r2_key"),
  outputGraphicId: varchar("output_graphic_id"),
  tokensCharged: integer("tokens_charged").default(0).notNull(),
  progress: integer("progress").default(0).notNull(),
  errorMessage: text("error_message"),
  // ElevenLabs audio integration (optional, opt-in per job)
  script: jsonb("script").$type<Array<{ start: number; end: number; text: string }>>(),
  audioMode: varchar("audio_mode", { length: 20 }), // 'native' | 'overlay' | 'lipsync' | 'voiceover'
  voiceId: varchar("voice_id", { length: 60 }),
  audioR2Key: text("audio_r2_key"),
  /**
   * R2 key of the raw Seedance MP4 (with native Seedance audio), kept around
   * so the user can download the unmodified clip and the TTS audio side-by-
   * side with the final lip-synced result. Only populated when audio !=
   * 'native', otherwise the final output IS the seedance output.
   */
  seedanceR2Key: text("seedance_r2_key"),
  /**
   * Per-component breakdown of `tokensCharged` (in app tokens, AFTER margin).
   * Shape matches `UgcJobCostBreakdown` from `@shared/ugc-pricing`:
   *   { videoTokens, ttsTokens, lipsyncTokens, whisperTokens, scriptLlmTokens, llmOverheadTokens }
   * Sum equals `tokensCharged` at enqueue time. After staged refunds the
   * `tokensCharged` column is updated, but breakdown stays as the original
   * estimate for transparency.
   */
  costBreakdown: jsonb("cost_breakdown").$type<{
    videoTokens: number;
    ttsTokens: number;
    lipsyncTokens: number;
    whisperTokens: number;
    scriptLlmTokens: number;
    llmOverheadTokens: number;
  }>(),
  /**
   * Last completed pipeline stage. Used by staged refunds: when a job fails,
   * the unconsumed components from `costBreakdown` are refunded based on
   * which stage we got to. Values mirror `UgcJobStage` in `@shared/ugc-pricing`.
   */
  currentStage: varchar("current_stage", { length: 20 }).default("queued"),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});

export const insertUgcStudioJobSchema = createInsertSchema(ugcStudioJobs).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});
export type UgcStudioJob = typeof ugcStudioJobs.$inferSelect;
export type InsertUgcStudioJob = z.infer<typeof insertUgcStudioJobSchema>;

// ── Background generation jobs (shared mechanism: graphic now, video + other long ops later) ──
// Decouples a long generation from the HTTP request and the React view: enqueue returns
// immediately, the work runs fire-and-forget in the Node loop, and the client polls by id.
// Survives navigation/unmount because the source of truth is this table, not the request.
export const generationJobs = pgTable(
  "generation_jobs",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    type: varchar("type", { length: 30 }).notNull(), // 'graphic' | 'video' | ...
    status: varchar("status", { length: 20 }).notNull().default("queued"), // 'queued' | 'generating' | 'done' | 'error'
    // Whitelisted, secret-free generation params (brief/aspect/resolution/imageModel/referenceImages/...).
    params: jsonb("params").$type<Record<string, unknown>>().notNull(),
    resultUrls: jsonb("result_urls")
      .$type<string[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),
    // Where the badge sends the user to "return exactly to where it's generating".
    deepLink: text("deep_link").notNull(),
    progress: integer("progress").default(0).notNull(),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at").defaultNow(),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
  },
  (t) => ({
    userStatusIdx: index("generation_jobs_user_status_idx").on(t.userId, t.status),
  })
);

export const insertGenerationJobSchema = createInsertSchema(generationJobs).omit({
  id: true,
  createdAt: true,
  startedAt: true,
  completedAt: true,
});
export type GenerationJob = typeof generationJobs.$inferSelect;
export type InsertGenerationJob = z.infer<typeof insertGenerationJobSchema>;

// ── UGC Studio: Custom Voices (ElevenLabs Voice Design) ─────
export const ugcCustomVoices = pgTable("ugc_custom_voices", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 80 }).notNull(),
  voiceId: varchar("voice_id", { length: 60 }).notNull(),
  prompt: text("prompt").notNull(),
  language: varchar("language", { length: 40 }),
  samplePreviewKey: text("sample_preview_key"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type UgcCustomVoice = typeof ugcCustomVoices.$inferSelect;
export type InsertUgcCustomVoice = typeof ugcCustomVoices.$inferInsert;

// ── Brand Templates (schemat graficzny) ──
export interface TemplateTextZone {
  id: string;
  label: string;
  x: number; // percentage 0-100
  y: number; // percentage 0-100
  width: number; // percentage 0-100
  height: number; // percentage 0-100
  defaultText: string;
  fontSize: number; // px (relative to original image)
  fontWeight: "normal" | "bold";
  color: string; // hex e.g. #FFFFFF
  textAlign: "left" | "center" | "right";
}

export const brandTemplates = pgTable("brand_templates", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  clientId: varchar("client_id"),
  name: text("name").notNull(),
  imageUrl: text("image_url").notNull(),
  storagePath: text("storage_path").notNull(),
  mimeType: varchar("mime_type", { length: 50 }).notNull(),
  width: integer("width").notNull(),
  height: integer("height").notNull(),
  textZones: jsonb("text_zones").$type<TemplateTextZone[]>().notNull().default([]),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertBrandTemplateSchema = createInsertSchema(brandTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type BrandTemplate = typeof brandTemplates.$inferSelect;
export type InsertBrandTemplate = z.infer<typeof insertBrandTemplateSchema>;

// ── Scheduled Posts (Content Scheduler) ──
export type ScheduledPostStatus = "draft" | "scheduled" | "publishing" | "published" | "failed";
export type ScheduledPostPlatform = "facebook" | "instagram" | "linkedin" | "youtube" | "tiktok";
export type ScheduledPostMediaType = "text" | "image" | "video" | "carousel";
export type ScheduledMetaPostType = "post" | "reel" | "trial_reel" | "story";

// ── Per-platform presets stored as JSONB on scheduled_posts ──
export interface SchedulerGlobalPresets {
  autoPublish: boolean;
  useUrlShortener: boolean;
}

export interface InstagramPresets {
  collaborators: string[];
}

export type ThreadsReplyControl = "everyone" | "accounts_you_follow" | "mentioned_only";
export interface ThreadsPresets {
  replyControl: ThreadsReplyControl;
}

export interface LinkedinPresets {
  showLinkPreview: boolean;
  imageCarousel: boolean;
}

// Raw TikTok privacy_level values returned by creator_info/query. '' = the user
// has not chosen yet (Content Sharing Guidelines require NO default selection).
export type TikTokPrivacy =
  | ""
  | "PUBLIC_TO_EVERYONE"
  | "MUTUAL_FOLLOW_FRIENDS"
  | "FOLLOWER_OF_CREATOR"
  | "SELF_ONLY";
export interface TikTokPresets {
  /** TikTok privacy_level chosen from creator_info.privacy_level_options. '' = not yet chosen. */
  privacy: TikTokPrivacy;
  allowComments: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  aiGenerated: boolean;
  /** Master "Disclose content" toggle (Commercial Content Disclosure). Off by default. */
  commercialDisclosure: boolean;
  /** "Your brand" — promotes user's own brand. Maps to brand_organic_toggle. */
  brandOrganic: boolean;
  /** "Branded content" — paid partnership with another brand. Maps to brand_content_toggle. */
  brandContent: boolean;
}

export type YoutubePrivacy = "public" | "unlisted" | "private";
export type YoutubeAudience = "kids" | "not_kids";
export interface YoutubePresets {
  title: string;
  privacy: YoutubePrivacy;
  audience: YoutubeAudience | "";
  category: string;
  playlistId: string;
  tags: string[];
}

export interface SchedulerPresets {
  global: SchedulerGlobalPresets;
  instagram: InstagramPresets;
  threads: ThreadsPresets;
  linkedin: LinkedinPresets;
  tiktok: TikTokPresets;
  youtube: YoutubePresets;
}

export const DEFAULT_SCHEDULER_PRESETS: SchedulerPresets = {
  global: { autoPublish: true, useUrlShortener: false },
  instagram: { collaborators: [] },
  threads: { replyControl: "everyone" },
  linkedin: { showLinkPreview: false, imageCarousel: false },
  tiktok: {
    // Guidelines: privacy must have NO pre-selected default; interaction toggles
    // must all start OFF (the user manually enables each).
    privacy: "",
    allowComments: false,
    allowDuet: false,
    allowStitch: false,
    aiGenerated: false,
    commercialDisclosure: false,
    brandOrganic: false,
    brandContent: false,
  },
  youtube: {
    title: "",
    privacy: "public",
    audience: "",
    category: "",
    playlistId: "",
    tags: [],
  },
};

export const scheduledPosts = pgTable(
  "scheduled_posts",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    // Agency-delegation: identity whose Meta/social token should publish this post.
    // For a normal user this equals userId; for an agency-managed client (who has no
    // own meta_integrations row) this is the agency owner/employee who scheduled it,
    // whose token reaches the client's pages. Nullable for legacy rows — falls back to
    // userId at publish time. See resolveMetaPublishToken / resolveMetaContextForTarget.
    tokenOwnerUserId: varchar("token_owner_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    platform: varchar("platform", { length: 20 }).notNull().$type<ScheduledPostPlatform>(),
    status: varchar("status", { length: 20 })
      .notNull()
      .default("draft")
      .$type<ScheduledPostStatus>(),
    postType: varchar("post_type", { length: 20 })
      .notNull()
      .default("post")
      .$type<ScheduledMetaPostType>(),
    title: varchar("title", { length: 100 }),
    caption: text("caption"),
    mediaType: varchar("media_type", { length: 20 })
      .notNull()
      .default("text")
      .$type<ScheduledPostMediaType>(),
    mediaUrls: jsonb("media_urls").$type<string[]>().default([]),
    pageId: varchar("page_id", { length: 100 }),
    pageName: varchar("page_name", { length: 255 }),
    igAccountId: varchar("ig_account_id", { length: 100 }),
    igAccountName: varchar("ig_account_name", { length: 255 }),
    scheduledAt: timestamp("scheduled_at"),
    publishedAt: timestamp("published_at"),
    metaPostId: varchar("meta_post_id", { length: 255 }),
    errorMessage: text("error_message"),
    presets: jsonb("presets").$type<SchedulerPresets>(),
    batchId: varchar("batch_id", { length: 36 }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    byBatch: index("scheduled_posts_batch_id_idx").on(t.batchId),
  })
);

export const insertScheduledPostSchema = createInsertSchema(scheduledPosts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type ScheduledPost = typeof scheduledPosts.$inferSelect;
export type InsertScheduledPost = z.infer<typeof insertScheduledPostSchema>;

// ── Motion Graphics Style Templates ──

export interface MgCaptionStyle {
  bgColor: string;
  bgOpacity?: number;
  textColor: string;
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  borderRadius: number;
  dropShadow: boolean;
  shadowColor: string;
  shadowBlur: number;
  entranceEffect: "none" | "fade-in" | "scale-up" | "slide-up" | "bounce-in";
  textTransform: "none" | "uppercase" | "lowercase";
  letterSpacing: string;
  padding: string;
}

export interface MgTemplateOverlay {
  type: string;
  code: string;
  compiled: string;
  name: string;
  triggerMode: "per-subtitle" | "intro" | "outro" | "manual";
  durationFrames: number;
}

export const mgPresetPacks = pgTable("mg_preset_packs", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  isPublic: boolean("is_public").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertMgPresetPackSchema = createInsertSchema(mgPresetPacks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type MgPresetPack = typeof mgPresetPacks.$inferSelect;
export type InsertMgPresetPack = z.infer<typeof insertMgPresetPackSchema>;

export const mgStyleTemplates = pgTable("mg_style_templates", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  packId: varchar("pack_id"),
  name: text("name").notNull(),
  description: text("description"),
  category: varchar("category", { length: 50 }).default("custom"),
  captionStyle: jsonb("caption_style").$type<MgCaptionStyle>().notNull(),
  overlays: jsonb("overlays").$type<MgTemplateOverlay[]>().notNull().default([]),
  previewCode: text("preview_code"),
  previewCompiled: text("preview_compiled"),
  fps: integer("fps").default(30),
  isPublic: boolean("is_public").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertMgStyleTemplateSchema = createInsertSchema(mgStyleTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type MgStyleTemplate = typeof mgStyleTemplates.$inferSelect;
export type InsertMgStyleTemplate = z.infer<typeof insertMgStyleTemplateSchema>;

// ── Scene Plan Templates (Montaż overlay schemes) ───────────────
export interface ScenePlanTemplateOverlay {
  type: string;
  startRatio: number;
  endRatio: number;
  payload: Record<string, unknown>;
  anchor?: {
    startWord: string;
    startOccurrence: number;
    endWord: string;
    endOccurrence: number;
  };
}

export const mgScenePlanTemplates = pgTable("mg_scene_plan_templates", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  captionStyle: varchar("caption_style", { length: 50 }).notNull(),
  overlays: jsonb("overlays").$type<ScenePlanTemplateOverlay[]>().notNull().default([]),
  isPublic: boolean("is_public").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertMgScenePlanTemplateSchema = createInsertSchema(mgScenePlanTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type MgScenePlanTemplate = typeof mgScenePlanTemplates.$inferSelect;
export type InsertMgScenePlanTemplate = z.infer<typeof insertMgScenePlanTemplateSchema>;

// ─── URL Tracking Rules (HYROS-style URL Rules) ───
// Match URL patterns → assign source tags for attribution
export const trackingUrlRules = pgTable("tracking_url_rules", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  ruleType: varchar("rule_type", { length: 20 }).notNull().default("simple"), // "simple" | "regex"
  tag: varchar("tag", { length: 100 }).notNull(), // e.g. "@google_organic", "$setcall", "$calls"
  wordsToMatch: jsonb("words_to_match").$type<string[]>().notNull().default([]), // URL params/words to match
  wordsToIgnore: jsonb("words_to_ignore").$type<string[]>().default([]), // URL params/words to exclude
  applyTo: varchar("apply_to", { length: 20 }).notNull().default("tracked_url"), // "tracked_url" | "previous_url" | "both"
  // Advanced settings
  baseDomainOnly: boolean("base_domain_only").default(false),
  disregardSource: boolean("disregard_source").default(false),
  unqualifiedKeywords: jsonb("unqualified_keywords").$type<string[]>().default([]),
  dynamicSourceParam: varchar("dynamic_source_param", { length: 100 }), // e.g. "utm_source"
  dynamicCategoryParam: varchar("dynamic_category_param", { length: 100 }),
  enabled: boolean("enabled").default(true),
  priority: integer("priority").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTrackingUrlRuleSchema = createInsertSchema(trackingUrlRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type TrackingUrlRule = typeof trackingUrlRules.$inferSelect;
export type InsertTrackingUrlRule = z.infer<typeof insertTrackingUrlRuleSchema>;

// ─── Conversion Rules (Lead / Call / Sale rules — HYROS-style) ───
// "If lead has tag X → then send conversion Y" rules
export type ConversionRuleCategory = "lead" | "call" | "sale";
export type ConversionConditionType =
  | "has_tag"
  | "tag_prefix"
  | "has_product_tag"
  | "is_unqualified";
export type ConversionActionType = "send_with_params" | "send_by_product" | "do_not_send";

export const trackingConversionRules = pgTable("tracking_conversion_rules", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  category: varchar("category", { length: 20 }).notNull(), // "lead" | "call" | "sale"
  // Condition: "If..."
  conditionType: varchar("condition_type", { length: 30 }).notNull(), // "has_tag" | "tag_prefix" | "has_product_tag" | "is_unqualified"
  conditionValue: text("condition_value"), // tag name, prefix, etc.
  conditionTags: jsonb("condition_tags").$type<string[]>().default([]), // multiple tags to match
  // Action: "Then..."
  actionType: varchar("action_type", { length: 30 }).notNull(), // "send_with_params" | "send_by_product" | "do_not_send"
  actionEventName: varchar("action_event_name", { length: 100 }), // event name for conversion send
  actionPlatforms: jsonb("action_platforms").$type<string[]>().default([]), // ["meta", "google_ads", "tiktok"]
  enabled: boolean("enabled").default(true),
  priority: integer("priority").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTrackingConversionRuleSchema = createInsertSchema(trackingConversionRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type TrackingConversionRule = typeof trackingConversionRules.$inferSelect;
export type InsertTrackingConversionRule = z.infer<typeof insertTrackingConversionRuleSchema>;

// ─── Cloud Storage Integrations (Google Drive, Dropbox) ───
export const cloudStorageIntegrations = pgTable("cloud_storage_integrations", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  provider: varchar("provider", { length: 20 }).notNull(), // 'google_drive' | 'dropbox'
  providerEmail: varchar("provider_email", { length: 255 }),
  providerAccountId: varchar("provider_account_id", { length: 255 }),
  accessToken: text("access_token").notNull(), // encrypted via encryptToken()
  refreshToken: text("refresh_token"), // encrypted via encryptToken()
  tokenExpiresAt: timestamp("token_expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCloudStorageIntegrationSchema = createInsertSchema(
  cloudStorageIntegrations
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CloudStorageIntegration = typeof cloudStorageIntegrations.$inferSelect;
export type InsertCloudStorageIntegration = z.infer<typeof insertCloudStorageIntegrationSchema>;

// ─── Custom Dashboard Columns ───
// User-configurable columns for the campaign table
export interface CustomDashboardColumn {
  key: string; // unique column identifier
  label: string; // display name (PL)
  category: string; // grouping: "general" | "calls" | "lead_gen" | "ecommerce" | "subscriptions" | "custom"
  isCustom: boolean; // user-created metric
  visible: boolean; // currently shown in table
  order: number; // display order
  format?: "currency" | "number" | "percent" | "ratio"; // display format
  formula?: string; // for custom: e.g. "spend / leads" — evaluated client-side
}

export interface DashboardColumnConfig {
  columns: CustomDashboardColumn[];
  presetName?: string; // active preset name
}

// ── Video Review Comments ─────────────────────────────────

export const videoReviewComments = pgTable("video_review_comments", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  taskId: varchar("task_id").notNull(),
  userId: varchar("user_id").notNull(),
  authorName: text("author_name").notNull(),
  text: text("text").notNull(),
  timestampSeconds: numeric("timestamp_seconds"),
  timestampEnd: numeric("timestamp_end"),
  status: varchar("status", { length: 20 }).default("open").notNull(),
  parentId: varchar("parent_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertVideoReviewCommentSchema = createInsertSchema(videoReviewComments).omit({
  id: true,
  createdAt: true,
});

export type VideoReviewComment = typeof videoReviewComments.$inferSelect;
export type InsertVideoReviewComment = z.infer<typeof insertVideoReviewCommentSchema>;

// ─── Video Render Jobs ────────────────────────────────────────────────────────
export const videoRenderJobs = pgTable("video_render_jobs", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  type: varchar("type", { length: 50 }).notNull(),
  status: varchar("status", { length: 30 }).notNull().default("queued"),
  progress: integer("progress").notNull().default(0),
  script: jsonb("script"),
  outputPath: text("output_path"),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type VideoRenderJob = typeof videoRenderJobs.$inferSelect;
export type InsertVideoRenderJob = typeof videoRenderJobs.$inferInsert;

export const DEFAULT_CAMPAIGN_COLUMNS: CustomDashboardColumn[] = [
  {
    key: "leads",
    label: "Leady",
    category: "general",
    isCustom: false,
    visible: true,
    order: 0,
    format: "number",
  },
  {
    key: "spend",
    label: "Wydatek",
    category: "general",
    isCustom: false,
    visible: true,
    order: 1,
    format: "currency",
  },
  {
    key: "cpl",
    label: "Koszt/Lead",
    category: "general",
    isCustom: false,
    visible: true,
    order: 2,
    format: "currency",
  },
  {
    key: "cpc",
    label: "CPC",
    category: "general",
    isCustom: false,
    visible: true,
    order: 3,
    format: "currency",
  },
  {
    key: "cpm",
    label: "CPM",
    category: "general",
    isCustom: false,
    visible: true,
    order: 4,
    format: "currency",
  },
  {
    key: "ctr",
    label: "CTR",
    category: "general",
    isCustom: false,
    visible: true,
    order: 5,
    format: "percent",
  },
  {
    key: "roas",
    label: "ROAS",
    category: "general",
    isCustom: false,
    visible: true,
    order: 6,
    format: "ratio",
  },
  // General (hidden by default)
  {
    key: "impressions",
    label: "Wyświetlenia",
    category: "general",
    isCustom: false,
    visible: false,
    order: 7,
    format: "number",
  },
  {
    key: "clicks",
    label: "Kliki",
    category: "general",
    isCustom: false,
    visible: false,
    order: 8,
    format: "number",
  },
  {
    key: "reach",
    label: "Zasięg",
    category: "general",
    isCustom: false,
    visible: false,
    order: 9,
    format: "number",
  },
  {
    key: "frequency",
    label: "Częstotliwość",
    category: "general",
    isCustom: false,
    visible: false,
    order: 10,
    format: "ratio",
  },
  // Calls
  {
    key: "calls",
    label: "Rozmowy",
    category: "calls",
    isCustom: false,
    visible: false,
    order: 11,
    format: "number",
  },
  {
    key: "cost_per_call",
    label: "Koszt/Rozmowę",
    category: "calls",
    isCustom: false,
    visible: false,
    order: 12,
    format: "currency",
  },
  // Lead Gen
  {
    key: "new_leads",
    label: "Nowe Leady",
    category: "lead_gen",
    isCustom: false,
    visible: false,
    order: 13,
    format: "number",
  },
  {
    key: "cost_per_new_lead",
    label: "Koszt/Nowy Lead",
    category: "lead_gen",
    isCustom: false,
    visible: false,
    order: 14,
    format: "currency",
  },
  {
    key: "sales",
    label: "Sprzedaże",
    category: "lead_gen",
    isCustom: false,
    visible: false,
    order: 15,
    format: "number",
  },
  {
    key: "cost_per_sale",
    label: "Koszt/Sprzedaż",
    category: "lead_gen",
    isCustom: false,
    visible: false,
    order: 16,
    format: "currency",
  },
  // E-Commerce
  {
    key: "purchases",
    label: "Zakupy",
    category: "ecommerce",
    isCustom: false,
    visible: false,
    order: 17,
    format: "number",
  },
  {
    key: "purchase_value",
    label: "Wartość zakupów",
    category: "ecommerce",
    isCustom: false,
    visible: false,
    order: 18,
    format: "currency",
  },
  {
    key: "cost_per_purchase",
    label: "Koszt/Zakup",
    category: "ecommerce",
    isCustom: false,
    visible: false,
    order: 19,
    format: "currency",
  },
  {
    key: "aov",
    label: "AOV",
    category: "ecommerce",
    isCustom: false,
    visible: false,
    order: 20,
    format: "currency",
  },
  {
    key: "net_profit",
    label: "Zysk netto",
    category: "ecommerce",
    isCustom: false,
    visible: false,
    order: 21,
    format: "currency",
  },
  // Subscriptions
  {
    key: "trials",
    label: "Triale",
    category: "subscriptions",
    isCustom: false,
    visible: false,
    order: 22,
    format: "number",
  },
  {
    key: "subscriptions",
    label: "Subskrypcje",
    category: "subscriptions",
    isCustom: false,
    visible: false,
    order: 23,
    format: "number",
  },
  {
    key: "cost_per_trial",
    label: "Koszt/Trial",
    category: "subscriptions",
    isCustom: false,
    visible: false,
    order: 24,
    format: "currency",
  },
  {
    key: "mrr",
    label: "MRR",
    category: "subscriptions",
    isCustom: false,
    visible: false,
    order: 25,
    format: "currency",
  },
];

// Campaign Creator — AI targeting variants (1/3/5 propozycji z różnymi hipotezami)
export const TargetingVariantCountSchema = z.union([z.literal(1), z.literal(3), z.literal(5)]);
export type TargetingVariantCount = z.infer<typeof TargetingVariantCountSchema>;

export const TargetingInterestSchema = z.object({
  id: z.string(),
  name: z.string(),
  audienceSize: z.number().optional(),
});

export const TargetingVariantSchema = z.object({
  id: z.string(),
  label: z.string(),
  countries: z.array(z.string()),
  ageMin: z.number(),
  ageMax: z.number(),
  genders: z.string(),
  // Meta locale **keys** (e.g. "pl_PL"). Numeric IDs are resolved server-side
  // against Meta's live /search?type=adlocale catalogue — see
  // shared/meta/ad-locales.ts.
  locales: z.array(z.string()),
  interests: z.array(TargetingInterestSchema),
  reasoning: z.string(),
});
export type TargetingVariant = z.infer<typeof TargetingVariantSchema>;

export const LaunchTargetingVariantSchema = z.object({
  label: z.string().min(1).max(80),
  targeting: z.record(z.any()),
});
export type LaunchTargetingVariant = z.infer<typeof LaunchTargetingVariantSchema>;

// ── Site Hosting (custom-domain static sites) ───────────────
// Userzy uploadują ZIP/HTML, hostujemy ich strony pod ich własnymi domenami.
// Custom domains tylko (na start) — bez subdomeny *.viral.partners.
export const hostedSites = pgTable("hosted_sites", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  // Wix-style subdomain on platform root, e.g. "moja-strona" → moja-strona.sites.viral.partners
  // Unique platform-wide. Lower-case, RFC 1035 label rules, max 63 chars.
  subdomain: varchar("subdomain", { length: 63 }).unique(),
  // 'active' | 'suspended' | 'deleted'
  status: varchar("status", { length: 20 }).notNull().default("active"),
  // FK to hostedSiteVersions; soft-link (not enforced) to allow rollback / null on first create
  currentVersionId: varchar("current_version_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const hostedSiteVersions = pgTable("hosted_site_versions", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  siteId: varchar("site_id")
    .notNull()
    .references(() => hostedSites.id, { onDelete: "cascade" }),
  versionNumber: integer("version_number").notNull(),
  // Storage prefix in Supabase Storage bucket, e.g. "sites/{siteId}/v3/"
  storagePrefix: text("storage_prefix").notNull(),
  indexFile: text("index_file").notNull().default("index.html"),
  // SPA fallback: if true, edge serves indexFile on 404
  isSpa: boolean("is_spa").default(false).notNull(),
  sizeBytes: integer("size_bytes").notNull(),
  fileCount: integer("file_count").notNull(),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
});

export const hostedSiteDomains = pgTable("hosted_site_domains", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  siteId: varchar("site_id")
    .notNull()
    .references(() => hostedSites.id, { onDelete: "cascade" }),
  domain: text("domain").notNull().unique(),
  verificationToken: text("verification_token").notNull(),
  // 'pending' | 'verified' | 'active' | 'failed'
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  verifiedAt: timestamp("verified_at"),
  lastCheckedAt: timestamp("last_checked_at"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Media uploaded by user for use inside hosted site HTML (images, video, fonts).
// Stored on R2; r2Key is the canonical reference. Served publicly via /api/sites-public/:siteId/asset/:id
// so HTML pages embedded with absolute URLs work both in app preview and on the live custom domain.
export const hostedSiteAssets = pgTable("hosted_site_assets", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  siteId: varchar("site_id")
    .notNull()
    .references(() => hostedSites.id, { onDelete: "cascade" }),
  r2Key: text("r2_key").notNull(),
  fileName: text("file_name").notNull(),
  mimeType: text("mime_type").notNull(),
  sizeBytes: integer("size_bytes").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// When a user regenerates their site URL we keep the old subdomain alive for
// SUBDOMAIN_ALIAS_TTL_DAYS days as a 301 redirect to the current URL. Stops
// links / QR codes / printed business cards going dead instantly.
export const hostedSiteSubdomainAliases = pgTable("hosted_site_subdomain_aliases", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  siteId: varchar("site_id")
    .notNull()
    .references(() => hostedSites.id, { onDelete: "cascade" }),
  // Unique platform-wide so a freed alias can't collide with an active subdomain.
  aliasSubdomain: varchar("alias_subdomain", { length: 63 }).notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertHostedSiteSchema = createInsertSchema(hostedSites).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  currentVersionId: true,
});
export const insertHostedSiteAliasSchema = createInsertSchema(hostedSiteSubdomainAliases).omit({
  id: true,
  createdAt: true,
});
export const insertHostedSiteVersionSchema = createInsertSchema(hostedSiteVersions).omit({
  id: true,
  uploadedAt: true,
});
export const insertHostedSiteDomainSchema = createInsertSchema(hostedSiteDomains).omit({
  id: true,
  createdAt: true,
  verifiedAt: true,
  lastCheckedAt: true,
  lastError: true,
});
export const insertHostedSiteAssetSchema = createInsertSchema(hostedSiteAssets).omit({
  id: true,
  createdAt: true,
});

export type HostedSite = typeof hostedSites.$inferSelect;
export type InsertHostedSite = z.infer<typeof insertHostedSiteSchema>;
export type HostedSiteVersion = typeof hostedSiteVersions.$inferSelect;
export type InsertHostedSiteVersion = z.infer<typeof insertHostedSiteVersionSchema>;
export type HostedSiteDomain = typeof hostedSiteDomains.$inferSelect;
export type InsertHostedSiteDomain = z.infer<typeof insertHostedSiteDomainSchema>;
export type HostedSiteAsset = typeof hostedSiteAssets.$inferSelect;
export type InsertHostedSiteAsset = z.infer<typeof insertHostedSiteAssetSchema>;
export type HostedSiteSubdomainAlias = typeof hostedSiteSubdomainAliases.$inferSelect;
export type InsertHostedSiteSubdomainAlias = z.infer<typeof insertHostedSiteAliasSchema>;
export type HostedSiteStatus = "active" | "suspended" | "deleted";
export type HostedDomainStatus = "pending" | "verified" | "active" | "failed";

// ── SEO Blog Module ───────────────────────────────────────────────────────────

export const seoBlogKeywords = pgTable("seo_blog_keywords", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  keyword: text("keyword").notNull(),
  notes: text("notes"),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const seoBlogDrafts = pgTable("seo_blog_drafts", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  keywordId: varchar("keyword_id").references(() => seoBlogKeywords.id, { onDelete: "set null" }),
  keyword: text("keyword").notNull(),
  title: text("title").notNull(),
  metaDescription: text("meta_description"),
  slug: text("slug"),
  content: text("content").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  ghlBlogId: varchar("ghl_blog_id"),
  ghlPostId: varchar("ghl_post_id"),
  ghlPublishedAt: timestamp("ghl_published_at"),
  publishError: text("publish_error"),
  generationModel: varchar("generation_model"),
  tokensUsed: integer("tokens_used"),
  costUsd: numeric("cost_usd"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSeoBlogKeywordSchema = createInsertSchema(seoBlogKeywords).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertSeoBlogDraftSchema = createInsertSchema(seoBlogDrafts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type SeoBlogKeyword = typeof seoBlogKeywords.$inferSelect;
export type InsertSeoBlogKeyword = z.infer<typeof insertSeoBlogKeywordSchema>;
export type SeoBlogDraft = typeof seoBlogDrafts.$inferSelect;
export type InsertSeoBlogDraft = z.infer<typeof insertSeoBlogDraftSchema>;

// ── Cancellation feedback (subscription cancel/retention flow) ──
// Outcome: 'kept' (closed modal), 'accepted_discount', 'accepted_pause', 'cancelled'
export const cancellationFeedback = pgTable("cancellation_feedback", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  reasons: jsonb("reasons").$type<string[]>().default([]).notNull(),
  freeText: text("free_text"),
  outcome: varchar("outcome", { length: 32 }).notNull(),
  planAtCancel: text("plan_at_cancel"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertCancellationFeedbackSchema = createInsertSchema(cancellationFeedback).omit({
  id: true,
  createdAt: true,
});
export type CancellationFeedback = typeof cancellationFeedback.$inferSelect;
export type InsertCancellationFeedback = z.infer<typeof insertCancellationFeedbackSchema>;
export type SeoBlogDraftStatus = "draft" | "ready" | "published" | "failed";

// ─────────────────────────────────────────────────────────────────────────────
// Scaling Up Cockpit — internal management module (super_admin + holding board)
// Methodology: Verne Harnish, Rockefeller Habits 2.0
// Multi-tenant: holdings → companies → memberships (per-company role)
// See: SCALING_UP_COCKPIT_PLAN.md
// ─────────────────────────────────────────────────────────────────────────────

export const holdings = pgTable("holdings", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: varchar("slug", { length: 64 }).notNull().unique(),
  ownerUserId: varchar("owner_user_id")
    .notNull()
    .references(() => users.id, { onDelete: "restrict" }),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const holdingCompanies = pgTable(
  "holding_companies",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    holdingId: varchar("holding_id")
      .notNull()
      .references(() => holdings.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    slug: varchar("slug", { length: 64 }).notNull(),
    sector: varchar("sector", { length: 64 }),
    description: text("description"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    holdingSlugIdx: uniqueIndex("holding_companies_holding_slug_idx").on(
      table.holdingId,
      table.slug
    ),
  })
);

export const holdingMemberships = pgTable(
  "holding_memberships",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    holdingId: varchar("holding_id")
      .notNull()
      .references(() => holdings.id, { onDelete: "cascade" }),
    companyId: varchar("company_id").references(() => holdingCompanies.id, { onDelete: "cascade" }),
    role: varchar("role", { length: 32 }).notNull(),
    department: varchar("department", { length: 64 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    userHoldingCompanyIdx: uniqueIndex("holding_memberships_user_holding_company_idx").on(
      table.userId,
      table.holdingId,
      table.companyId
    ),
    holdingIdx: index("holding_memberships_holding_idx").on(table.holdingId),
    userIdx: index("holding_memberships_user_idx").on(table.userId),
  })
);

export const insertHoldingSchema = createInsertSchema(holdings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertHoldingCompanySchema = createInsertSchema(holdingCompanies).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertHoldingMembershipSchema = createInsertSchema(holdingMemberships).omit({
  id: true,
  createdAt: true,
});
export type Holding = typeof holdings.$inferSelect;
export type InsertHolding = z.infer<typeof insertHoldingSchema>;
export type HoldingCompany = typeof holdingCompanies.$inferSelect;
export type InsertHoldingCompany = z.infer<typeof insertHoldingCompanySchema>;
export type HoldingMembership = typeof holdingMemberships.$inferSelect;
export type InsertHoldingMembership = z.infer<typeof insertHoldingMembershipSchema>;
export type HoldingMembershipRole = "holding_owner" | "board_member" | "executive" | "team_member";

export const scalingUpVision = pgTable(
  "scaling_up_vision",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    holdingId: varchar("holding_id")
      .notNull()
      .references(() => holdings.id, { onDelete: "cascade" }),
    companyId: varchar("company_id").references(() => holdingCompanies.id, { onDelete: "cascade" }),
    coreValues: jsonb("core_values").$type<string[]>().default([]).notNull(),
    corePurpose: text("core_purpose"),
    bhag: text("bhag"),
    threeYearTargets: jsonb("three_year_targets")
      .$type<Record<string, unknown>>()
      .default({})
      .notNull(),
    oneYearGoals: jsonb("one_year_goals").$type<Record<string, unknown>>().default({}).notNull(),
    brandPromises: jsonb("brand_promises").$type<string[]>().default([]).notNull(),
    sevenStrata: jsonb("seven_strata").$type<Record<string, unknown>>().default({}).notNull(),
    revisedAt: timestamp("revised_at").defaultNow().notNull(),
    revisedByUserId: varchar("revised_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    scopeIdx: uniqueIndex("scaling_up_vision_scope_idx").on(table.holdingId, table.companyId),
  })
);

export const scalingUpPriorities = pgTable(
  "scaling_up_priorities",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    companyId: varchar("company_id")
      .notNull()
      .references(() => holdingCompanies.id, { onDelete: "cascade" }),
    period: varchar("period", { length: 16 }).notNull(),
    quarterStart: date("quarter_start").notNull(),
    title: text("title").notNull(),
    description: text("description"),
    ownerUserId: varchar("owner_user_id").references(() => users.id, { onDelete: "set null" }),
    status: varchar("status", { length: 16 }).notNull().default("on_track"),
    criticalNumber: boolean("critical_number").default(false).notNull(),
    kpiTargets: jsonb("kpi_targets").$type<Record<string, number>>().default({}).notNull(),
    kpiActuals: jsonb("kpi_actuals").$type<Record<string, number>>().default({}).notNull(),
    parentPriorityId: varchar("parent_priority_id"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    companyQuarterIdx: index("scaling_up_priorities_company_quarter_idx").on(
      table.companyId,
      table.quarterStart
    ),
    ownerIdx: index("scaling_up_priorities_owner_idx").on(table.ownerUserId),
  })
);

export const scalingUpKpis = pgTable(
  "scaling_up_kpis",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    holdingId: varchar("holding_id")
      .notNull()
      .references(() => holdings.id, { onDelete: "cascade" }),
    companyId: varchar("company_id").references(() => holdingCompanies.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    description: text("description"),
    ownerUserId: varchar("owner_user_id").references(() => users.id, { onDelete: "set null" }),
    frequency: varchar("frequency", { length: 16 }).notNull(),
    unit: varchar("unit", { length: 32 }),
    target: numeric("target"),
    redThreshold: numeric("red_threshold"),
    yellowThreshold: numeric("yellow_threshold"),
    higherIsBetter: boolean("higher_is_better").default(true).notNull(),
    currentValue: numeric("current_value"),
    currentStatus: varchar("current_status", { length: 8 }),
    lastUpdatedAt: timestamp("last_updated_at"),
    dataSource: varchar("data_source", { length: 32 }).notNull().default("manual"),
    sourceConfig: jsonb("source_config").$type<Record<string, unknown>>().default({}).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    scopeIdx: index("scaling_up_kpis_scope_idx").on(table.holdingId, table.companyId),
    ownerIdx: index("scaling_up_kpis_owner_idx").on(table.ownerUserId),
  })
);

export const scalingUpKpiHistory = pgTable(
  "scaling_up_kpi_history",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    kpiId: varchar("kpi_id")
      .notNull()
      .references(() => scalingUpKpis.id, { onDelete: "cascade" }),
    value: numeric("value"),
    capturedAt: timestamp("captured_at").defaultNow().notNull(),
    status: varchar("status", { length: 8 }),
    sourceStatus: varchar("source_status", { length: 16 }).notNull().default("fresh"),
    errorMessage: text("error_message"),
  },
  (table) => ({
    kpiCapturedIdx: index("scaling_up_kpi_history_kpi_captured_idx").on(
      table.kpiId,
      table.capturedAt
    ),
  })
);

export const scalingUpMeetings = pgTable(
  "scaling_up_meetings",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    companyId: varchar("company_id")
      .notNull()
      .references(() => holdingCompanies.id, { onDelete: "cascade" }),
    type: varchar("type", { length: 16 }).notNull(),
    scheduledAt: timestamp("scheduled_at"),
    startedAt: timestamp("started_at"),
    endedAt: timestamp("ended_at"),
    attendees: jsonb("attendees").$type<string[]>().default([]).notNull(),
    agenda: jsonb("agenda").$type<Record<string, unknown>>().default({}).notNull(),
    notes: text("notes"),
    aiSummary: text("ai_summary"),
    aiSummaryAt: timestamp("ai_summary_at"),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    companyTypeIdx: index("scaling_up_meetings_company_type_idx").on(table.companyId, table.type),
    scheduledIdx: index("scaling_up_meetings_scheduled_idx").on(table.scheduledAt),
  })
);

export const scalingUpActionItems = pgTable(
  "scaling_up_action_items",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    companyId: varchar("company_id")
      .notNull()
      .references(() => holdingCompanies.id, { onDelete: "cascade" }),
    meetingId: varchar("meeting_id").references(() => scalingUpMeetings.id, {
      onDelete: "set null",
    }),
    description: text("description").notNull(),
    ownerUserId: varchar("owner_user_id").references(() => users.id, { onDelete: "set null" }),
    dueDate: date("due_date"),
    status: varchar("status", { length: 16 }).notNull().default("open"),
    resolutionNote: text("resolution_note"),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    completedAt: timestamp("completed_at"),
  },
  (table) => ({
    companyStatusIdx: index("scaling_up_action_items_company_status_idx").on(
      table.companyId,
      table.status
    ),
    ownerIdx: index("scaling_up_action_items_owner_idx").on(table.ownerUserId),
    dueIdx: index("scaling_up_action_items_due_idx").on(table.dueDate),
  })
);

export const scalingUpIssues = pgTable(
  "scaling_up_issues",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    companyId: varchar("company_id")
      .notNull()
      .references(() => holdingCompanies.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    description: text("description"),
    raisedByUserId: varchar("raised_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    raisedAt: timestamp("raised_at").defaultNow().notNull(),
    severity: varchar("severity", { length: 16 }).notNull().default("medium"),
    status: varchar("status", { length: 16 }).notNull().default("open"),
    resolution: text("resolution"),
    resolvedAt: timestamp("resolved_at"),
    resolvedByUserId: varchar("resolved_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
  },
  (table) => ({
    companyStatusIdx: index("scaling_up_issues_company_status_idx").on(
      table.companyId,
      table.status
    ),
  })
);

export const scalingUpPeople = pgTable(
  "scaling_up_people",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    companyId: varchar("company_id")
      .notNull()
      .references(() => holdingCompanies.id, { onDelete: "cascade" }),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    period: date("period").notNull(),
    tier: varchar("tier", { length: 1 }).notNull(),
    notes: text("notes"),
    assessedByUserId: varchar("assessed_by_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "restrict" }),
    assessedAt: timestamp("assessed_at").defaultNow().notNull(),
  },
  (table) => ({
    companyUserPeriodIdx: uniqueIndex("scaling_up_people_company_user_period_idx").on(
      table.companyId,
      table.userId,
      table.period
    ),
  })
);

export const scalingUpAuditLog = pgTable(
  "scaling_up_audit_log",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    actorUserId: varchar("actor_user_id").references(() => users.id, { onDelete: "set null" }),
    scope: varchar("scope", { length: 64 }).notNull(),
    entityType: varchar("entity_type", { length: 64 }).notNull(),
    entityId: varchar("entity_id"),
    action: varchar("action", { length: 32 }).notNull(),
    beforeState: jsonb("before_state").$type<Record<string, unknown>>(),
    afterState: jsonb("after_state").$type<Record<string, unknown>>(),
    occurredAt: timestamp("occurred_at").defaultNow().notNull(),
  },
  (table) => ({
    scopeOccurredIdx: index("scaling_up_audit_log_scope_occurred_idx").on(
      table.scope,
      table.occurredAt
    ),
    entityIdx: index("scaling_up_audit_log_entity_idx").on(table.entityType, table.entityId),
  })
);

export const insertScalingUpVisionSchema = createInsertSchema(scalingUpVision).omit({
  id: true,
  createdAt: true,
  revisedAt: true,
});
export const insertScalingUpPrioritySchema = createInsertSchema(scalingUpPriorities).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertScalingUpKpiSchema = createInsertSchema(scalingUpKpis).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertScalingUpKpiHistorySchema = createInsertSchema(scalingUpKpiHistory).omit({
  id: true,
  capturedAt: true,
});
export const insertScalingUpMeetingSchema = createInsertSchema(scalingUpMeetings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertScalingUpActionItemSchema = createInsertSchema(scalingUpActionItems).omit({
  id: true,
  createdAt: true,
});
export const insertScalingUpIssueSchema = createInsertSchema(scalingUpIssues).omit({
  id: true,
  raisedAt: true,
});
export const insertScalingUpPersonSchema = createInsertSchema(scalingUpPeople).omit({
  id: true,
  assessedAt: true,
});
export const insertScalingUpAuditLogSchema = createInsertSchema(scalingUpAuditLog).omit({
  id: true,
  occurredAt: true,
});

export type ScalingUpVision = typeof scalingUpVision.$inferSelect;
export type InsertScalingUpVision = z.infer<typeof insertScalingUpVisionSchema>;
export type ScalingUpPriority = typeof scalingUpPriorities.$inferSelect;
export type InsertScalingUpPriority = z.infer<typeof insertScalingUpPrioritySchema>;
export type ScalingUpKpi = typeof scalingUpKpis.$inferSelect;
export type InsertScalingUpKpi = z.infer<typeof insertScalingUpKpiSchema>;
export type ScalingUpKpiHistory = typeof scalingUpKpiHistory.$inferSelect;
export type InsertScalingUpKpiHistory = z.infer<typeof insertScalingUpKpiHistorySchema>;
export type ScalingUpMeeting = typeof scalingUpMeetings.$inferSelect;
export type InsertScalingUpMeeting = z.infer<typeof insertScalingUpMeetingSchema>;
export type ScalingUpActionItem = typeof scalingUpActionItems.$inferSelect;
export type InsertScalingUpActionItem = z.infer<typeof insertScalingUpActionItemSchema>;
export type ScalingUpIssue = typeof scalingUpIssues.$inferSelect;
export type InsertScalingUpIssue = z.infer<typeof insertScalingUpIssueSchema>;
export type ScalingUpPerson = typeof scalingUpPeople.$inferSelect;
export type InsertScalingUpPerson = z.infer<typeof insertScalingUpPersonSchema>;
export type ScalingUpAuditLog = typeof scalingUpAuditLog.$inferSelect;
export type InsertScalingUpAuditLog = z.infer<typeof insertScalingUpAuditLogSchema>;

export type ScalingUpKpiStatus = "red" | "yellow" | "green";
export type ScalingUpKpiSourceStatus = "fresh" | "stale" | "failed";
export type ScalingUpPriorityStatus = "on_track" | "at_risk" | "off_track" | "done";
export type ScalingUpPriorityPeriod = "quarterly" | "annual";
export type ScalingUpKpiFrequency = "daily" | "weekly" | "monthly" | "quarterly";
export type ScalingUpKpiDataSource =
  | "manual"
  | "auto:internal"
  | "auto:stripe"
  | "auto:llm-cost"
  | "auto:meta-ads"
  | "auto:ga4"
  | "auto:powerbi";
export type ScalingUpMeetingType = "daily" | "weekly" | "monthly" | "quarterly" | "annual";
export type ScalingUpActionItemStatus = "open" | "done" | "overdue" | "cancelled";
export type ScalingUpIssueSeverity = "low" | "medium" | "high" | "critical";
export type ScalingUpIssueStatus = "open" | "in_progress" | "resolved";
export type ScalingUpPersonTier = "A" | "B" | "C";

// =============================================================================
// AUTOPILOT REKLAMOWY — schema scaffold (12 tabel)
//
// Plan: docs/autopilot-ads-plan.md (v3 + addendum 2026-05-10)
// Methodology: docs/methodology/{theriot-ecom-2026.md, haynes-leadgen-callfunnel-2026.md}
//
// Po edycji TEGO BLOKU uruchom: `npx drizzle-kit generate`
// (CLAUDE.md zakazuje ręcznego SQL w migrations/)
// =============================================================================

export type AutopilotFunnelType =
  | "lead_gen"
  | "ecom"
  | "trial_saas"
  | "book_call"
  | "direct_sale"
  | "observer";

export type AutopilotMode = "observer" | "conservative" | "balanced" | "aggressive";
export type AutopilotStatus = "draft" | "active" | "paused" | "vacation" | "stopped";
export type AutopilotTamFactor = "small" | "large";

// 1) AUTOPILOT_SESSIONS — jeden klient × jeden funnel × jedna agencja
export const autopilotSessions = pgTable(
  "autopilot_sessions",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    agencyClientId: varchar("agency_client_id"),
    funnelType: varchar("funnel_type", { length: 20 }).notNull(),
    industry: text("industry"),
    market: varchar("market", { length: 8 }),
    primaryKpi: jsonb("primary_kpi").notNull(),
    secondaryKpis: jsonb("secondary_kpis"),
    hardDeck: numeric("hard_deck"),
    dailyBudgetMax: numeric("daily_budget_max"),
    monthlyBudgetMax: numeric("monthly_budget_max"),
    costCap: numeric("cost_cap"),
    tamFactor: varchar("tam_factor", { length: 8 }).default("large"),
    mode: varchar("mode", { length: 20 }).default("observer").notNull(),
    status: varchar("status", { length: 20 }).default("draft").notNull(),
    metaAdAccountId: text("meta_ad_account_id"),
    ghlLocationId: text("ghl_location_id"),
    calendlyEventId: text("calendly_event_id"),
    config: jsonb("config"),
    inventoryGateEnabled: boolean("inventory_gate_enabled").default(false),
    monthlyInfraCostCap: numeric("monthly_infra_cost_cap"),
    // Limity wolumenu testowanych kreacji — dyscyplina Theriot/Haynes.
    // weeklyTestVolume i monthlyTestVolume są uważane przez BatchManager
    // — jeśli cap przekroczony, autopilot nie tworzy nowych batchy aż do nast. cyklu.
    weeklyTestVolume: integer("weekly_test_volume"),
    monthlyTestVolume: integer("monthly_test_volume"),
    // Granular permissions per akcja: 'auto' | 'require_approval' | 'disabled'.
    // Klucze: scaling_up, scaling_down, killing_ads, creating_new_ads, ai_generation,
    // publishing_to_meta, budget_changes, audience_changes, creative_rotation.
    // Brak klucza → default = wartość zależna od session.mode (observer→all require_approval).
    actionPermissions: jsonb("action_permissions"),
    startedAt: timestamp("started_at"),
    lastDecisionAt: timestamp("last_decision_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    byUser: index("autopilot_sessions_user_idx").on(t.userId),
    byStatus: index("autopilot_sessions_status_idx").on(t.status),
    byFunnel: index("autopilot_sessions_funnel_idx").on(t.funnelType),
  })
);

// 2) AUTOPILOT_DECISIONS — immutable audit log
export type AutopilotLayer =
  | "L1_meta_rule"
  | "L2_engine"
  | "L3_playbook"
  | "L4_pixel"
  | "L5_creative"
  | "L6_funnel";

export type AutopilotDecisionSource = "rule" | "ai_override" | "human" | "meta_webhook";

export const autopilotDecisions = pgTable(
  "autopilot_decisions",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    decidedAt: timestamp("decided_at").defaultNow().notNull(),
    layer: varchar("layer", { length: 20 }).notNull(),
    action: text("action").notNull(),
    targetType: varchar("target_type", { length: 20 }),
    targetId: text("target_id"),
    before: jsonb("before"),
    after: jsonb("after"),
    reasoning: text("reasoning"),
    metricsSnapshot: jsonb("metrics_snapshot"),
    source: varchar("source", { length: 20 }).notNull(),
    applied: boolean("applied").default(false),
    appliedAt: timestamp("applied_at"),
    reverted: boolean("reverted").default(false),
    revertedAt: timestamp("reverted_at"),
    revertReason: text("revert_reason"),
  },
  (t) => ({
    bySession: index("autopilot_decisions_session_idx").on(t.sessionId, t.decidedAt),
    byAction: index("autopilot_decisions_action_idx").on(t.action),
  })
);

// 3) KPI_SNAPSHOTS
export const kpiSnapshots = pgTable(
  "kpi_snapshots",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    targetType: varchar("target_type", { length: 20 }).notNull(),
    targetId: text("target_id").notNull(),
    capturedAt: timestamp("captured_at").defaultNow().notNull(),
    attributionWindow: varchar("attribution_window", { length: 24 }).default("7d_click_1d_view"),
    spend: numeric("spend"),
    impressions: integer("impressions"),
    clicks: integer("clicks"),
    conversions: integer("conversions"),
    conversionValue: numeric("conversion_value"),
    ncpa: numeric("ncpa"),
    cpa: numeric("cpa"),
    cpl: numeric("cpl"),
    roas: numeric("roas"),
    ctr: numeric("ctr"),
    cpm: numeric("cpm"),
    frequency: numeric("frequency"),
    funnelMetrics: jsonb("funnel_metrics"),
    pixelMatchScore: numeric("pixel_match_score"),
    optimizationEventVolume7d: integer("optimization_event_volume_7d"),
    leadQualityAvg: numeric("lead_quality_avg"),
  },
  (t) => ({
    byTarget: index("kpi_snapshots_target_idx").on(
      t.sessionId,
      t.targetType,
      t.targetId,
      t.capturedAt
    ),
    byCaptured: index("kpi_snapshots_captured_idx").on(t.capturedAt),
  })
);

// 4) CREATIVE_IDEA_BATCHES — Theriot Batch 101+ schema
export type CreativeBatchType = "iteration" | "variation" | "new_messaging";
export type CreativeBatchResult = "pending" | "winner" | "dud" | "catastrophic";

export const creativeIdeaBatches = pgTable(
  "creative_idea_batches",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    batchNumber: integer("batch_number").notNull(),
    type: varchar("type", { length: 20 }).notNull(),
    parentWinnerId: varchar("parent_winner_id"),
    hypothesis: text("hypothesis"),
    result: varchar("result", { length: 20 }).default("pending"),
    learnings: text("learnings"),
    adsPerBatch: integer("ads_per_batch").default(3).notNull(),
    awarenessStage: varchar("awareness_stage", { length: 20 }),
    createdAt: timestamp("created_at").defaultNow(),
    evaluatedAt: timestamp("evaluated_at"),
  },
  (t) => ({
    bySession: index("creative_batches_session_idx").on(t.sessionId, t.batchNumber),
    uniqBatchPerSession: uniqueIndex("creative_batches_uniq_idx").on(t.sessionId, t.batchNumber),
  })
);

// 5) CREATIVE_ASSETS
export type CreativeAssetType = "image" | "video" | "carousel" | "ugc" | "static_reel";
export type CreativeAssetSource =
  | "uploaded"
  | "heygen"
  | "runway"
  | "fal"
  | "manual"
  | "ai_pixar"
  | "ugc_creator";
export type CreativeAssetStatus =
  | "on_deck"
  | "active"
  | "foundational_winner"
  | "fatigued"
  | "archived"
  | "catastrophic"
  | "pending_review"
  | "generating";

export const creativeAssets = pgTable(
  "creative_assets",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    batchId: varchar("batch_id"),
    sessionId: varchar("session_id").notNull(),
    type: varchar("type", { length: 20 }).notNull(),
    source: varchar("source", { length: 20 }).notNull(),
    assetUrl: text("asset_url"),
    thumbnailUrl: text("thumbnail_url"),
    awarenessStage: varchar("awareness_stage", { length: 20 }),
    hook: text("hook"),
    bodyCopy: text("body_copy"),
    headline: text("headline"),
    styleTier: varchar("style_tier", { length: 8 }),
    tags: text("tags").array(),
    performance: jsonb("performance"),
    baselineMetrics: jsonb("baseline_metrics"),
    fatigueScore: numeric("fatigue_score"),
    status: varchar("status", { length: 24 }).default("pending_review").notNull(),
    uniqueUrlPath: text("unique_url_path"),
    complianceChecked: boolean("compliance_checked").default(false),
    complianceFlags: jsonb("compliance_flags"),
    generationMeta: jsonb("generation_meta"),
    metaAdId: text("meta_ad_id"),
    createdAt: timestamp("created_at").defaultNow(),
    archivedAt: timestamp("archived_at"),
  },
  (t) => ({
    bySession: index("creative_assets_session_idx").on(t.sessionId, t.status),
    byBatch: index("creative_assets_batch_idx").on(t.batchId),
    byMetaAd: index("creative_assets_metaad_idx").on(t.metaAdId),
  })
);

// 6) FOUNDATIONAL_ADSETS — Haynes lifecycle
export type FoundationalAdsetStatus =
  | "testing"
  | "scaled_winner"
  | "foundational_frozen"
  | "duplicating";

export const foundationalAdsets = pgTable(
  "foundational_adsets",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    adsetId: text("adset_id").notNull(),
    campaignId: text("campaign_id"),
    status: varchar("status", { length: 24 }).default("testing").notNull(),
    ceilingBudget: numeric("ceiling_budget"),
    currentBudget: numeric("current_budget"),
    parentAdsetId: text("parent_adset_id"),
    parentAdsetUuid: varchar("parent_adset_uuid"),
    activeWinnerCreativeIds: text("active_winner_creative_ids").array(),
    onDeckCreativeIds: text("on_deck_creative_ids").array(),
    winnerCount: integer("winner_count").default(0),
    costPerResult: numeric("cost_per_result"),
    audienceQuality: varchar("audience_quality", { length: 20 }),
    audienceType: varchar("audience_type", { length: 20 }),
    uniqueUrlPath: text("unique_url_path"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    bySession: index("foundational_adsets_session_idx").on(t.sessionId, t.status),
    byMetaAdset: index("foundational_adsets_meta_idx").on(t.adsetId),
  })
);

// 7) CONTENT_CYCLE_BINS — The Forester
export type ContentCycleBinType = "cold" | "warm_post_3sec";
export type ContentCycleBinStatus = "seeding" | "feeding_dr" | "exhausted";

export const contentCycleBins = pgTable(
  "content_cycle_bins",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    binType: varchar("bin_type", { length: 24 }).notNull(),
    campaignId: text("campaign_id"),
    contentCount: integer("content_count").default(0),
    exclusionAudienceId: text("exclusion_audience_id"),
    retargetableAudienceId: text("retargetable_audience_id"),
    status: varchar("status", { length: 24 }).default("seeding").notNull(),
    costPer3sView: numeric("cost_per_3s_view"),
    audienceSize: integer("audience_size"),
    step2LaunchedAt: timestamp("step2_launched_at"),
    cpmLiftMeasured: numeric("cpm_lift_measured"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    bySession: index("content_cycle_bins_session_idx").on(t.sessionId, t.status),
  })
);

// 8) HAMMER_THEM_QUEUES — post-booking nurture + 30-day retention state machine
export type HammerThemTrigger = "book_call" | "optin" | "low_ticket_purchase" | "event_signup";
export type HammerThemOutcome =
  | "pending"
  | "showed"
  | "no_show"
  | "closed"
  | "refunded"
  | "expired";
export type RetentionStage =
  | "d0_3_intangibles"
  | "d3_7_tangible"
  | "d7_21_habitualization"
  | "d21_30_final_nine"
  | "completed";

export const hammerThemQueues = pgTable(
  "hammer_them_queues",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    contactId: text("contact_id").notNull(),
    contactEmail: text("contact_email"),
    contactPhone: text("contact_phone"),
    triggeredBy: varchar("triggered_by", { length: 24 }).notNull(),
    triggerAt: timestamp("trigger_at").defaultNow().notNull(),
    targetEventAt: timestamp("target_event_at"),
    videosQueued: jsonb("videos_queued"),
    emailsQueued: jsonb("emails_queued"),
    deliveryProgress: jsonb("delivery_progress"),
    outcome: varchar("outcome", { length: 16 }).default("pending").notNull(),
    showRateUplift: numeric("show_rate_uplift"),
    retentionStage: varchar("retention_stage", { length: 32 }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    bySession: index("hammer_them_session_idx").on(t.sessionId, t.outcome),
    byContact: index("hammer_them_contact_idx").on(t.contactId),
    byTarget: index("hammer_them_target_idx").on(t.targetEventAt),
  })
);

// 9) PIXEL_CONDITIONING_STATE — Haynes critical subsystem
export type PixelConditioningStateType = "healthy" | "data_starved" | "wrong_pocket" | "recovery";

export const pixelConditioningState = pgTable(
  "pixel_conditioning_state",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull().unique(),
    optimizationEvent: text("optimization_event"),
    weeklyVolume: integer("weekly_volume"),
    threshold: integer("threshold"),
    matchScore: numeric("match_score"),
    state: varchar("state", { length: 20 }).default("healthy").notNull(),
    lastRelaunchAt: timestamp("last_relaunch_at"),
    conditionalFireRules: jsonb("conditional_fire_rules"),
    enabledEvents: text("enabled_events").array(),
    qualityFilterRate: numeric("quality_filter_rate"),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    bySession: index("pixel_conditioning_session_idx").on(t.sessionId),
  })
);

// 10) CONFIRMATION_PAGES — Haynes BookCall (Devon +30%)
export type ConfirmationPageType = "due_diligence" | "urgency";

export const confirmationPages = pgTable(
  "confirmation_pages",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    type: varchar("type", { length: 24 }).notNull(),
    frontVideoUrl: text("front_video_url"),
    frontVideoChapters: jsonb("front_video_chapters"),
    breakoutVideos: jsonb("breakout_videos"),
    testimonialWallIds: text("testimonial_wall_ids").array(),
    player: varchar("player", { length: 20 }),
    hasSummaryFirst60_90s: boolean("has_summary_first_60_90s").default(false),
    hasChapters: boolean("has_chapters").default(false),
    showRateBaseline: numeric("show_rate_baseline"),
    showRateCurrent: numeric("show_rate_current"),
    pageUrl: text("page_url"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    bySession: index("confirmation_pages_session_idx").on(t.sessionId),
  })
);

// 11) INDUSTRY_BENCHMARKS
export type BenchmarkSource = "expert" | "external" | "self_learned";

export const industryBenchmarks = pgTable(
  "industry_benchmarks",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    funnelType: varchar("funnel_type", { length: 20 }).notNull(),
    industry: text("industry").notNull(),
    market: varchar("market", { length: 8 }).notNull(),
    metric: varchar("metric", { length: 32 }).notNull(),
    p25: numeric("p25"),
    p50: numeric("p50"),
    p75: numeric("p75"),
    sampleSize: integer("sample_size"),
    source: varchar("source", { length: 20 }).default("external").notNull(),
    notes: text("notes"),
    lastUpdated: timestamp("last_updated").defaultNow(),
  },
  (t) => ({
    uniq: uniqueIndex("industry_benchmarks_uniq_idx").on(
      t.funnelType,
      t.industry,
      t.market,
      t.metric
    ),
  })
);

// 12·5) AUTOPILOT_JOB_RUNS — cron job execution tracking dla SRE lag monitoring
export type AutopilotJobName =
  | "hourly_kpi_snapshot"
  | "hourly_orchestrator"
  | "six_hour_decision"
  | "benchmark_learner";

export type AutopilotJobStatus = "started" | "success" | "failed" | "timeout";

export const autopilotJobRuns = pgTable(
  "autopilot_job_runs",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    jobName: varchar("job_name", { length: 32 }).notNull(),
    startedAt: timestamp("started_at").defaultNow().notNull(),
    completedAt: timestamp("completed_at"),
    durationMs: integer("duration_ms"),
    status: varchar("status", { length: 16 }).default("started").notNull(),
    // Reports from individual jobs — variable shape per job type
    report: jsonb("report"),
    errorMessage: text("error_message"),
  },
  (t) => ({
    byJob: index("autopilot_job_runs_job_idx").on(t.jobName, t.startedAt),
    byStatus: index("autopilot_job_runs_status_idx").on(t.status, t.startedAt),
  })
);

// 13) AUTOPILOT_ALERTS — pending human inbox
export type AutopilotAlertType =
  | "upload_creatives"
  | "approve_scale"
  | "approve_kill"
  | "approve_relaunch"
  | "attention_needed"
  | "pixel_quality_low"
  | "inventory_stockout_risk"
  | "andromeda_cpm_spike"
  | "sophistication_pivot_required"
  | "tracking_quality_gate_failed";

export type AutopilotAlertSeverity = "info" | "warning" | "critical";
export type AutopilotAlertStatus = "pending" | "resolved" | "dismissed";

export const autopilotAlerts = pgTable(
  "autopilot_alerts",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    type: varchar("type", { length: 32 }).notNull(),
    severity: varchar("severity", { length: 16 }).default("info").notNull(),
    payload: jsonb("payload"),
    status: varchar("status", { length: 16 }).default("pending").notNull(),
    decisionId: varchar("decision_id"),
    createdAt: timestamp("created_at").defaultNow(),
    resolvedAt: timestamp("resolved_at"),
    resolvedBy: varchar("resolved_by"),
  },
  (t) => ({
    bySession: index("autopilot_alerts_session_idx").on(t.sessionId, t.status),
    bySeverity: index("autopilot_alerts_severity_idx").on(t.severity, t.status),
  })
);

// =============================================================================
// AUTOPILOT — Zod insert schemas + type exports
// =============================================================================

export const insertAutopilotSessionSchema = createInsertSchema(autopilotSessions);
export const insertAutopilotDecisionSchema = createInsertSchema(autopilotDecisions);
export const insertKpiSnapshotSchema = createInsertSchema(kpiSnapshots);
export const insertCreativeIdeaBatchSchema = createInsertSchema(creativeIdeaBatches);
export const insertCreativeAssetSchema = createInsertSchema(creativeAssets);
export const insertFoundationalAdsetSchema = createInsertSchema(foundationalAdsets);
export const insertContentCycleBinSchema = createInsertSchema(contentCycleBins);
export const insertHammerThemQueueSchema = createInsertSchema(hammerThemQueues);
export const insertPixelConditioningStateSchema = createInsertSchema(pixelConditioningState);
export const insertConfirmationPageSchema = createInsertSchema(confirmationPages);
export const insertIndustryBenchmarkSchema = createInsertSchema(industryBenchmarks);
export const insertAutopilotAlertSchema = createInsertSchema(autopilotAlerts);
export const insertAutopilotJobRunSchema = createInsertSchema(autopilotJobRuns);

export type AutopilotSession = typeof autopilotSessions.$inferSelect;
export type InsertAutopilotSession = z.infer<typeof insertAutopilotSessionSchema>;
export type AutopilotDecision = typeof autopilotDecisions.$inferSelect;
export type InsertAutopilotDecision = z.infer<typeof insertAutopilotDecisionSchema>;
export type KpiSnapshot = typeof kpiSnapshots.$inferSelect;
export type InsertKpiSnapshot = z.infer<typeof insertKpiSnapshotSchema>;
export type CreativeIdeaBatch = typeof creativeIdeaBatches.$inferSelect;
export type InsertCreativeIdeaBatch = z.infer<typeof insertCreativeIdeaBatchSchema>;
export type CreativeAsset = typeof creativeAssets.$inferSelect;
export type InsertCreativeAsset = z.infer<typeof insertCreativeAssetSchema>;
export type FoundationalAdset = typeof foundationalAdsets.$inferSelect;
export type InsertFoundationalAdset = z.infer<typeof insertFoundationalAdsetSchema>;
export type ContentCycleBin = typeof contentCycleBins.$inferSelect;
export type InsertContentCycleBin = z.infer<typeof insertContentCycleBinSchema>;
export type HammerThemQueue = typeof hammerThemQueues.$inferSelect;
export type InsertHammerThemQueue = z.infer<typeof insertHammerThemQueueSchema>;
export type PixelConditioningState = typeof pixelConditioningState.$inferSelect;
export type InsertPixelConditioningState = z.infer<typeof insertPixelConditioningStateSchema>;
export type ConfirmationPage = typeof confirmationPages.$inferSelect;
export type InsertConfirmationPage = z.infer<typeof insertConfirmationPageSchema>;
export type IndustryBenchmark = typeof industryBenchmarks.$inferSelect;
export type InsertIndustryBenchmark = z.infer<typeof insertIndustryBenchmarkSchema>;
export type AutopilotAlert = typeof autopilotAlerts.$inferSelect;
export type InsertAutopilotAlert = z.infer<typeof insertAutopilotAlertSchema>;
export type AutopilotJobRun = typeof autopilotJobRuns.$inferSelect;
export type InsertAutopilotJobRun = z.infer<typeof insertAutopilotJobRunSchema>;

// Creative Pipeline (Kanban) — backend dla "Pipeline Kreatywny" w module Kreator reklam.
// Karty per agency_client. Zasilane manualnie, importem z Meta/grafik, oraz przez autopilot
// (briefy → pomysly, zwycięzcy → zwycieskie, fatigued → przestalo-dzialac).
export const creativePipelineTasks = pgTable(
  "creative_pipeline_tasks",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    agencyClientId: varchar("agency_client_id").references(() => agencyClients.id, {
      onDelete: "cascade",
    }),
    columnId: varchar("column_id", { length: 32 }).notNull(),
    position: integer("position").default(0).notNull(),
    sourceType: varchar("source_type", { length: 32 }).notNull(),
    sourceRefId: varchar("source_ref_id"),
    title: text("title").notNull(),
    description: text("description"),
    mediaType: varchar("media_type", { length: 16 }),
    mediaUrl: text("media_url"),
    thumbnailUrl: text("thumbnail_url"),
    metadata: jsonb("metadata"),
    autopilotSessionId: varchar("autopilot_session_id").references(() => autopilotSessions.id, {
      onDelete: "set null",
    }),
    autopilotAssetId: varchar("autopilot_asset_id").references(() => creativeAssets.id, {
      onDelete: "set null",
    }),
    archivedAt: timestamp("archived_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    byUserClient: index("creative_pipeline_tasks_user_client_idx").on(t.userId, t.agencyClientId),
    byColumn: index("creative_pipeline_tasks_column_idx").on(t.columnId, t.position),
    bySource: index("creative_pipeline_tasks_source_idx").on(t.sourceType, t.sourceRefId),
    bySession: index("creative_pipeline_tasks_session_idx").on(t.autopilotSessionId),
  })
);

export const insertCreativePipelineTaskSchema = createInsertSchema(creativePipelineTasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CreativePipelineTask = typeof creativePipelineTasks.$inferSelect;
export type InsertCreativePipelineTask = z.infer<typeof insertCreativePipelineTaskSchema>;

// Firm Creative Assets — biblioteka assetów firmowych per agency_client.
// Rdzeń Model B (Theriot ecosystem theory + Haynes brand-asset reuse): AI generuje
// warianty (HeyGen avatar swap, fal style transfer, voice clone) na podstawie tych assetów.
// Typy: brand_logo | product_photo | founder_video | voice_sample | style_reference | testimonial | b_roll
export const firmCreativeAssets = pgTable(
  "firm_creative_assets",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    agencyClientId: varchar("agency_client_id").references(() => agencyClients.id, {
      onDelete: "cascade",
    }),
    assetType: varchar("asset_type", { length: 32 }).notNull(),
    mediaType: varchar("media_type", { length: 16 }).notNull(),
    title: text("title").notNull(),
    description: text("description"),
    fileUrl: text("file_url").notNull(),
    thumbnailUrl: text("thumbnail_url"),
    fileSizeBytes: integer("file_size_bytes"),
    durationSeconds: numeric("duration_seconds"),
    width: integer("width"),
    height: integer("height"),
    tags: text("tags").array(),
    // Heygen avatar id, voice clone id, fal style id — pointer do zasobu providera dla reuse
    providerRefs: jsonb("provider_refs"),
    // Theriot tagging: do której fazy ekosystemu pasuje
    ecosystemPhase: varchar("ecosystem_phase", { length: 24 }),
    isActive: boolean("is_active").default(true).notNull(),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    byUserClient: index("firm_creative_assets_user_client_idx").on(t.userId, t.agencyClientId),
    byType: index("firm_creative_assets_type_idx").on(t.assetType, t.isActive),
  })
);

export const insertFirmCreativeAssetSchema = createInsertSchema(firmCreativeAssets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type FirmCreativeAsset = typeof firmCreativeAssets.$inferSelect;
export type InsertFirmCreativeAsset = z.infer<typeof insertFirmCreativeAssetSchema>;

// ── Autopilot Feedback — testerzy zapisują feedback / pomysły funkcjonalności ──
// Wewnętrzne narzędzie: super_admin (Maciej, Marcin Horowski) widzi cudzy + swój feedback.
export const autopilotFeedback = pgTable("autopilot_feedback", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  type: varchar("type", { length: 20 }).notNull().default("feedback"), // 'feedback' | 'feature_request' | 'bug'
  content: text("content").notNull(),
  pageContext: text("page_context"),
  status: varchar("status", { length: 20 }).notNull().default("open"), // 'open' | 'in_progress' | 'done' | 'wontdo'
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type AutopilotFeedback = typeof autopilotFeedback.$inferSelect;
